/**
 * Playwright Client
 *
 * An in-process implementation of IBrowserClient that uses Playwright directly.
 * This is the shared browser automation core used by both cloud (apps/api) and
 * local (canary-cli) environments.
 *
 * Features:
 * - Lower latency (no IPC overhead)
 * - Full access to Playwright APIs
 * - Stealth mode, video recording, crash recovery
 * - Toast capture, snapshot enrichment, downloads
 *
 * ## Playwright APIs Used
 *
 * - `page.ariaSnapshot({ mode: 'ai' })` - Generates accessibility snapshots with refs
 * - `aria-ref=` selector - Resolves refs from snapshots to locators
 *
 * @module playwright-client
 */

import {
  chromium as playwrightChromium,
  type Browser,
  type BrowserContext,
  type Dialog,
  type Locator,
  type Page,
  type Request,
  type Response,
} from 'playwright';
import { chromium as playwrightExtra } from 'playwright-extra';
import StealthPlugin from 'puppeteer-extra-plugin-stealth';
import {
  DEFAULT_VIEWPORT,
  type IBrowserClient,
  type MCPContentResult,
  type ScrollInfo,
  type ScrollResult,
  type StorageStateMethod,
  type BrowserClientOptions,
  type RunCodeExecutionSignals,
  type BrowserConsoleErrorSummary,
  type FileUploadOptions,
  type BrowserConnectOptions,
  type BrowserDomSignals,
  type VideoRecordingResult,
  type StealthOptions,
  type ExtensionOptions,
  type InteractionHighlightOptions,
  type CursorOverlayOptions,
  type SnapshotMode,
  type RecoveryInfo,
  type ProbeResult,
  type BrowserProxyConfig,
  type ContextRoute,
} from './browser-client.interface.js';
import type {
  AssertionEvaluationOutcome,
  AssertionSeverity,
  AssertionType,
  BrowserMode,
  ElementInfo,
} from '@chatsdet/types';
import type { ClickResult, ClickedElementInfo } from './click-validation.js';
import { TIMEOUTS } from './constants.js';
import {
  buildLocatorClickOptions,
  clickLocatorWithOptions,
  clickMouseWithDelay,
  pressClickModifiers,
  releaseClickModifiers,
} from './playwright-client/click-actions.js';
import { performPointerDrag } from './playwright-client/pointer-drag.js';
import { evaluateAssertion } from './assertion-evaluator.js';
import { hasStructuredPageTeardown } from './assertion-evaluation-outcome.js';
import { consoleLogger, type BrowserLogger } from './logger.js';
import * as fs from 'fs/promises';
import * as path from 'path';
import { getCanaryTmpDir } from '@chatsdet/tmp';
import { downsampleImage } from './image-utils.js';
import { parseSnapshot, type SnapshotElement } from './snapshot-analyzer.js';
import { identifyLargeGridRefs } from './snapshot-formatter.js';
import { formatReflessSnapshot } from './refless-snapshot.js';
import { estimateTextTokens } from './truncation.js';
import {
  CdpScreencastManager,
  type ScreencastOptions,
  type ScreencastFrameHandler,
  type ScreencastFrameMetadata,
  type ScreencastEncoderFactory,
  type VideoEncodingCallbacks,
} from './cdp-screencast.js';
import {
  isReportableCanvasMismatch,
  ScreencastFileEncoder,
} from './screencast-file-encoder.js';
import { TOAST_CAPTURE_SCRIPT } from './playwright-client/toast-capture-script.js';
import { FAKE_MIC_SCRIPT } from './playwright-client/fake-mic-script.js';
import { AUDIO_CAPTURE_SCRIPT } from './playwright-client/audio-capture-script.js';
import { CursorOverlay } from './cursor-overlay.js';
import { AutonomousCursor } from './autonomous-cursor.js';
import { buildHighlightCss } from './cursor-visuals.js';
import {
  type CapturedConsoleMessage,
  type CapturedDownload,
  type CapturedNetworkRequest,
  type CapturedToast,
  type PendingDialog,
} from './playwright-client/internal-types.js';
import {
  formatConsoleMessages,
  emptyConsoleMessagesText,
} from './playwright-client/console-messages-formatter.js';
import {
  formatNetworkRequests,
  getPendingNetworkInfo as getPendingNetworkRequestInfo,
  NETWORK_NOISE_URL_PATTERNS,
} from './playwright-client/diagnostics.js';
import {
  buildRunCodeDialogMessage as buildRunCodeQueuedDialogMessage,
  consumePendingDialogMessage as consumePendingDialogQueueMessage,
  consumeRunCodeDialog as consumeQueuedRunCodeDialog,
  getPendingDialogMessage as getPendingDialogQueueMessage,
} from './playwright-client/dialog-queue.js';
import {
  augmentBlockingModalSignal as augmentBlockingModalSignalInSnapshot,
  augmentHiddenSelectOptions as augmentHiddenSelectOptionsInSnapshot,
  enrichHiddenClickableElements as enrichHiddenClickableElementsInPage,
  enrichInteractiveSVGElements as enrichInteractiveSVGElementsInPage,
  enrichModalDialogElements as enrichModalDialogElementsInPage,
} from './playwright-client/snapshot-augmentation.js';
import { runPerRefAugmentations } from './playwright-client/per-ref-augmentation.js';
import { augmentTailMaybeOffloaded } from './cpu-offload/index.js';
import { DOM_REVISION_SCRIPT } from './playwright-client/dom-revision-script.js';
import { evaluateWithCap, EVALUATE_CAPPED } from './playwright-client/evaluate-with-cap.js';
import { countUnsettledRequests } from './playwright-client/http-settle.js';
import { probePageLiveness } from './playwright-client/liveness-probe.js';
import type { Liveness } from './playwright-client/liveness-probe.js';
import { LiveSnapshotManager, mintedYaml } from './live-snapshot.js';
import {
  LiveSnapshotStatsAccumulator,
  type LiveSnapshotStats,
  type LiveSnapshotExecutorEvent,
} from './live-snapshot-stats.js';
import { resolveRefs } from './playwright-client/resolve-refs.js';
import { resolveBrowserContextRequestUrl } from './playwright-client/request-url.js';
import { resolveBrowserLaunchTimeouts } from './playwright-client/launch-timeouts.js';
import {
  assertPdfFetchSizeWithinLimit,
  MAX_PDF_TEXT_FETCH_BYTES,
  parseContentLengthBytes,
} from './pdf-fetch-size.js';
import {
  fetchWithNativeRequestCookies,
  mergeNativeRequestCookies,
  type NativeRequestCookieJar,
} from './playwright-client/native-request-fetch.js';
import { createSingleUseContextRouteHandle } from './playwright-client/context-route-handle.js';
import { buildCssHint } from './playwright-client/css-hint-filter.js';
import { FillTargetNotEditableError } from './fill-target-errors.js';
import { captureSubstrateV2 as captureSubstrateV2Free } from './playwright-client/capture-substrate-v2.js';
import { collectFrameSelectors } from './playwright-client/frame-walker.js';
import {
  getFastDriveConsoleErrorSummarySince,
  getFastDriveOpenPageUrls,
  readFastDriveDomRevision,
  readFastDriveDomSignals,
} from './playwright-client/fast-drive-signals.js';
import { createNavigationSnapshotGate } from './playwright-client/navigation-snapshot-gate.js';
import { runFastDriveScan } from './playwright-client/fast-drive-scan.js';
import type {
  FastDriveScanInput,
  FastDriveScanResult,
} from './playwright-client/fast-drive-scan.js';
import {
  isFastDriveScanStampingEnabled,
  isFastDriveClickRepairEnabled,
} from './fast-drive-scan-gate.js';
import { diagnoseRunCodeClickFailure } from './playwright-client/run-code-failure-repair.js';
import {
  captureElementAtPoint as captureElementAtPointInPage,
  extractElementMetadata as extractElementMetadataFromLocator,
} from './playwright-client/element-inspection.js';
import { formatMcpResponse, TOAST_SNAPSHOT_TTL } from './playwright-client/response-format.js';
import { appendV2RoleOverrides } from './playwright-client/snapshot-substrate-v2.js';
import { launchDirectBrowserWithRetry } from './playwright-client/direct-launch.js';
import {
  assertCompleteIndexedDBRestore,
  assertRequiredLocalStorageRestored,
  extractIndexedDB,
  getIndexedDBRestoreExpectation,
  getIndexedDBRestoreWatchdogMs,
  restoreIndexedDB,
  withQuietOriginPage,
  type IndexedDBData,
} from './indexeddb-helpers.js';
import {
  AUTH_DIAGNOSTICS_SCHEMA_VERSION,
  MAX_AUTH_DIAGNOSTIC_EVENTS,
  fingerprintStorageState,
  hasAuthDiagnosticCaptureCapacity,
  inspectAuthProviderError,
  inspectAuthRequest,
  sanitizeDiagnosticMessage,
  sanitizeDiagnosticUrl,
  type AuthRequestEvidence,
  type BrowserAuthDiagnosticEvent,
  type BrowserAuthDiagnostics,
} from './auth-diagnostics.js';
import {
  acquireTestBrowserLock,
  shouldUseTestBrowserLock,
} from './playwright-client/test-browser-lock.js';
import { resolveTemporalInputFillValue } from './playwright-client/temporal-input-fill.js';
import { PlaywrightTraceAdapter, type TraceStopResult } from './playwright-client/trace-adapter.js';
import { DownloadCaptureStore } from './playwright-client/downloads.js';
import {
  DownloadNavigationRecoveryCoordinator,
  asDownloadExpectation,
  filenameMatches,
  resolveDownloadAssertionTimeout,
} from './playwright-client/download-recovery.js';
import {
  PlaywrightTabRegistry,
  buildClosedTabResult,
  buildNoTabMatchResult,
  buildSelectedTabResult,
  chooseActivePageAfterClose,
  hasExplicitTabSelector,
  listOpenPageHandles,
  listTabs,
  normalizeTabSelector,
  resolveTabTarget,
} from './playwright-client/tabs.js';

export { extractSemanticHint } from './playwright-client/semantic-hints.js';

/** @internal Content-free snapshot diagnostics used by the structured logger. */
export function buildSnapshotSizeLogFields(yaml: string): {
  charCount: number;
  estimatedTokens: number;
  refCount: number;
  lineCount: number;
} {
  const charCount = yaml.length;
  return {
    charCount,
    estimatedTokens: Math.ceil(charCount / 4),
    refCount: (yaml.match(/\[ref=/g) || []).length,
    lineCount: yaml.split('\n').length,
  };
}

/**
 * Browser lease interface for pool integration.
 * Matches the BrowserLease type from apps/api/src/browser-pool/types.ts
 * without importing it, keeping browser-core free of api dependencies.
 */
export interface BrowserLeaseCompat {
  leaseId: string;
  browserId: string;
  createContext(options?: {
    viewport?: { width: number; height: number };
    storageState?: string;
    recordVideo?: { dir: string; size?: { width: number; height: number } };
    extraHTTPHeaders?: Record<string, string>;
    acceptDownloads?: boolean;
  }): Promise<BrowserContext>;
  release(): Promise<void>;
  recover(): Promise<BrowserLeaseCompat>;
}

/**
 * Playwright client that implements the IBrowserClient interface.
 *
 * Provides the full browser automation API surface, running Playwright
 * directly in-process. Used by both cloud (apps/api) and CLI (canary-cli).
 */
/**
 * Opaque span handle returned by the span factory.
 * Callers must pass this back to `endSpan` when the operation completes.
 */
export type ClientSpanHandle = unknown;

/**
 * Callback interface for creating child spans within PlaywrightClient operations.
 * Keeps browser-core free of any tracing library dependency.
 */
export interface ClientSpanFactory {
  startSpan(op: string, description: string): ClientSpanHandle;
  endSpan(handle: ClientSpanHandle, status?: 'ok' | 'error'): void;
}

export interface PlaywrightClientOptions {
  /**
   * Pre-acquired browser lease from the BrowserPool.
   * When provided, connect() uses lease.createContext() instead of launching
   * a new browser, and disconnect() calls lease.release() instead of closing it.
   */
  browserLease?: BrowserLeaseCompat;
  /**
   * Logger implementation. Defaults to console logger.
   */
  logger?: BrowserLogger;
  /**
   * Optional encoder factory for CDP screencast video encoding.
   * When not provided, video encoding is unavailable (frame-only screencast still works).
   */
  screencastEncoderFactory?: ScreencastEncoderFactory;
  /**
   * Base directory for temporary files (downloads, traces, videos).
   * When provided, overrides the default canary tmp dir.
   * API callers should pass an org-scoped directory.
   */
  tmpBaseDir?: string;
  /**
   * Optional span factory for sub-operation tracing (Sentry, OpenTelemetry, etc.).
   */
  spanFactory?: ClientSpanFactory;
  /**
   * Override DOM stabilization delay. Default keeps production behavior; tests
   * that control their own page mutations can set this to 0 to avoid fixed waits.
   */
  stabilizeDomMs?: number;
}

export { ToolAbortedError } from './tool-abort-error.js';
import { ToolAbortedError } from './tool-abort-error.js';
export { OperationTimeoutError } from './operation-timeout-error.js';
import { OperationTimeoutError } from './operation-timeout-error.js';
import { withOperationTimeout, withOperationTimeoutFallback } from './with-operation-timeout.js';

/**
 * @refactor-next
 * Planned extraction targets for subsequent passes (this file is still large):
 * 1. Session lifecycle + crash recovery:
 *    `connect/disconnect`, context/page event wiring, and restore paths.
 *    Target module: `playwright-client/session-lifecycle.ts`.
 * 2. Action execution orchestration:
 *    shared pre-action guards, stabilization, retry-on-recovery wrappers, and
 *    interaction highlighting control flow.
 *    Target module: `playwright-client/action-runner.ts`.
 * 3. Diagnostics pipeline:
 *    console/network capture state management and MCP diagnostics rendering.
 *    Target module: `playwright-client/diagnostics.ts`.
 * 4. Recording subsystem:
 *    tracing, screencast/video lifecycle, and artifact path management.
 *    Target module: `playwright-client/recording.ts`.
 * 5. Internal mutable state normalization:
 *    group class fields into a typed state container to reduce surface area and
 *    simplify unit/integration test setup.
 */
export {
  buildListenerStripWarning,
  decideClosePolicy,
  decideDialogAction,
  detectFunctionExpressionBody,
  guardFileChooserWaitForEvent,
  guardRunCodeFetch,
  guardRunCodePageRequest,
  guardRunCodeWaits,
  serializeRunCodeResult,
  stripNewListeners,
  type DialogHandlerAction,
} from './playwright-client/run-code-guard.js';
export { executeRunCode, type RunCodeOutcome } from './playwright-client/run-code-execution.js';
import { executeRunCode } from './playwright-client/run-code-execution.js';
import {
  decideClosePolicy,
  decideDialogAction,
  serializeRunCodeResult,
  truncateForError,
  type RunCodeEgressPolicy,
} from './playwright-client/run-code-guard.js';

/**
 * Upper bound on a PDF returned to the model as vision input. Mirrors the
 * flow-planner document image cap and keeps the base64 payload within provider
 * document limits. Larger PDFs fall back to text extraction.
 */
const MAX_PDF_IMAGE_BYTES = 10 * 1024 * 1024; // 10MB

/**
 * Recovery is itself an infrastructure operation and must never outlive the
 * caller that asked it to unstick the page. The underlying restore may still
 * finish in the background, but callers get a bounded `false` verdict and can
 * fall back instead of parking the whole browser session indefinitely.
 */
const FORCE_RECOVER_TIMEOUT_MS = 10_000;

/**
 * Timeout handed to Playwright's own `chromium.launch`.
 *
 * Playwright's default is 30s, and our watchdog below used to be 30s too — so
 * the watchdog always won the race and every launch failure was reported as
 * the contentless `chromium.launch timed out after 30000ms`, discarding
 * Playwright's own error, which carries Chromium's stderr and is the only
 * thing that says WHY it did not start. A cold MicroVM launch reads a ~190 MB
 * browser off a cold page cache, so 30s is also simply tight there.
 */
export class PlaywrightClient implements IBrowserClient {
  private disconnectInProgress = false;
  private connectAbortController: AbortController | null = null;
  private browserLease: BrowserLeaseCompat | null;
  private releaseTestBrowserLock: (() => Promise<void>) | null = null;
  private logger: BrowserLogger;
  /** Product-specific iframe selectors searched by `assert()` in addition to
   *  the default generic list. Apps set this once at client construction;
   *  keeps the shared package free of customer-specific selectors. */
  private assertionFrameHints: string[] = [];
  private screencastEncoderFactory: ScreencastEncoderFactory | null;
  private tmpBaseDir: string;
  private downloads: DownloadCaptureStore;
  private downloadRecovery: DownloadNavigationRecoveryCoordinator;
  private spanFactory: ClientSpanFactory | null;
  private stabilizeDomMs: number;
  /** In-flight latch for `attemptUnwedgePage` so close-succession aborts
   *  don't dispatch redundant `window.stop()` evaluates. */
  private unwedgeInFlight = false;

  constructor(options?: PlaywrightClientOptions) {
    this.browserLease = options?.browserLease ?? null;
    this.logger = options?.logger ?? consoleLogger;
    this.spanFactory = options?.spanFactory ?? null;
    this.stabilizeDomMs = options?.stabilizeDomMs ?? TIMEOUTS.STABILIZE_DOM;
    this.screencastEncoderFactory = options?.screencastEncoderFactory ?? null;
    this.cursorOverlay = new CursorOverlay({ enabled: false });
    this.autonomousCursor = new AutonomousCursor({ enabled: false });
    if (!options?.tmpBaseDir) {
      this.logger.debug(
        '[PlaywrightClient] No tmpBaseDir provided, using flat canary dir (not org-scoped)'
      );
    }
    this.tmpBaseDir = options?.tmpBaseDir ?? getCanaryTmpDir();
    this.downloads = new DownloadCaptureStore(this.tmpBaseDir, this.logger);
    this.downloadRecovery = new DownloadNavigationRecoveryCoordinator({
      downloads: this.downloads,
      getContext: () => this.getContext(),
      logger: this.logger,
    });
    this.traceAdapter = new PlaywrightTraceAdapter({
      tmpBaseDir: this.tmpBaseDir,
      logger: this.logger,
      getContext: () => this.getContext(),
      getCurrentContext: () => this.context,
    });

    // Live-snapshot manager. All page access is injected so the manager stays
    // browser-agnostic and unit-testable. Only consulted when liveSnapshotEnabled.
    this.liveSnapshot = new LiveSnapshotManager({
      logger: this.logger,
      // CHEAP tier: the raw substrate (the freshness ground truth).
      captureRaw: async (signal) => {
        const page = this.page;
        if (!page || page.isClosed()) return null;
        const rawYaml = await this.captureRawSubstrate(signal).catch(() => '');
        if (signal?.aborted || !rawYaml) return null;
        return { rawYaml, url: page.url() };
      },
      // EXPENSIVE tier: augment the raw substrate.
      augment: (rawYaml, signal) => this.augmentSubstrate(rawYaml, signal),
      waitQuiet: (quietMs, maxWaitMs) => this.waitDomQuiet(quietMs, maxWaitMs),
      // Efficacy telemetry — merged with executor-layer events into one
      // per-step accumulator the agent drains at end-of-step.
      onEvent: (ev) => this.liveSnapshotStatsAcc.recordManagerEvent(ev),
    });
  }

  private authDiagnosticContext(): {
    sequence: number;
    timestamp: string;
    contextCreatedAtMs: number | null;
    pageId: number | null;
    recoveryGeneration: number;
    browserId: string | null;
    leaseId: string | null;
  } {
    return {
      sequence: ++this._authDiagnosticSequence,
      timestamp: new Date().toISOString(),
      contextCreatedAtMs: this._contextCreatedAtMs,
      pageId: this._currentFocusedPageId,
      recoveryGeneration: this._recoveryGeneration,
      browserId: this.browserLease?.browserId ?? null,
      leaseId: this.browserLease?.leaseId ?? null,
    };
  }

  private pushAuthDiagnostic(event: BrowserAuthDiagnosticEvent): void {
    this._authDiagnosticEvents.push(event);
    if (this._authDiagnosticEvents.length > MAX_AUTH_DIAGNOSTIC_EVENTS) {
      this._authDiagnosticEvents.splice(
        0,
        this._authDiagnosticEvents.length - MAX_AUTH_DIAGNOSTIC_EVENTS
      );
    }
  }

  private recordBrowserLifecycleDiagnostic(
    event: Extract<BrowserAuthDiagnosticEvent, { kind: 'browser_lifecycle' }>['event'],
    args?: { url?: string | null; reason?: string }
  ): void {
    this.pushAuthDiagnostic({
      ...this.authDiagnosticContext(),
      kind: 'browser_lifecycle',
      event,
      url: args?.url ? sanitizeDiagnosticUrl(args.url) : null,
      ...(args?.reason ? { reason: sanitizeDiagnosticMessage(args.reason).slice(0, 256) } : {}),
    });
  }

  private async captureAuthResponse(
    response: Response,
    tracked: { requestId: string; startedAtMs: number; evidence: AuthRequestEvidence }
  ): Promise<void> {
    let providerErrorCode: string | null = null;
    if (response.status() >= 400) {
      const body = await response.text().catch(() => '');
      providerErrorCode = inspectAuthProviderError(body);
    }
    const serverAddress = await response.serverAddr().catch(() => null);
    this.pushAuthDiagnostic({
      ...this.authDiagnosticContext(),
      kind: 'auth_request',
      phase: 'completed',
      requestId: tracked.requestId,
      evidence: tracked.evidence,
      status: response.status(),
      statusText: response.statusText().slice(0, 128),
      durationMs: Date.now() - tracked.startedAtMs,
      providerErrorCode,
      remoteAddress: serverAddress?.ipAddress ?? null,
      protocol: null,
    });
  }

  private trackAuthResponse(response: Response): void {
    const tracked = this._authRequests.get(response.request());
    if (!tracked) return;
    if (!hasAuthDiagnosticCaptureCapacity(this._pendingAuthDiagnosticCaptures.size)) {
      this.pushAuthDiagnostic({
        ...this.authDiagnosticContext(),
        kind: 'auth_request',
        phase: 'completed',
        requestId: tracked.requestId,
        evidence: tracked.evidence,
        status: response.status(),
        statusText: response.statusText().slice(0, 128),
        durationMs: Date.now() - tracked.startedAtMs,
        providerErrorCode: null,
        remoteAddress: null,
        protocol: null,
        error: 'auth diagnostic capture capacity exceeded',
      });
      return;
    }
    const capture = withOperationTimeout(
      this.captureAuthResponse(response, tracked),
      2_000,
      '[DirectPlaywright] auth response diagnostics'
    )
      .catch((error: unknown) => {
        this.pushAuthDiagnostic({
          ...this.authDiagnosticContext(),
          kind: 'auth_request',
          phase: 'completed',
          requestId: tracked.requestId,
          evidence: tracked.evidence,
          status: response.status(),
          statusText: response.statusText().slice(0, 128),
          durationMs: Date.now() - tracked.startedAtMs,
          providerErrorCode: null,
          remoteAddress: null,
          protocol: null,
          error:
            error instanceof Error
              ? sanitizeDiagnosticMessage(error.message).slice(0, 256)
              : 'diagnostic capture failed',
        });
      })
      .finally(() => this._pendingAuthDiagnosticCaptures.delete(capture));
    this._pendingAuthDiagnosticCaptures.add(capture);
  }

  async getAuthDiagnostics(): Promise<BrowserAuthDiagnostics> {
    if (this._pendingAuthDiagnosticCaptures.size > 0) {
      await withOperationTimeout(
        Promise.allSettled([...this._pendingAuthDiagnosticCaptures]),
        2_500,
        '[DirectPlaywright] pending auth diagnostics'
      ).catch(() => undefined);
    }
    const now = Date.now();
    let currentUrl: string | null = null;
    try {
      currentUrl = this.page ? sanitizeDiagnosticUrl(this.page.url()) : null;
    } catch {
      currentUrl = null;
    }
    return {
      schemaVersion: AUTH_DIAGNOSTICS_SCHEMA_VERSION,
      capturedAt: new Date(now).toISOString(),
      current: {
        connected: !!this.context && !!this.page && !this.page.isClosed(),
        contextCreatedAtMs: this._contextCreatedAtMs,
        contextAgeMs:
          this._contextCreatedAtMs === null ? null : Math.max(0, now - this._contextCreatedAtMs),
        pageId: this._currentFocusedPageId,
        url: currentUrl,
        recoveryGeneration: this._recoveryGeneration,
        browserId: this.browserLease?.browserId ?? null,
        leaseId: this.browserLease?.leaseId ?? null,
      },
      events: this._authDiagnosticEvents.slice(),
    };
  }

  /** Enable/disable the live-snapshot (warm + minted) path. Off = legacy v1. */
  setLiveSnapshotEnabled(enabled: boolean): void {
    this.liveSnapshotEnabled = enabled;
  }

  /**
   * On a freshness miss, return the raw substrate immediately and augment in the
   * background (instead of blocking). Default off → block on augmentation
   * (matches legacy behavior and the snapshot formatter's expectations).
   */
  setLiveSnapshotRawOnMiss(rawOnMiss: boolean): void {
    this.liveSnapshotRawOnMiss = rawOnMiss;
  }

  setReflessSnapshots(refless: boolean): void {
    this.navigationSnapshotGate.setReflessSnapshots(refless);
  }

  /**
   * Capture the accessibility-tree YAML, routing through the live-snapshot
   * manager when enabled (raw substrate compared exactly; augmentation reused
   * when unchanged). `force` re-augments unconditionally (used by navigation).
   * Falls back to the legacy full capture when disabled.
   */
  private captureTreeYaml(signal?: AbortSignal, opts?: { force?: boolean }): Promise<string> {
    if (this.liveSnapshotEnabled) {
      return (
        this.liveSnapshot
          .mint({ signal, force: opts?.force, rawOnMiss: this.liveSnapshotRawOnMiss })
          // A null mint means the page was unavailable with no warm snapshot to
          // serve (first capture on a closing/navigating page). Fall back to the
          // legacy capture rather than surfacing an error — never serve stale.
          .then((s) => (s ? mintedYaml(s) : this.captureSnapshotInternal(signal)))
      );
    }
    return this.traced('snapshot_for_ai', 'capture accessibility tree', () =>
      this.captureSnapshotInternal(signal)
    );
  }

  /**
   * Wait until the DOM has been quiet for `quietMs` (bounded by `maxWaitMs`),
   * using the page-side `__waitDomQuiet` helper. If the helper is missing or
   * unreadable (init-script race, or a pre-existing CDP document), fall back to
   * a fixed `quietMs` delay — NOT an immediate return, which would drop the
   * stabilize wait and risk capturing a half-rendered SPA. Soft — never throws.
   *
   * The evaluate is bounded by a DRIVER-side race (`maxWaitMs` + grace): the
   * in-page cap is a page-side setTimeout that never fires when the main
   * thread is blocked, so without the race a single stalled page hangs this
   * await forever and wedges every subsequent tool call (June 2026
   * live-snapshot suite wedge). On cap we return immediately — the stabilize
   * budget is already spent; stacking the quietMs fallback on top would only
   * delay the caller further on a page that can't get healthier by waiting.
   */
  private async waitDomQuiet(quietMs: number, maxWaitMs: number): Promise<void> {
    const page = this.page;
    if (!page || page.isClosed()) return;
    let ran = false;
    try {
      const result = await evaluateWithCap(maxWaitMs + TIMEOUTS.WAIT_DOM_QUIET_EVAL_GRACE, () =>
        page.evaluate(
          async ({ q, m }) => {
            const w = window as unknown as {
              __waitDomQuiet?: (q: number, m: number) => Promise<unknown>;
            };
            if (typeof w.__waitDomQuiet !== 'function') return false;
            await w.__waitDomQuiet(q, m);
            return true;
          },
          { q: quietMs, m: maxWaitMs }
        )
      );
      if (result === EVALUATE_CAPPED) {
        this.logger.warn(
          '[DirectPlaywright] waitDomQuiet evaluate exceeded driver-side cap (page main thread may be blocked)',
          { maxWaitMs, graceMs: TIMEOUTS.WAIT_DOM_QUIET_EVAL_GRACE }
        );
        // The capped page.evaluate is still alive in Playwright's CDP session.
        // Returning while that orphan owns a wedged renderer only moves the
        // hang to the next title/snapshot call. Tear down this page so the
        // orphan rejects, then restore the authenticated URL through the same
        // crash-recovery path used by per-tool timeout recovery.
        await this.forceRecoverPage('wait_dom_quiet_evaluate_capped');
        return;
      }
      ran = result;
    } catch {
      // Page navigated / context destroyed mid-wait — fall through to the
      // fixed delay below as a best-effort stabilize.
      ran = false;
    }
    if (!ran) {
      await page.waitForTimeout(quietMs).catch(() => undefined);
    }
  }

  /**
   * Race a Playwright op against `signal`:
   *  - returns the action's resolved value if it wins.
   *  - throws `ToolAbortedError` if the signal aborts first.
   *
   * Note: this helper does NOT call `attemptUnwedgePage`. The unwedge contract
   * is owned by `BrowserToolExecutor.withCallSignal` so it fires on every
   * aborted tool call regardless of whether the in-flight method was wrapped
   * in `raceAgainstSignal`. Keeping the responsibility in one place avoids
   * double-firing `page.evaluate('window.stop()')` on the same abort.
   *
   * Currently applied only to `navigate()`. Other long-running methods (click,
   * type, scroll, snapshot, evaluate, ...) propagate `signal` to Playwright but
   * do not race here. Wrap incrementally as data justifies.
   *
   * `stage` is used for the error message so callers can identify which step
   * was interrupted (e.g. `'click:locator'`, `'navigate:goto'`).
   */
  private raceAgainstSignal<T>(
    stage: string,
    signal: AbortSignal | undefined,
    fn: () => Promise<T>
  ): Promise<T> {
    if (!signal) return fn();
    if (signal.aborted) return Promise.reject(new ToolAbortedError(stage));
    return new Promise<T>((resolve, reject) => {
      const onAbort = (): void => reject(new ToolAbortedError(stage));
      signal.addEventListener('abort', onAbort, { once: true });
      const cleanup = (): void => signal.removeEventListener('abort', onAbort);
      fn().then(
        (v) => {
          cleanup();
          resolve(v);
        },
        (e) => {
          cleanup();
          reject(e);
        }
      );
    });
  }

  /**
   * Best-effort: dispatch `window.stop()` on the active page to release any
   * pending fetch/XHR/resource loads that may be wedging the JS context, so
   * the next tool call sees a responsive page.
   *
   * Fire-and-forget — never await this, never let it reject. The 1s cap exists
   * only to bound a wedged context's response time; a healthy page returns in
   * <10ms. Safe to call multiple times.
   *
   * Public: invoked by `BrowserToolExecutor.withCallSignal` when the per-tool
   * timeout fires. The orphan Playwright op may continue to settle in the
   * background — `window.stop()` halts pending resource loads but does NOT
   * cancel an in-flight `page.click()`/`page.type()`. A click can therefore
   * still land *after* the agent has already received the timeout sentinel;
   * that's a Playwright cancellation gap, not a bug here.
   *
   * Implemented by `PlaywrightClient` only. Other `IBrowserClient` impls
   * (mocks, RemoteBrowserClient) leave this undefined and the executor's
   * abort listener is skipped via `typeof === 'function'` guard.
   */
  attemptUnwedgePage(): void {
    if (this.unwedgeInFlight) return;
    const page = this.page;
    if (!page) return;
    this.unwedgeInFlight = true;
    this.logger.info('[DirectPlaywright] attemptUnwedgePage dispatched');
    let timer: ReturnType<typeof setTimeout> | undefined;
    void Promise.race([
      page
        // Function form, not a string: function evaluation goes through
        // Runtime.callFunctionOn, which sidesteps in-page eval policy
        // (CSP `unsafe-eval` / Trusted Types) entirely and matches how every
        // other evaluate in this client is written.
        .evaluate(() => window.stop())
        .then(() => this.logger.info('[DirectPlaywright] attemptUnwedgePage completed'))
        .catch((err: unknown) => {
          this.logger.warn('[DirectPlaywright] attemptUnwedgePage evaluate failed', {
            error: err instanceof Error ? err.message : String(err),
          });
        }),
      new Promise<void>((r) => {
        timer = setTimeout(() => {
          this.logger.warn('[DirectPlaywright] attemptUnwedgePage hit cap (page may be dead)', {
            capMs: TIMEOUTS.UNWEDGE_PAGE_CAP,
          });
          r();
        }, TIMEOUTS.UNWEDGE_PAGE_CAP);
      }),
    ])
      .catch(() => {})
      .finally(() => {
        if (timer) clearTimeout(timer);
        this.unwedgeInFlight = false;
      });
  }

  /**
   * Cheap, driver-bounded liveness check (`page.evaluate(() => 1)` under a
   * short cap). Used by the per-tool-timeout recovery path to decide whether a
   * timed-out page is genuinely wedged (force-recover) or just slow (spare it).
   */
  async probeLiveness(): Promise<Liveness> {
    return probePageLiveness(this.page, TIMEOUTS.LIVENESS_PROBE_CAP);
  }

  /**
   * Force-recover the active page so orphaned Playwright ops die with the
   * renderer. `ariaSnapshot`/`screenshot` have no timeout and cannot be
   * cancelled; when the per-tool timeout fires they keep running for minutes on
   * a jammed renderer. Closing the page rejects every in-flight op bound to its
   * CDP session — the only thing that truly kills the orphans — then we recreate
   * the page and re-navigate to `lastKnownUrl` via the existing crash-recovery
   * path. Never throws; returns whether a live page is available afterward.
   */
  async forceRecoverPage(reason: string): Promise<boolean> {
    // Never resurrect a page the agent deliberately closed.
    if (this.pageClosedIntentionally) return false;
    // Coalesce with any in-flight recovery (lazy getPage() recovery or a
    // concurrent forceRecoverPage) rather than tearing down twice.
    if (this.restoreInProgress && this.restorePromise) {
      await withOperationTimeout(
        this.restorePromise,
        FORCE_RECOVER_TIMEOUT_MS,
        'forceRecover:coalescedRestore'
      ).catch(() => undefined);
      return !!this.page && !this.page.isClosed();
    }
    const startedAt = Date.now();
    try {
      const page = this.page;
      if (page && !page.isClosed()) {
        // `forcedTeardownInProgress` tells the page-close handler this is a
        // deliberate teardown (suppresses the "closed unexpectedly" crash log
        // and its crash-state stamping). Bounded so even page.close() can't
        // hang us on a wedged renderer.
        this.forcedTeardownInProgress = true;
        try {
          await withOperationTimeout(
            page.close({ runBeforeUnload: false }),
            2000,
            'forceRecover:pageClose'
          ).catch(() => {});
        } finally {
          this.forcedTeardownInProgress = false;
        }
      }
      // A forced teardown reuses the page_crash recovery path (URL restore +
      // video salvage) — that's the correct machinery. The distinguishing
      // signal is `reason`, which restoreBrowser logs. Set explicitly to cover
      // the already-closed-page case (no close handler fired). page.isClosed()
      // is now true → restoreBrowser recreates the page and re-navigates.
      this._pendingCrashUrl = this.lastKnownUrl;
      this._pendingCrashType = 'page_crash';
      await withOperationTimeout(
        this.restoreBrowser(reason),
        FORCE_RECOVER_TIMEOUT_MS,
        'forceRecover:restoreBrowser'
      );
      const recovered = !!this.page && !this.page.isClosed();
      this.logger.info('[DirectPlaywright] forceRecoverPage completed', {
        reason,
        recovered,
        durationMs: Date.now() - startedAt,
        restoredUrl: this.lastKnownUrl,
      });
      return recovered;
    } catch (err) {
      this.logger.warn('[DirectPlaywright] forceRecoverPage failed', {
        reason,
        error: err instanceof Error ? err.message : String(err),
      });
      return false;
    }
  }

  /**
   * Set the span factory for sub-operation tracing after construction.
   * Called by the API layer to propagate Sentry span context.
   */
  setSpanFactory(factory: ClientSpanFactory): void {
    this.spanFactory = factory;
  }

  /**
   * Capture a substrate-v2 YAML snapshot of the given page without
   * updating `lastSnapshotYaml`. The v2 enrichers used here (modal role
   * overrides, frame walker, derived-attr post-pass) are non-mutating.
   *
   * Used by the scenario suite to take a fresh capture for the corpus.
   */
  async captureSubstrateV2(page: Page): Promise<string> {
    return captureSubstrateV2Free(page, this.logger);
  }

  /**
   * Run an async operation wrapped in a tracing span (if spanFactory is configured).
   */
  private async traced<T>(op: string, description: string, fn: () => Promise<T>): Promise<T> {
    if (!this.spanFactory) return fn();
    const handle = this.spanFactory.startSpan(op, description);
    try {
      const result = await fn();
      this.spanFactory.endSpan(handle, 'ok');
      return result;
    } catch (err) {
      this.spanFactory.endSpan(handle, 'error');
      throw err;
    }
  }

  /** Assign a stable monotonic ID to a page and return it. */
  private _assignPageId(page: Page): number {
    return this._tabRegistry.assign(page);
  }

  /**
   * Make `page` the active/bound page and keep the focus + screencast state
   * consistent — the single choke point every tab switch (new/select/close/
   * context-swap/recovery) should route through. Idempotent.
   */
  private _setActivePage(page: Page): void {
    this.page = page;
    const id = this._assignPageId(page);
    this._currentFocusedPageId = id;
    // Refresh lastKnownUrl to the newly-active tab's URL. Selecting a
    // fully-loaded background tab fires no `framenavigated`, so without this a
    // crash immediately after the switch would recover the PREVIOUS tab's URL.
    try {
      const url = page.url();
      if (url && url !== 'about:blank') this.lastKnownUrl = url;
    } catch {
      // page.url() can throw on a closing/detached page — leave lastKnownUrl.
    }
    // Ensure the target's listeners are wired (a followed popup may not have
    // them) and that video follows the switch even for pages that predate the
    // encoder. Both are idempotent.
    this.setupPageEventListeners(page);
    void this._startScreencastOnPage(page, id).catch((err) => {
      this.logger.debug('[DirectPlaywright] _startScreencastOnPage on setActive failed', {
        error: err instanceof Error ? err.message : String(err),
      });
    });
  }

  private isRecoverableClosureError(error: unknown): boolean {
    const message = error instanceof Error ? error.message : String(error);
    return (
      message.includes('Target closed') ||
      message.includes('has been closed') ||
      message.includes('Browser has been closed') ||
      message.includes('Execution context was destroyed') ||
      message.includes('Browser closed') ||
      message.includes('Context closed')
    );
  }
  private browser: Browser | null = null;
  private context: BrowserContext | null = null;
  private page: Page | null = null;
  private storageStatePath?: string;
  private contextClosedIntentionally = false;

  // ---- Isolated api_sequence request session ----
  // When active, fetch() uses a native Fetch-backed request path with an
  // in-memory cookie jar seeded from the browser context. This avoids
  // Playwright APIRequestContext's Bun-specific Set-Cookie parser bug and the
  // browser-context request's CDP cookie write-back stall. Nothing is persisted.
  private apiRequestStorageState?: Awaited<ReturnType<BrowserContext['storageState']>>;
  private apiRequestSessionActive = false;
  private lastConnectOptions: {
    storageStatePath?: string;
    storageStateRequired?: boolean;
    browserMode: BrowserMode;
    viewport: { width: number; height: number };
    recordVideo?: boolean;
    stealth?: boolean | StealthOptions;
    extensions?: ExtensionOptions;
    highlightInteractions?: boolean | InteractionHighlightOptions;
    cursorOverlay?: boolean | CursorOverlayOptions;
    extraHTTPHeaders?: Record<string, string>;
    httpCredentials?: { username: string; password: string };
    proxy?: BrowserProxyConfig;
  } | null = null;
  private pageClosedIntentionally = false;
  // Set while `forceRecoverPage` is deliberately closing the page so the
  // page-close handler doesn't log it as an unexpected crash / re-stamp crash
  // state. Distinct from `pageClosedIntentionally` (which makes getPage throw).
  private forcedTeardownInProgress = false;
  private restoreInProgress = false;
  private restorePromise: Promise<void> | null = null;

  // State tracking (MCP does this internally via Tab class)
  // Note: underscore prefix to avoid collision with interface method names
  private _capturedConsoleMessages: CapturedConsoleMessage[] = [];
  private _capturedNetworkRequests: Map<string, CapturedNetworkRequest> = new Map();
  /** Secret-free, bounded evidence retained for auth incident diagnosis. */
  private _authDiagnosticEvents: BrowserAuthDiagnosticEvent[] = [];
  private _authDiagnosticSequence = 0;
  private _authRequestSequence = 0;
  private _authRequests = new WeakMap<
    Request,
    { requestId: string; startedAtMs: number; evidence: AuthRequestEvidence }
  >();
  private _pendingAuthDiagnosticCaptures = new Set<Promise<void>>();
  private _pendingDialogs: PendingDialog[] = [];
  // When set, the next dialog will use this action instead of auto-dismiss
  private _nextDialogAction: { action: 'accept' | 'dismiss'; promptText?: string } | null = null;
  // Tracks dialogs our global handler has already acted on so a duplicate fire
  // is a no-op instead of throwing "Cannot accept dialog which is already handled!".
  // Agent-authored listeners (registered via browser_run_code) can race us;
  // this keeps our side idempotent even when they do.
  private _handledDialogs: WeakSet<Dialog> = new WeakSet();
  private _lastCapturedToasts: CapturedToast[] = [];
  private _lastSnapshotConsoleIndex = 0;
  private _snapshotedFailedRequestUrls = new Set<string>();

  // Last snapshot for reference (used by some methods)
  private lastSnapshotYaml: string = '';
  private lastSnapshotTimestamp: number = 0;

  // Last RAW ref-less substrate YAML captured in fast-drive mode (no refs, no
  // augmentation). Kept separate from `lastSnapshotYaml` (the augmented, ref'd
  // substrate) so the two modes never cross-contaminate; the upcoming ref-less
  // diff uses this as its baseline.
  private lastReflessYaml: string = '';

  // Live-snapshot (warm/minted) state. Gated by `liveSnapshotEnabled`; when off,
  // snapshot() behaves exactly as before. See live-snapshot.ts.
  private liveSnapshotEnabled = false;
  private liveSnapshotRawOnMiss = false;
  private readonly navigationSnapshotGate = createNavigationSnapshotGate();
  private readonly liveSnapshot: LiveSnapshotManager;
  /**
   * Per-step LiveSnapshot efficacy stats. Merges manager-layer events (via the
   * manager's `onEvent`) and executor-layer events (via `recordLiveSnapshotEvent`).
   * Drained once per step by the agent through `drainLiveSnapshotStats()`.
   */
  private readonly liveSnapshotStatsAcc = new LiveSnapshotStatsAccumulator();

  // Last resolved element metadata (for recording layer to avoid snapshot re-parsing)
  private lastResolvedElementMetadata: Map<string, ElementInfo> = new Map();

  private traceAdapter: PlaywrightTraceAdapter;

  // Context route GROUPS keyed by owner (e.g. 'cf-access' scoped-header
  // injection, 'emulation', 'diagnostic-host-boundary'), last applied via
  // setupContextRoutes(). Keyed so independent owners can arm/replace their own
  // group without de-arming another's. Route interception is per-context and is
  // dropped when the context is recreated; we remember the groups so context
  // recreation paths (restoreBrowser, swapContext) can re-arm them —
  // otherwise e.g. the edge would bounce post-recovery requests to a login
  // page, or a session-internal re-login would silently disarm the diagnostic
  // host boundary. Per-group options: `matchFirst` registers the group LAST so
  // Playwright (newest-first matching) consults it before every other group;
  // `reapplyOnContextSwap: false` marks credential-bound groups (CF Access
  // headers) that must NOT be replayed onto a swapped context — their owner
  // re-applies fresh values after the swap's login.
  private storedContextRoutes: Map<
    string,
    { routes: ContextRoute[]; matchFirst: boolean; reapplyOnContextSwap: boolean }
  > = new Map();

  // Handler wrappers currently armed on the context, so a rebuild
  // can register the NEW handlers first and only then unroute the stale ones —
  // no unguarded window mid-rebuild. Tied to the context they were armed on:
  // a fresh context starts with nothing to unroute.
  private armedContextRouteHandlers: Array<{
    pattern: string | RegExp;
    handler: Parameters<BrowserContext['route']>[1];
  }> = [];

  private armedContextRouteContext: BrowserContext | null = null;

  // Serializes setupContextRoutes/rebuild across concurrent owners (e.g. a CF
  // re-apply racing a diagnostic re-arm) so interleaved rebuilds can't strip
  // each other's freshly-registered handlers.
  private contextRouteLock: Promise<void> = Promise.resolve();

  // Opt-in egress policy applied to every runCode block (fetch binding +
  // page.goto/page.context surfaces). Set by security-sensitive sessions
  // (diagnostic live browser) via setRunCodeEgressPolicy.
  private runCodeEgressPolicy: RunCodeEgressPolicy | null = null;

  // Video recording state.
  // The screencast encoder is the single source of truth for the recorded
  // file: while it lives, `videoDir`/`videoPath` derive from it. Before the
  // encoder is created we hold the resolved output directory in
  // `pendingVideoDir`. After the encoder is finalized in `saveVideo()` we
  // snapshot its output path into `_savedVideoPath` so callers
  // (`getVideoPath()`, the disconnect warning) can still retrieve it.
  private videoEnabled = false;
  private pendingVideoDir: string | null = null;
  private _savedVideoPath: string | null = null;
  private get videoDir(): string | null {
    return this._screencastEncoder?.outputDir ?? this.pendingVideoDir;
  }
  private get videoPath(): string | null {
    return this._screencastEncoder?.outputFilePath ?? this._savedVideoPath;
  }
  /**
   * Best-available wall-clock proxy for when the video encoder started.
   * Captured immediately after newContext() / createContext() returns.
   */
  private _contextCreatedAtMs: number | null = null;

  // Stable page IDs (monotonically increasing) are used by the screencast frame
  // hot path and as durable within-run tab handles surfaced to the agent.
  private readonly _tabRegistry = new PlaywrightTabRegistry();
  // Guards setupPageEventListeners against double-wiring the same page.
  private _wiredPages = new WeakSet<Page>();
  // When true, newly opened tabs (popups / target=_blank) get their listeners
  // wired at open time so they feed console/URL signals — WITHOUT stealing the
  // active page (explicit-switch policy). Set from the API knob for fast-drive.
  private _autoCaptureNewPages = false;

  // Stealth mode state
  private stealthEnabled = false;
  private stealthPluginApplied = false;

  // Extension support state
  private extensionsEnabled = false;
  private userDataDir: string | null = null;
  private shouldCleanupUserDataDir = false;

  // Interaction highlighting state
  private highlightEnabled = false;
  private highlightDuration = 150;
  private highlightStylesInjected = false;

  // Cursor overlay state
  private cursorOverlay: CursorOverlay;
  // Autonomous (in-page, event-driven) cursor for the fast-drive run_code path.
  private autonomousCursor: AutonomousCursor;

  // Snapshot file tracking (for bash tool analysis)
  private snapshotFilePaths: Set<string> = new Set();

  // Crash recovery state tracking
  /** Last known URL for recovery (updated on navigation and frame events) */
  private lastKnownUrl: string | null = null;

  /**
   * Subscribers to main-frame navigation events. Notified on every framenavigated
   * fire (full loads, redirects, SPA pushState/popstate). See onNavigation().
   */
  private navigationHandlers = new Set<(url: string) => void>();
  /** Pending crash URL to restore after recovery */
  private _pendingCrashUrl: string | null = null;
  /** Type of crash that occurred */
  private _pendingCrashType: RecoveryInfo['crashType'] | null = null;
  /** Recovery info to be consumed by next tool call */
  private _lastRecoveryInfo: RecoveryInfo | null = null;
  // Monotonic count of completed crash/wedge recoveries. Unlike _lastRecoveryInfo
  // (consumed once by the next tool call), this is never cleared — callers compare
  // it across a step to detect "a recovery happened during this step" (used to
  // treat a post-recovery auth wall as a retriable infra failure, not a credential
  // failure).
  private _recoveryGeneration = 0;

  /**
   * Shared CDP screencast bus used by BOTH live streaming (B, via
   * startScreencast/stopScreencast) and file recording (A, via
   * _startScreencastOnPage). Refcounted internally — first subscriber on a
   * page starts `Page.startScreencast`, last unsubscribe stops it. Created
   * lazily so non-screencast sessions don't allocate it.
   */
  private screencastManager: CdpScreencastManager | null = null;
  private screencastHandler: ScreencastFrameHandler | null = null;
  /** Preserve the full live-stream contract across context swaps and crash recovery. */
  private screencastOptions: ScreencastOptions | undefined;
  private screencastVideoCallbacks: VideoEncodingCallbacks | undefined;

  // Screencast file recording state (wall-clock-accurate video via the shared bus above)
  private _screencastEncoder: ScreencastFileEncoder | null = null;

  /**
   * Whether frames are ACTUALLY being encoded right now — not merely whether
   * recording was configured.
   *
   * `videoEnabled` only says a video config was supplied. A direct client drops
   * every frame until `ensureScreencastEncoder()` runs, and `saveVideo()`
   * nulls the encoder while leaving `videoEnabled` true. Anything that spends real
   * time to land a state in an encoded frame — the anticipatory cursor cue — must
   * gate on this, or it pays dwell for frames nobody is recording.
   */
  private isRecordingActive(): boolean {
    return this.videoEnabled && this._screencastEncoder !== null;
  }
  private _screencastDisposables = new Map<Page, { dispose: () => Promise<void> }>();
  /** Pages whose frame size has already been checked against the encoder canvas. */
  private _canvasMismatchWarnedPageIds = new Set<number>();
  /** Page ID of the currently-focused page; read by the screencast onFrame hot path. */
  private _currentFocusedPageId: number | null = null;

  // ================ LIFECYCLE ================

  async connect(arg?: string | BrowserConnectOptions): Promise<void> {
    if (this.browser || this.context) {
      this.logger.debug('[DirectPlaywright] Already connected, skipping');
      return;
    }

    // A client can be explicitly disconnected and reconnected. Never let a
    // later session inherit auth evidence or fingerprints from the prior one.
    this._authDiagnosticEvents = [];
    this._authDiagnosticSequence = 0;
    this._authRequestSequence = 0;
    this._authRequests = new WeakMap();
    this._pendingAuthDiagnosticCaptures.clear();

    const options = typeof arg === 'string' ? { storageStatePath: arg } : (arg ?? {});

    const {
      storageStatePath,
      storageStateRequired,
      browserMode,
      cdpUrl,
      viewport,
      recordVideo,
      stealth,
      extensions,
      highlightInteractions,
      cursorOverlay,
      extraHTTPHeaders,
      httpCredentials,
      proxy,
    } = options;

    const resolvedBrowserMode: BrowserMode = browserMode ?? 'headless';
    const resolvedViewport = viewport ?? DEFAULT_VIEWPORT;
    this.lastConnectOptions = {
      storageStatePath,
      storageStateRequired,
      browserMode: resolvedBrowserMode,
      viewport: resolvedViewport,
      recordVideo,
      stealth,
      extensions,
      highlightInteractions,
      cursorOverlay,
      extraHTTPHeaders,
      httpCredentials,
      proxy,
    };
    this.storageStatePath = storageStatePath;
    this.pageClosedIntentionally = false;

    // Configure video recording if requested
    const videoConfig = this.resolveVideoConfig(recordVideo);
    this.videoEnabled = !!videoConfig;

    // Determine if stealth mode is enabled
    const useStealthMode = stealth === true || (typeof stealth === 'object' && stealth.enabled);
    this.stealthEnabled = useStealthMode;

    // Determine if extensions are requested
    const hasExtensions = !!(extensions?.paths && extensions.paths.length > 0);
    this.extensionsEnabled = hasExtensions;

    // Configure interaction highlighting (defaults to true when video is enabled)
    if (recordVideo) {
      // Default to enabled unless explicitly disabled
      const explicitlyDisabled =
        highlightInteractions === false ||
        (typeof highlightInteractions === 'object' && !highlightInteractions.enabled);
      this.highlightEnabled = !explicitlyDisabled;
      this.highlightDuration =
        typeof highlightInteractions === 'object' && highlightInteractions.duration
          ? highlightInteractions.duration
          : 150;
    } else {
      this.highlightEnabled = false;
    }

    // Configure cursor overlay (defaults to enabled when video + highlights are enabled)
    {
      const explicitlyDisabled =
        cursorOverlay === false ||
        (typeof cursorOverlay === 'object' && cursorOverlay.enabled === false);
      const explicitlyEnabled =
        cursorOverlay === true ||
        (typeof cursorOverlay === 'object' && cursorOverlay.enabled === true);
      const cursorEnabled =
        explicitlyEnabled || (this.highlightEnabled && this.videoEnabled && !explicitlyDisabled);
      const cursorOpts = typeof cursorOverlay === 'object' ? cursorOverlay : {};
      this.cursorOverlay = new CursorOverlay({ ...cursorOpts, enabled: cursorEnabled });
      // The fast-drive run_code path uses a separate in-page cursor; enable it
      // under the same condition as the driver-driven overlay. Its ANTICIPATORY
      // cue is gated more narrowly — on actual recording — because the cue spends
      // real dwell time to land the pre-action state in an encoded frame, and
      // `cursorOverlay: true` turns the cursor on without video (the canary-cli
      // daemon does exactly that in headed mode), where that dwell buys nothing.
      // A thunk, not a snapshot: recording state is not fixed at connect time.
      // Deferred-login sessions create the encoder later, and saveVideo() destroys
      // it while `videoEnabled` stays true — so this asks "is an encoder actually
      // taking frames right now", re-evaluated per cue.
      this.autonomousCursor = new AutonomousCursor({
        enabled: cursorEnabled,
        cueEnabled: () => cursorEnabled && this.isRecordingActive(),
      });
    }

    const downloadDir = await this.downloads.prepareDirectory();

    this.logger.info('[DirectPlaywright] Launching browser', {
      browserMode: resolvedBrowserMode,
      hasStorageState: !!storageStatePath,
      storageStateRequired: storageStateRequired ?? false,
      viewport: viewport ?? DEFAULT_VIEWPORT,
      videoEnabled: this.videoEnabled,
      stealthEnabled: useStealthMode,
      extensionCount: extensions?.paths?.length ?? 0,
      highlightEnabled: this.highlightEnabled,
      downloadDir,
    });

    const connectAbortController = new AbortController();
    this.connectAbortController = connectAbortController;

    try {
      if (
        shouldUseTestBrowserLock({
          browserLease: !!this.browserLease,
          cdpUrl,
          browserMode: resolvedBrowserMode,
        }) &&
        !this.releaseTestBrowserLock
      ) {
        this.releaseTestBrowserLock = await acquireTestBrowserLock(this.logger);
      }

      // Launch browser: CDP, pool lease, or direct launch
      if (cdpUrl) {
        // CDP path: connect to an existing Chrome instance
        this.logger.info('[DirectPlaywright] Connecting via CDP', { cdpUrl });
        this.browser = await withOperationTimeout(
          playwrightChromium.connectOverCDP(cdpUrl),
          30_000,
          '[DirectPlaywright] connectOverCDP'
        );
        const existingContexts = this.browser.contexts();
        this._contextCreatedAtMs = Date.now();
        // A CDP-connected Chrome always has a default context, so the reuse is the
        // rule and the newContext() fallback is the exception. The reused context
        // is `viewport: null` and cannot be reconfigured — the requested viewport
        // is applied per page instead (applyRequestedViewport).
        this.context =
          existingContexts[0] ?? (await this.browser.newContext({ viewport: resolvedViewport }));
        if (existingContexts[0]) {
          this.logger.info(
            '[DirectPlaywright] Reusing pre-existing CDP context; viewport applied per page',
            { existingContextCount: existingContexts.length, viewport: resolvedViewport }
          );
        }
      } else if (
        this.browserLease &&
        !this.stealthEnabled &&
        !this.extensionsEnabled &&
        resolvedBrowserMode !== 'headed'
      ) {
        // Pool path: create a BrowserContext on the leased browser
        const videoConfig = this.resolveVideoConfig(recordVideo);
        const contextOptions: {
          viewport?: { width: number; height: number };
          storageState?: string;
          recordVideo?: { dir: string; size?: { width: number; height: number } };
          extraHTTPHeaders?: Record<string, string>;
          httpCredentials?: { username: string; password: string };
          acceptDownloads?: boolean;
        } = {
          viewport: resolvedViewport,
          acceptDownloads: true,
        };
        if (storageStatePath) {
          try {
            await fs.access(storageStatePath);
            contextOptions.storageState = storageStatePath;
          } catch (error) {
            if (storageStateRequired) {
              throw new Error(
                `Required browser storage state is unreadable: ${error instanceof Error ? error.message : String(error)}`
              );
            }
            // Optional cached state can fall back to the normal login path.
          }
        }
        if (videoConfig) {
          await fs.mkdir(videoConfig.dir, { recursive: true });
          // recordVideo is NOT passed to Playwright — frames are delivered to the
          // screencast encoder via page.screencast (CDP).
          this.pendingVideoDir = videoConfig.dir;
        }
        if (extraHTTPHeaders && Object.keys(extraHTTPHeaders).length > 0) {
          contextOptions.extraHTTPHeaders = extraHTTPHeaders;
        }
        if (httpCredentials) {
          contextOptions.httpCredentials = httpCredentials;
        }

        this.logger.info('[DirectPlaywright] Using pooled browser via lease', {
          leaseId: this.browserLease.leaseId,
          browserId: this.browserLease.browserId,
        });

        this._contextCreatedAtMs = Date.now();
        this.context = await withOperationTimeout(
          this.browserLease.createContext(contextOptions),
          30_000,
          '[DirectPlaywright] browserLease.createContext'
        );
        this.browser = null; // Browser is managed by the pool, not by this client
      } else {
        const launched = await launchDirectBrowserWithRetry({
          browserLease: this.browserLease,
          browserMode: resolvedBrowserMode,
          logger: this.logger,
          signal: connectAbortController.signal,
          launch: () =>
            this.launchBrowser({
              browserMode: resolvedBrowserMode,
              storageStatePath,
              storageStateRequired,
              viewport,
              recordVideo,
              stealth,
              extensions,
              extraHTTPHeaders,
              httpCredentials,
              proxy,
            }),
        });

        this.browserLease = launched.browserLease;
        this.browser = launched.browser;
        this.context = launched.context;
      }

      this.attachContextListeners(this.context);
      if (this.browser) {
        this.attachBrowserListeners(this.browser);
      }

      await this.createPageWithListeners(this.context, 'initial_connect');

      // Track the focused page for screencast frame delivery
      const initialPageId = this._assignPageId(this.page!);
      this._currentFocusedPageId = initialPageId;

      // Start screencast frame delivery on the initial page. This direct client
      // drops frames until ensureScreencastEncoder() creates the file encoder.
      if (this.videoEnabled && this.page) {
        await this._startScreencastOnPage(this.page, initialPageId);
      }

      // Restore IndexedDB data if the storage state file includes it
      if (storageStatePath) {
        await this.restoreIndexedDBFromStorageState(
          storageStatePath,
          storageStateRequired ?? false
        );
      }

      this.recordBrowserLifecycleDiagnostic('connected', {
        url: this.page?.url() ?? null,
      });

      this.logger.info('[DirectPlaywright] Browser connected successfully', {
        browserMode: resolvedBrowserMode,
        stealthEnabled: this.stealthEnabled,
        extensionsEnabled: this.extensionsEnabled,
        pooled: !!this.browserLease,
      });
    } catch (error) {
      if (!connectAbortController.signal.aborted) {
        if (this.connectAbortController === connectAbortController) {
          this.connectAbortController = null;
        }
        await this.cleanupFailedConnect(error);
      }
      throw error;
    } finally {
      if (this.connectAbortController === connectAbortController) {
        this.connectAbortController = null;
      }
    }
  }

  supportsContextSwap(): boolean {
    // Extensions use launchPersistentContext which does not support context swapping.
    // We require either a direct browser handle or a pool lease to create new contexts.
    return !this.extensionsEnabled && (!!this.browser || !!this.browserLease);
  }

  /**
   * Replace the browser context (e.g. to apply fresh storage state on re-auth),
   * keeping the same browser. Contract for context-bound state:
   *  - Tracing AUTO-resumes here: the in-progress trace is flushed to a chunk
   *    first (recoverable), then restarted on the new context.
   *  - Context route groups ARE auto re-armed on the new context (best-effort,
   *    like crash recovery) so a swap can never silently disarm an owner that
   *    doesn't know the swap happened (e.g. the diagnostic host boundary when a
   *    session-internal re-login swaps the context). Owners whose values change
   *    with the swap (CF Access headers on a new credential) re-apply their own
   *    group immediately after, replacing the re-armed copy; owners with
   *    fail-closed requirements verify their own arm after swapping.
   */
  async swapContext(options: {
    storageStatePath?: string;
    storageStateRequired?: boolean;
    recordVideo?: boolean;
    /** Callback invoked with the saved video path before context is closed */
    onVideoSaved?: (result: VideoRecordingResult) => void | Promise<void>;
    /** Extra HTTP headers to apply to the new context (merged with existing headers from connect) */
    extraHTTPHeaders?: Record<string, string>;
    /** HTTP Basic Authentication credentials for browser-level auth popups */
    httpCredentials?: { username: string; password: string };
  }): Promise<void> {
    if (!this.supportsContextSwap()) {
      throw new Error(
        'Browser context swap not supported (requires PlaywrightClient without extensions and an active browser handle)'
      );
    }

    if (!this.browser && !this.browserLease) {
      throw new Error('Browser not connected. Call connect() first.');
    }

    const nextStorageStatePath = options.storageStatePath;
    const storageStateRequired = options.storageStateRequired ?? false;
    const recordVideo = options.recordVideo ?? this.lastConnectOptions?.recordVideo ?? false;

    this.logger.info('[DirectPlaywright] Swapping browser context', {
      hasStorageState: !!nextStorageStatePath,
      storageStateRequired,
      recordVideoEnabled: !!recordVideo,
    });

    // Stop screencast before closing context (CDP session is tied to the page)
    const wasScreencasting = this.isScreencastActive();
    if (wasScreencasting) {
      await this.stopScreencast();
    }

    // Stop frame delivery but keep the encoder running. The encoder will resume
    // receiving frames from the new page after swap, so the same MP4 spans the
    // entire session.
    if (this.videoEnabled && this.page) {
      this.logger.info('[DirectPlaywright] Stopping screencast frame delivery for context swap');
      await this._stopScreencastFrameDelivery();
    }

    // Flush the in-progress trace BEFORE the context is destroyed. Tracing is
    // bound to a context; stopping with a path preserves the chunk so the
    // run-level trace survives the swap. We resume tracing on the new context
    // below. (Stopping without a path would silently discard the trace, and
    // leaving it running would make the final stopTracing() fail with
    // "Must start tracing before stopping" against a context that never started.)
    const resumeTracingAfterSwap = this.traceAdapter.isActive() && !!this.context;
    if (resumeTracingAfterSwap) {
      await this.traceAdapter.flushCurrentChunk();
    }

    // Close current context (keep browser alive).
    // Mark as intentional to prevent auto-restore noise.
    this.contextClosedIntentionally = true;
    this.pageClosedIntentionally = true;
    try {
      if (this.context) {
        await this.context.close();
      }
    } finally {
      this.context = null;
      this.page = null;
      this.contextClosedIntentionally = false;
      this.pageClosedIntentionally = false;
    }

    // Prepare new context options.
    const contextOptions: {
      viewport: { width: number; height: number };
      storageState?: string;
      recordVideo?: { dir: string; size?: { width: number; height: number } };
      extraHTTPHeaders?: Record<string, string>;
      httpCredentials?: { username: string; password: string };
      acceptDownloads?: boolean;
    } = {
      viewport: this.lastConnectOptions?.viewport ?? DEFAULT_VIEWPORT,
      acceptDownloads: true,
    };

    // Merge extraHTTPHeaders: start with lastConnectOptions, overlay with swapContext options
    const mergedHeaders: Record<string, string> = {
      ...this.lastConnectOptions?.extraHTTPHeaders,
      ...options.extraHTTPHeaders,
    };
    if (Object.keys(mergedHeaders).length > 0) {
      contextOptions.extraHTTPHeaders = mergedHeaders;
      // Also update lastConnectOptions so subsequent swaps preserve these headers
      if (this.lastConnectOptions) {
        this.lastConnectOptions.extraHTTPHeaders = mergedHeaders;
      }
      this.logger.debug('[DirectPlaywright] Custom HTTP headers configured for swapped context', {
        headerCount: Object.keys(mergedHeaders).length,
        headerNames: Object.keys(mergedHeaders),
      });
    }

    // Apply httpCredentials: use swap option if provided, else fall back to lastConnectOptions
    const resolvedHttpCredentials =
      options.httpCredentials ?? this.lastConnectOptions?.httpCredentials;
    if (resolvedHttpCredentials) {
      contextOptions.httpCredentials = resolvedHttpCredentials;
      if (this.lastConnectOptions) {
        this.lastConnectOptions.httpCredentials = resolvedHttpCredentials;
      }
    }

    // Apply storage state if present and readable.
    if (nextStorageStatePath) {
      try {
        await fs.access(nextStorageStatePath);
        contextOptions.storageState = nextStorageStatePath;
        this.logger.debug('[DirectPlaywright] Loading storage state for swapped context', {
          storageStatePath: nextStorageStatePath,
        });
      } catch (error) {
        if (storageStateRequired) {
          throw new Error(
            `Required browser storage state is unreadable during context swap: ${error instanceof Error ? error.message : String(error)}`
          );
        }
        this.logger.debug('[DirectPlaywright] Storage state file not found for swapped context', {
          storageStatePath: nextStorageStatePath,
        });
      }
    }

    // Configure video for the new context (video is configured at context creation time).
    // videoDir/videoPath are owned by the screencast encoder when alive — the
    // encoder spans context swaps, so don't null them when recordVideo:false.
    const videoConfig = this.resolveVideoConfig(recordVideo);
    this.videoEnabled = !!videoConfig;

    // Reset page-id tracking for new context (handles are within-context only).
    this._tabRegistry.reset();

    // Configure video recording and highlighting for the new context.
    // recordVideo is NOT passed to Playwright context — frames are delivered to
    // the live screencast encoder via page.screencast (CDP).
    if (videoConfig) {
      await fs.mkdir(videoConfig.dir, { recursive: true });
      this.pendingVideoDir = videoConfig.dir;

      // Enable highlighting when video is enabled (supports deferred video recording)
      this.highlightEnabled = true;
      this.highlightDuration = 150;
      this.highlightStylesInjected = false; // Reset so styles are re-injected on new context
      this.cursorOverlay.resetInjectionState();

      this.logger.info('[DirectPlaywright] Video recording enabled for swapped context', {
        videoDir: videoConfig.dir,
        size: videoConfig.size,
      });
    }

    this._contextCreatedAtMs = Date.now();
    const newContext = this.browserLease
      ? await this.browserLease.createContext(contextOptions)
      : await this.browser!.newContext(contextOptions);
    this.context = newContext;
    this.storageStatePath = nextStorageStatePath;
    if (this.lastConnectOptions) {
      this.lastConnectOptions = {
        ...this.lastConnectOptions,
        storageStatePath: nextStorageStatePath,
        storageStateRequired,
        recordVideo,
      };
    }

    this.attachContextListeners(this.context);
    // PURGE credential-bound groups (reapplyOnContextSwap: false, e.g. CF
    // Access secret headers) from the store before re-arming: a swap changes
    // credentials, and merely skipping them here would leave the stale group
    // in the store for the NEXT rebuild (any owner's setupContextRoutes call)
    // to replay onto the new credential's context. Their owner re-applies
    // fresh values after the swap's login.
    for (const [key, group] of this.storedContextRoutes) {
      if (!group.reapplyOnContextSwap) this.storedContextRoutes.delete(key);
    }
    // Re-arm the remaining groups before any page exists, so no request on
    // the fresh context can race ahead of the interception guards.
    await this.reapplyStoredContextRoutes();
    const swapPage = await this.createPageWithListeners(this.context, 'swap_context');

    // Track the focused page for screencast frame delivery
    const swapPageId = this._assignPageId(swapPage);
    this._currentFocusedPageId = swapPageId;

    // Restore IndexedDB data if the storage state file includes it
    if (nextStorageStatePath) {
      await this.restoreIndexedDBFromStorageState(nextStorageStatePath, storageStateRequired);
    }

    // Resume screencast frame delivery on new page (encoder is still running)
    if (this.videoEnabled && this.page) {
      await this._startScreencastOnPage(this.page, swapPageId);
    }

    // Restart CDP screencast on new page if it was active before swap
    if (wasScreencasting && this.page && this.screencastHandler) {
      await this.startScreencast(
        this.screencastHandler,
        this.screencastOptions,
        this.screencastVideoCallbacks
      );
    }

    // Resume tracing on the new context so the run-level trace continues across
    // the swap. Best-effort — a failed resume must not fail the swap itself.
    if (resumeTracingAfterSwap) {
      await this.traceAdapter.beginOnContext().catch((err) => {
        this.logger.warn('[DirectPlaywright] Failed to resume tracing after context swap', {
          error: err instanceof Error ? err.message : String(err),
        });
      });
    }

    this.logger.info('[DirectPlaywright] Context swapped successfully');
    this.recordBrowserLifecycleDiagnostic('context_swapped', {
      url: swapPage.url(),
    });
  }

  /**
   * Launch browser with the appropriate configuration.
   * Uses launchPersistentContext for extensions, standard launch otherwise.
   * Applies stealth plugin when requested.
   */
  private async launchBrowser(options: {
    browserMode: BrowserMode;
    storageStatePath?: string;
    storageStateRequired?: boolean;
    viewport?: { width: number; height: number };
    recordVideo?: boolean;
    stealth?: boolean | StealthOptions;
    extensions?: ExtensionOptions;
    extraHTTPHeaders?: Record<string, string>;
    httpCredentials?: { username: string; password: string };
    proxy?: BrowserProxyConfig;
  }): Promise<{ browser: Browser | null; context: BrowserContext }> {
    const {
      browserMode,
      storageStatePath,
      storageStateRequired,
      viewport,
      recordVideo,
      stealth,
      extensions,
      extraHTTPHeaders,
      httpCredentials,
      proxy,
    } = options;
    const resolvedViewport = viewport ?? DEFAULT_VIEWPORT;
    // Resolve at launch time. The MicroVM guest sets its host-specific policy
    // during bootstrap, after browser-core may already have been imported.
    const { launchTimeoutMs, launchWatchdogMs } = resolveBrowserLaunchTimeouts();

    const useStealthMode = stealth === true || (typeof stealth === 'object' && stealth.enabled);
    const hasExtensions = extensions?.paths && extensions.paths.length > 0;

    // Apply stealth plugin if requested (only needs to be done once)
    if (useStealthMode && !this.stealthPluginApplied) {
      playwrightExtra.use(StealthPlugin());
      this.stealthPluginApplied = true;
      this.logger.debug('[DirectPlaywright] Stealth plugin applied');
    }

    // Resolve a working chromium executable (handles version mismatches between
    // the bundled Playwright version and locally-installed browser binaries)
    const executablePath = await PlaywrightClient.resolveChromiumExecutable(this.logger);

    // Build base args
    const args: string[] = ['--no-sandbox', '--disable-dev-shm-usage'];

    // On macOS, prevent headed browser from stealing focus
    if (browserMode === 'headed' && process.platform === 'darwin') {
      args.push(
        '--disable-background-timer-throttling',
        '--disable-backgrounding-occluded-windows'
      );
    }

    // Build context options
    const videoConfig = this.resolveVideoConfig(recordVideo);
    const contextOptions: {
      viewport: { width: number; height: number };
      storageState?: string;
      recordVideo?: { dir: string; size?: { width: number; height: number } };
      extraHTTPHeaders?: Record<string, string>;
      httpCredentials?: { username: string; password: string };
      acceptDownloads?: boolean;
    } = {
      viewport: resolvedViewport,
      acceptDownloads: true,
    };

    // Add custom HTTP headers if provided
    if (extraHTTPHeaders && Object.keys(extraHTTPHeaders).length > 0) {
      contextOptions.extraHTTPHeaders = extraHTTPHeaders;
      this.logger.info('[DirectPlaywright] Custom HTTP headers configured', {
        headerCount: Object.keys(extraHTTPHeaders).length,
        headerNames: Object.keys(extraHTTPHeaders),
      });
    }

    // Add HTTP Basic Authentication credentials if provided
    if (httpCredentials) {
      contextOptions.httpCredentials = httpCredentials;
      this.logger.info('[DirectPlaywright] HTTP Basic Authentication configured');
    }

    // Log proxy configuration (redact any embedded credentials in the URL)
    if (proxy) {
      this.logger.info('[DirectPlaywright] Proxy configured', {
        server: proxy.server.replace(/\/\/[^@]+@/, '//***@'),
        bypass: proxy.bypass,
        hasAuth: !!(proxy.username && proxy.password),
      });
    }

    // Check for storage state
    if (storageStatePath) {
      try {
        await fs.access(storageStatePath);
        contextOptions.storageState = storageStatePath;
        this.logger.debug('[DirectPlaywright] Loading storage state from file', {
          storageStatePath,
        });
      } catch (error) {
        if (storageStateRequired) {
          throw new Error(
            `Required browser storage state is unreadable: ${error instanceof Error ? error.message : String(error)}`
          );
        }
        this.logger.debug('[DirectPlaywright] Storage state file not found, starting fresh', {
          storageStatePath,
        });
      }
    }

    // Add video recording config. recordVideo is NOT passed to Playwright —
    // frames are delivered to the screencast encoder via page.screencast (CDP).
    if (videoConfig) {
      await fs.mkdir(videoConfig.dir, { recursive: true });
      this.pendingVideoDir = videoConfig.dir;
      this.logger.info('[DirectPlaywright] Video recording enabled', {
        videoDir: videoConfig.dir,
        size: videoConfig.size,
      });
    }

    // Extension path requires launchPersistentContext
    if (hasExtensions) {
      // Setup user data directory
      this.userDataDir =
        extensions.userDataDir || path.join(this.tmpBaseDir, `playwright-userdata-${Date.now()}`);
      this.shouldCleanupUserDataDir = !extensions.persistUserData;
      await fs.mkdir(this.userDataDir, { recursive: true });

      // Add extension-specific args
      if (browserMode === 'headless') {
        args.push('--headless=new'); // New headless mode that supports extensions
      }
      args.push(`--disable-extensions-except=${extensions.paths.join(',')}`);
      args.push(`--load-extension=${extensions.paths.join(',')}`);

      this.logger.info('[DirectPlaywright] Loading extensions with persistent context', {
        userDataDir: this.userDataDir,
        extensionPaths: extensions.paths,
        cleanup: this.shouldCleanupUserDataDir,
      });

      // launchPersistentContext returns a BrowserContext directly (not Browser)
      const persistentLaunchOptions = {
        headless: false, // We use --headless=new in args for extension support
        args,
        // Same ordering rule as the standard launch below — see
        // launchTimeoutMs / launchWatchdogMs.
        timeout: launchTimeoutMs,
        ...(executablePath ? { executablePath } : {}),
        ...(proxy ? { proxy } : {}),
        ...contextOptions,
      };
      this._contextCreatedAtMs = Date.now();
      const context = await withOperationTimeout(
        // playwright-extra re-exports playwright-core types from its own resolved version,
        // which may differ from ours at the type level but is identical at runtime.
        useStealthMode
          ? (playwrightExtra.launchPersistentContext(
              this.userDataDir,
              persistentLaunchOptions
            ) as unknown as Promise<BrowserContext>)
          : playwrightChromium.launchPersistentContext(this.userDataDir, persistentLaunchOptions),
        launchWatchdogMs,
        '[DirectPlaywright] launchPersistentContext'
      );

      // For persistent context, we don't have a separate browser handle
      return { browser: null, context };
    }

    // Standard launch (no extensions)
    const launchOptions = {
      headless: browserMode === 'headless',
      args,
      timeout: launchTimeoutMs,
      ...(executablePath ? { executablePath } : {}),
      ...(proxy ? { proxy } : {}),
    };
    const browser = await withOperationTimeout(
      useStealthMode
        ? (playwrightExtra.launch(launchOptions) as unknown as Promise<Browser>)
        : playwrightChromium.launch(launchOptions),
      launchWatchdogMs,
      '[DirectPlaywright] chromium.launch'
    );

    this._contextCreatedAtMs = Date.now();
    const context = await withOperationTimeout(
      browser.newContext(contextOptions),
      15_000,
      '[DirectPlaywright] browser.newContext'
    );
    return { browser, context };
  }

  /**
   * Read the storage state file, verify Playwright-applied localStorage, and
   * restore its IndexedDB extension. Both are origin-scoped, so verification
   * and writes happen after navigating to the target origin.
   */
  private async restoreIndexedDBFromStorageState(
    storageStatePath: string,
    required: boolean
  ): Promise<void> {
    const startedAt = Date.now();
    let stateFingerprint = fingerprintStorageState(storageStatePath);
    let indexedDBData: IndexedDBData | null = null;
    let targetOrigin: string | null = null;
    let restoreResult: Awaited<ReturnType<typeof restoreIndexedDB>> | null = null;
    let expectedOrigins = 0;
    let expectedLocalStorage: Array<{ name: string; value: string }> = [];
    let matchedLocalStorageEntries: number | null = null;

    try {
      const raw = await fs.readFile(storageStatePath, 'utf-8');
      stateFingerprint = fingerprintStorageState(raw);
      const parsed = JSON.parse(raw) as {
        indexedDB?: IndexedDBData;
        origins?: Array<{
          origin: string;
          localStorage?: Array<{ name: string; value: string }>;
        }>;
        cookies?: Array<{ domain: string }>;
      };
      expectedOrigins = parsed.origins?.length ?? 0;
      if (parsed.origins && parsed.origins.length > 0) {
        targetOrigin = parsed.origins[0].origin;
        expectedLocalStorage = parsed.origins[0].localStorage ?? [];
      }
      if (!targetOrigin && parsed.cookies && parsed.cookies.length > 0) {
        const domain = parsed.cookies[0].domain.replace(/^\./, '');
        targetOrigin = `https://${domain}`;
      }
      if (
        !parsed.indexedDB ||
        !parsed.indexedDB.databases ||
        parsed.indexedDB.databases.length === 0
      ) {
        const context = this.authDiagnosticContext();
        this.pushAuthDiagnostic({
          ...context,
          kind: 'storage_restore',
          phase: 'started',
          targetOrigin: targetOrigin ? sanitizeDiagnosticUrl(targetOrigin) : null,
          stateFingerprint,
          expectedDatabases: 0,
          expectedDatabaseNames: [],
          expectedStores: 0,
          expectedStoreNames: [],
          expectedEntries: 0,
          expectedOrigins,
          expectedLocalStorageEntries: expectedLocalStorage.length,
        });
        if (expectedLocalStorage.length > 0) {
          const page = this.page;
          if (!page || page.isClosed()) {
            throw new Error(
              'No live page is available for localStorage authentication state verification'
            );
          }
          if (!targetOrigin) {
            throw new Error('No origin found for localStorage authentication state verification');
          }
          matchedLocalStorageEntries = await withQuietOriginPage(page, targetOrigin, () =>
            page.evaluate(
              (expected) =>
                expected.filter((entry) => localStorage.getItem(entry.name) === entry.value).length,
              expectedLocalStorage
            )
          );
          assertRequiredLocalStorageRestored({
            required,
            expectedEntries: expectedLocalStorage.length,
            matchedEntries: matchedLocalStorageEntries,
          });
        }
        this.pushAuthDiagnostic({
          ...this.authDiagnosticContext(),
          kind: 'storage_restore',
          phase: 'succeeded',
          targetOrigin: targetOrigin ? sanitizeDiagnosticUrl(targetOrigin) : null,
          stateFingerprint,
          expectedDatabases: 0,
          expectedDatabaseNames: [],
          expectedStores: 0,
          expectedStoreNames: [],
          expectedEntries: 0,
          expectedOrigins,
          expectedLocalStorageEntries: expectedLocalStorage.length,
          matchedLocalStorageEntries: matchedLocalStorageEntries ?? 0,
          restoredDatabases: 0,
          restoredStores: 0,
          restoredEntries: 0,
          skippedDatabases: [],
          skippedStores: [],
          durationMs: Date.now() - startedAt,
        });
        this.logger.info('[DirectPlaywright] Storage state contains no IndexedDB payload', {
          required,
          stateFingerprint,
          expectedOrigins,
          expectedLocalStorageEntries: expectedLocalStorage.length,
          matchedLocalStorageEntries: matchedLocalStorageEntries ?? 0,
        });
        return;
      }
      indexedDBData = parsed.indexedDB;
      const expectation = getIndexedDBRestoreExpectation(indexedDBData);
      const expectedDatabaseNames = indexedDBData.databases.map((database) => database.name);
      const expectedStoreNames = indexedDBData.databases.flatMap((database) =>
        database.objectStores
          .filter((store) => store.entries.length > 0)
          .map((store) => `${database.name}/${store.name}`)
      );

      const page = this.page;
      if (!page || page.isClosed()) {
        throw new Error('No live page is available for IndexedDB authentication state restoration');
      }

      // Determine the origin to navigate to for IndexedDB restoration.
      // Use the first origin from Playwright's localStorage origins, or fall back to cookies.
      if (!targetOrigin) {
        throw new Error('No origin found for IndexedDB authentication state restoration');
      }

      this.pushAuthDiagnostic({
        ...this.authDiagnosticContext(),
        kind: 'storage_restore',
        phase: 'started',
        targetOrigin: sanitizeDiagnosticUrl(targetOrigin),
        stateFingerprint,
        expectedDatabases: expectation.databases,
        expectedDatabaseNames,
        expectedStores: expectation.stores,
        expectedStoreNames,
        expectedEntries: expectation.entries,
        expectedOrigins,
        expectedLocalStorageEntries: expectedLocalStorage.length,
      });
      this.logger.info('[DirectPlaywright] Restoring IndexedDB authentication state', {
        targetOrigin,
        required,
        stateFingerprint,
        databaseCount: expectation.databases,
        databaseNames: expectedDatabaseNames,
        storeCount: expectation.stores,
        entryCount: expectation.entries,
      });

      // Park the page on the origin with a blank document so IndexedDB writes
      // land in the right scope while no application script holds a connection
      // open against the databases being restored.
      const restorePayload = indexedDBData;
      const restored = await withQuietOriginPage(page, targetOrigin, async () => {
        const matched = await page.evaluate(
          (expected) =>
            expected.filter((entry) => localStorage.getItem(entry.name) === entry.value).length,
          expectedLocalStorage
        );
        // Recorded before the assertion so a failure diagnostic keeps the count.
        matchedLocalStorageEntries = matched;
        assertRequiredLocalStorageRestored({
          required,
          expectedEntries: expectedLocalStorage.length,
          matchedEntries: matched,
        });
        const result = await withOperationTimeout(
          restoreIndexedDB(page, restorePayload),
          getIndexedDBRestoreWatchdogMs(restorePayload),
          '[DirectPlaywright] restoreIndexedDB'
        );
        return { matched, result };
      });
      matchedLocalStorageEntries = restored.matched;
      restoreResult = restored.result;
      assertCompleteIndexedDBRestore(indexedDBData, restoreResult);

      const durationMs = Date.now() - startedAt;
      this.pushAuthDiagnostic({
        ...this.authDiagnosticContext(),
        kind: 'storage_restore',
        phase: 'succeeded',
        targetOrigin: sanitizeDiagnosticUrl(targetOrigin),
        stateFingerprint,
        expectedDatabases: expectation.databases,
        expectedDatabaseNames,
        expectedStores: expectation.stores,
        expectedStoreNames,
        expectedEntries: expectation.entries,
        expectedOrigins,
        expectedLocalStorageEntries: expectedLocalStorage.length,
        matchedLocalStorageEntries,
        ...restoreResult,
        durationMs,
      });
      this.logger.info('[DirectPlaywright] IndexedDB authentication state restored completely', {
        required,
        stateFingerprint,
        durationMs,
        expectedOrigins,
        expectedLocalStorageEntries: expectedLocalStorage.length,
        matchedLocalStorageEntries,
        ...restoreResult,
      });
    } catch (error) {
      const expectation = indexedDBData
        ? getIndexedDBRestoreExpectation(indexedDBData)
        : { databases: 0, stores: 0, entries: 0 };
      const expectedDatabaseNames = indexedDBData?.databases.map((database) => database.name) ?? [];
      const expectedStoreNames =
        indexedDBData?.databases.flatMap((database) =>
          database.objectStores
            .filter((store) => store.entries.length > 0)
            .map((store) => `${database.name}/${store.name}`)
        ) ?? [];
      const message = error instanceof Error ? error.message : String(error);
      this.pushAuthDiagnostic({
        ...this.authDiagnosticContext(),
        kind: 'storage_restore',
        phase: 'failed',
        targetOrigin: targetOrigin ? sanitizeDiagnosticUrl(targetOrigin) : null,
        stateFingerprint,
        expectedDatabases: expectation.databases,
        expectedDatabaseNames,
        expectedStores: expectation.stores,
        expectedStoreNames,
        expectedEntries: expectation.entries,
        expectedOrigins,
        expectedLocalStorageEntries: expectedLocalStorage.length,
        ...(matchedLocalStorageEntries === null ? {} : { matchedLocalStorageEntries }),
        ...(restoreResult ?? {}),
        durationMs: Date.now() - startedAt,
        error: sanitizeDiagnosticMessage(message),
      });
      this.logger.warn('[DirectPlaywright] Failed to restore browser authentication state', {
        error: message,
        required,
        stateFingerprint,
        durationMs: Date.now() - startedAt,
        ...restoreResult,
      });
      if (required) throw error;
    }
  }

  /** Re-arm context-bound state before recovery returns to the interrupted URL. */
  private async restoreRecreatedContextState(storageStatePath?: string): Promise<void> {
    // Re-arm scoped headers before any authentication-origin navigation.
    await this.reapplyStoredContextRoutes();
    if (storageStatePath) {
      await this.restoreIndexedDBFromStorageState(
        storageStatePath,
        this.lastConnectOptions?.storageStateRequired ?? false
      );
    }
    await this.resumeTracingOnRecreatedContext();
  }

  /**
   * Resolve a working Chromium executable path, handling version mismatches
   * between the Playwright version bundled in this package and the browser
   * binaries installed on the system.
   *
   * When the exact expected binary doesn't exist (e.g. Playwright expects
   * chromium-1191 but only chromium-1194 is installed), this finds the
   * newest available chromium binary as a fallback.
   */
  private static async resolveChromiumExecutable(
    logger: BrowserLogger
  ): Promise<string | undefined> {
    // Explicit override: environments that ship a system Chromium instead of
    // Playwright's bundled (glibc) build — e.g. the Alpine/musl sandbox-agent
    // image — set PLAYWRIGHT_CHROMIUM_EXECUTABLE_PATH to the system binary.
    // Only honored when set, so it's a no-op everywhere else.
    const override = process.env.PLAYWRIGHT_CHROMIUM_EXECUTABLE_PATH;
    if (override) {
      try {
        // Require the executable bit so a present-but-not-runnable path (e.g. a
        // wrong-arch binary that exists) fails here with a clear message rather
        // than as an opaque ENOEXEC at launch.
        await fs.access(override, fs.constants.X_OK);
        return override;
      } catch {
        logger.warn(
          '[DirectPlaywright] PLAYWRIGHT_CHROMIUM_EXECUTABLE_PATH set but binary not executable/found',
          { override }
        );
      }
    }

    const defaultPath = playwrightChromium.executablePath();

    try {
      await fs.access(defaultPath);
      return undefined; // default is fine, no override needed
    } catch {
      // Default binary doesn't exist — try to find an alternative
    }

    // Derive the browsers cache directory from the expected path
    // e.g. /Users/x/Library/Caches/ms-playwright/chromium-1191/chrome-mac/...
    //   → /Users/x/Library/Caches/ms-playwright/
    const parts = defaultPath.split(path.sep);
    const msPlaywrightIdx = parts.findIndex((p) => p === 'ms-playwright');
    if (msPlaywrightIdx === -1) {
      logger.warn('[DirectPlaywright] Cannot locate ms-playwright cache directory', {
        defaultPath,
      });
      return undefined;
    }

    const cacheDir = parts.slice(0, msPlaywrightIdx + 1).join(path.sep);
    // The relative path inside the chromium-XXXX directory (platform-specific)
    // e.g. chrome-mac/Chromium.app/Contents/MacOS/Chromium
    const relativeBinaryPath = parts.slice(msPlaywrightIdx + 2).join(path.sep);

    try {
      const entries = await fs.readdir(cacheDir);
      const chromiumDirs = entries
        .filter((e) => e.startsWith('chromium-') && !e.includes('headless'))
        .sort((a, b) => {
          const revA = parseInt(a.split('-')[1] ?? '0', 10);
          const revB = parseInt(b.split('-')[1] ?? '0', 10);
          return revB - revA; // newest first
        });

      for (const dir of chromiumDirs) {
        const candidate = path.join(cacheDir, dir, relativeBinaryPath);
        try {
          await fs.access(candidate);
          logger.warn('[DirectPlaywright] Using fallback Chromium binary (version mismatch)', {
            expected: defaultPath,
            using: candidate,
          });
          return candidate;
        } catch {
          continue;
        }
      }

      logger.warn('[DirectPlaywright] No compatible Chromium binary found', {
        cacheDir,
        candidates: chromiumDirs,
        expectedRelativePath: relativeBinaryPath,
      });
    } catch (err) {
      logger.warn('[DirectPlaywright] Failed to scan browser cache directory', {
        cacheDir,
        error: String(err),
      });
    }

    return undefined;
  }

  async disconnect(): Promise<void> {
    this.logger.info('[DirectPlaywright] Disconnecting');

    if (this.connectAbortController && !this.connectAbortController.signal.aborted) {
      this.logger.warn('[DirectPlaywright] Cancelling in-flight browser connect');
      this.connectAbortController.abort(new Error('Browser connect cancelled by disconnect'));
    }

    // Mark intentional context closure to avoid noisy "unexpected" logs from the listener
    this.disconnectInProgress = true;
    this.contextClosedIntentionally = true;
    this.pageClosedIntentionally = true;

    // Timeout for cleanup operations - if Playwright hangs, we move on to avoid blocking jobs
    const CLEANUP_TIMEOUT_MS = 60_000;

    const withTimeout = async <T>(operation: Promise<T>, name: string): Promise<T | null> => {
      try {
        return await Promise.race([
          operation,
          new Promise<never>((_, reject) =>
            setTimeout(
              () => reject(new Error(`${name} timed out after ${CLEANUP_TIMEOUT_MS}ms`)),
              CLEANUP_TIMEOUT_MS
            )
          ),
        ]);
      } catch (err) {
        this.logger.warn(`[DirectPlaywright] ${name} failed or timed out`, {
          error: err instanceof Error ? err.message : String(err),
        });
        return null;
      }
    };

    try {
      // Drop any isolated api_sequence request cookie state so it never outlives
      // the browser session that seeded it.
      await this.endApiRequestSession();

      // Stop screencast before closing context
      if (this.isScreencastActive()) {
        await this.stopScreencast();
      }
      this.screencastManager = null;
      this.screencastHandler = null;
      this.screencastOptions = undefined;
      this.screencastVideoCallbacks = undefined;

      // Kill screencast file encoder if still active (safety net for error paths
      // where saveVideo() was not called before disconnect)
      if (this._screencastEncoder) {
        this.logger.warn(
          '[DirectPlaywright] Killing orphaned screencast encoder during disconnect'
        );
        this._screencastEncoder.kill();
        this._screencastEncoder = null;
      }
      // Unsubscribe each disposable so the bus tears down its CDP screencast
      // — clearing the map alone would leak subscribers (the bus would keep
      // the CDP session open thinking they're live).
      const orphans = [...this._screencastDisposables.values()];
      this._screencastDisposables.clear();
      await Promise.allSettled(orphans.map((info) => info.dispose()));

      // Stop tracing if active
      if (this.traceAdapter.isActive() && this.context) {
        await withTimeout(this.context.tracing.stop(), 'Tracing stop');
        this.traceAdapter.markInactive();
      }

      if (this.videoEnabled && !this.videoPath) {
        this.logger.warn('[DirectPlaywright] Video recording was enabled but no video was saved');
      }

      // Note: Video should be saved via saveVideo() BEFORE calling disconnect().
      // We don't auto-save here to give caller control over the timing.

      if (this.browserLease) {
        // Pool path: release the lease (which closes the context)
        await withTimeout(this.browserLease.release(), 'Lease release');
        this.context = null;
        this.browserLease = null;
      } else {
        if (this.context) {
          await withTimeout(this.context.close(), 'Context close');
          this.context = null;
        }
        if (this.browser) {
          await withTimeout(this.browser.close(), 'Browser close');
          this.browser = null;
        }
      }
      this.page = null;

      // Clear state (but keep video path for getVideoPath())
      this._capturedConsoleMessages = [];
      this._capturedNetworkRequests.clear();
      this._lastSnapshotConsoleIndex = 0;
      this._snapshotedFailedRequestUrls.clear();
      this._pendingDialogs = [];
      this.setPendingArm(null, 'disconnect');
      this.lastSnapshotYaml = '';

      await this.downloads.cleanupDirectory();

      // Cleanup trace temp directories (trace zips already extracted + uploaded
      // by stopTracing). Covers every per-context chunk dir created this session
      // — including chunks whose flush failed — so swapped-context runs don't
      // leak temp dirs. Removed in parallel; failures are logged, not fatal.
      await this.traceAdapter.cleanupTraceDirectories();
      // Drop stored context routes (e.g. CF Access scoped-header injection) so a
      // reused client instance can't re-arm a prior session's secret onto a new
      // context.
      this.storedContextRoutes.clear();
      this.armedContextRouteHandlers = [];
      this.armedContextRouteContext = null;
      this.downloads.reset();

      // Cleanup user data directory if we created it for extensions
      if (this.shouldCleanupUserDataDir && this.userDataDir) {
        try {
          await fs.rm(this.userDataDir, { recursive: true, force: true });
          this.logger.debug('[DirectPlaywright] Cleaned up user data directory', {
            userDataDir: this.userDataDir,
          });
        } catch (err) {
          this.logger.warn('[DirectPlaywright] Failed to clean up user data directory', {
            userDataDir: this.userDataDir,
            error: err instanceof Error ? err.message : String(err),
          });
        }
      }

      // Cleanup snapshot files written for bash tool analysis
      if (this.snapshotFilePaths.size > 0) {
        for (const filePath of this.snapshotFilePaths) {
          try {
            await fs.unlink(filePath);
          } catch (err) {
            this.logger.warn('[DirectPlaywright] Failed to clean up snapshot file', {
              filePath,
              error: err instanceof Error ? err.message : String(err),
            });
          }
        }
        this.logger.debug('[DirectPlaywright] Cleaned up snapshot files', {
          count: this.snapshotFilePaths.size,
        });
        this.snapshotFilePaths.clear();
      }

      // Reset extension and stealth state
      this.userDataDir = null;
      this.shouldCleanupUserDataDir = false;
      this.extensionsEnabled = false;
      this.stealthEnabled = false;

      this.logger.info('[DirectPlaywright] Disconnected');
    } finally {
      await this.releaseTestBrowserLockIfHeld();
      this.contextClosedIntentionally = false;
      this.pageClosedIntentionally = false;
      this.disconnectInProgress = false;
    }
  }

  private async cleanupFailedConnect(error: unknown): Promise<void> {
    this.logger.warn('[DirectPlaywright] Cleaning up after failed connect', {
      error: error instanceof Error ? error.message : String(error),
    });

    try {
      await this.disconnect();
    } catch (disconnectError) {
      this.logger.warn('[DirectPlaywright] Failed cleanup after connect error', {
        error: disconnectError instanceof Error ? disconnectError.message : String(disconnectError),
      });
      await this.releaseTestBrowserLockIfHeld();
    }
  }

  private async releaseTestBrowserLockIfHeld(): Promise<void> {
    if (!this.releaseTestBrowserLock) {
      return;
    }

    const release = this.releaseTestBrowserLock;
    this.releaseTestBrowserLock = null;
    await release();
  }

  // ================ NAVIGATION ================

  async navigate(url: string, opts?: BrowserClientOptions): Promise<string> {
    return this.raceAgainstSignal('navigate', opts?.signal, async () => {
      const dialogMsg = this.getPendingDialogMessage();
      if (dialogMsg) return dialogMsg;

      const page = await this.getPage();
      this.clearNavigationArtifacts();

      this.logger.debug('[DirectPlaywright] Navigating', { url });

      try {
        await page.goto(url, {
          waitUntil: 'domcontentloaded',
          timeout: opts?.timeoutMs ?? TIMEOUTS.NAVIGATE,
        });
      } catch (err) {
        if (isPlaywrightTargetClosedError(err)) {
          throw toBrowserNavigateTargetClosedError(err, {
            operation: 'goto',
            url,
            lastKnownUrl: this.lastKnownUrl,
            pendingCrashType: this._pendingCrashType,
            pendingCrashUrl: this._pendingCrashUrl,
            signalAborted: opts?.signal?.aborted ?? false,
            signalReason: abortSignalReason(opts?.signal),
          });
        }
        throw err;
      }

      // Track URL for crash recovery
      this.lastKnownUrl = url;

      // Wait for page to stabilize (network idle with timeout)
      await this.stabilize('navigation');

      // Navigation replaced the DOM — reset so cursor/highlights are re-injected on next action
      this.highlightStylesInjected = false;
      this.cursorOverlay.resetInjectionState();

      // Fast-drive: skip the post-navigation snapshot entirely. Every
      // navigate-node caller discards this return, and when the agent calls
      // browser_navigate directly it re-perceives on its next turn with its own
      // ref-less browser_snapshot / run_code observe — so ANY capture here (even
      // ref-less) is perception work the ref-less loop just repeats. Confirm the
      // navigation and return; the next ref-less capture seeds `lastReflessYaml`.
      // Ref-mode keeps the forced augmented capture below, which also warms the
      // reuse cache for the next ref-based snapshot.
      //
      // Use getPage() for title to get a fresh reference — captureSnapshotInternal
      // may trigger browser recovery if the page closed, replacing this.page.
      // Route through the live-snapshot manager (force) so this capture
      // serializes with any in-flight warm and refreshes `current`.
      if (this.navigationSnapshotGate.shouldSkipPostNavigationSnapshot()) {
        const reflessTitle = await this.getPage().then((p) =>
          withOperationTimeoutFallback(p.title(), 2_000, 'navigate:postNavigationTitle', '')
        );
        return this.formatMCPResponse(
          `Navigated to ${url}`,
          undefined,
          `await page.goto('${this.escapeString(url)}');`,
          reflessTitle
        );
      }
      const [snapshotYaml, title] = await Promise.all([
        withOperationTimeoutFallback(
          this.captureTreeYaml(opts?.signal, { force: true }),
          5_000,
          'navigate:postNavigationSnapshot',
          ''
        ),
        this.getPage().then((p) =>
          withOperationTimeoutFallback(p.title(), 2_000, 'navigate:postNavigationTitle', '')
        ),
      ]);
      return this.formatMCPResponse(
        `Navigated to ${url}`,
        snapshotYaml,
        `await page.goto('${this.escapeString(url)}');`,
        title
      );
    });
  }

  async navigateBack(opts?: BrowserClientOptions): Promise<string> {
    return this.raceAgainstSignal('navigateBack', opts?.signal, async () => {
      const dialogMsg = this.getPendingDialogMessage();
      if (dialogMsg) return dialogMsg;

      const page = await this.getPage();
      this.clearNavigationArtifacts();

      this.logger.debug('[DirectPlaywright] Navigating back');

      try {
        await page.goBack({ timeout: opts?.timeoutMs ?? TIMEOUTS.NAVIGATE });
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : String(err);
        this.logger.debug('[DirectPlaywright] Navigating back failed', { error: errorMsg });
        return this.formatMCPResponse(
          `Error: browser could not navigate back: ${errorMsg}`,
          undefined,
          'await page.goBack();'
        );
      }

      // Wait for page to stabilize (network idle with timeout)
      await this.stabilize('navigation');

      // Navigation replaced the DOM — reset so cursor/highlights are re-injected on next action
      this.highlightStylesInjected = false;
      this.cursorOverlay.resetInjectionState();

      // Fast-drive: skip the post-navigation snapshot entirely; the ref-less
      // loop re-perceives on its next turn. See navigate() for the rationale.
      if (this.navigationSnapshotGate.shouldSkipPostNavigationSnapshot()) {
        const reflessTitle = await this.getPage()
          .then((p) => p.title())
          .catch(() => '');
        return this.formatMCPResponse(
          'Navigated back',
          undefined,
          'await page.goBack();',
          reflessTitle
        );
      }
      const [snapshotYaml, title] = await Promise.all([
        this.captureTreeYaml(opts?.signal, { force: true }),
        this.getPage().then((p) => p.title()),
      ]);
      return this.formatMCPResponse('Navigated back', snapshotYaml, 'await page.goBack();', title);
    });
  }

  // ================ PAGE INSPECTION ================

  async snapshot(
    opts?: BrowserClientOptions & {
      mode?: SnapshotMode;
      includeScreenshot?: boolean;
      depth?: number;
      filterToInteractive?: boolean;
      maxImageDimension?: number;
      search?: string;
    }
  ): Promise<string | MCPContentResult> {
    const dialogMsg = this.getPendingDialogMessage();
    if (dialogMsg) return dialogMsg;

    const page = await this.getPage();

    // Clear cached element metadata since refs change with each snapshot
    this.clearCachedMetadata();

    // Resolve mode: explicit mode takes priority, then includeScreenshot, then default 'tree'
    // Note: includeScreenshot is deprecated, use mode instead
    let effectiveMode: SnapshotMode = 'tree';
    if (opts?.mode) {
      effectiveMode = opts.mode;
    } else if (opts?.includeScreenshot) {
      effectiveMode = 'both';
    }
    if (effectiveMode === 'auto') {
      effectiveMode = 'tree';
    }

    // Wait for DOM to settle before capturing snapshot (signal-aware so an
    // aborted tool call skips the wait promptly instead of orphaning it)
    await this.traced('stabilize', 'DOM stabilize', () => this.stabilize('dom-only', opts?.signal));

    // Screenshot-only mode: skip accessibility tree entirely
    if (effectiveMode === 'screenshot') {
      try {
        const title = await page.title();
        const buffer = await page.screenshot({
          type: 'jpeg',
          quality: 90,
          fullPage: false,
          // Hard cap: a screenshot blocks on a composited frame, which a jammed
          // renderer can't produce — without a timeout it orphans for minutes.
          timeout: TIMEOUTS.SCREENSHOT,
        });
        const base64 = buffer.toString('base64');
        const downsampledBase64 = await downsampleImage(base64, opts?.maxImageDimension);
        const media = [{ data: downsampledBase64, mimeType: 'image/jpeg' }];

        return {
          text: `[Screenshot-only mode]\n- Page: ${page.url()}\n- Title: ${title}\n\nUse coordinate-based clicking (x, y) to interact with elements visible in the screenshot.`,
          media,
          images: media,
        };
      } catch (err) {
        this.logger.warn(
          '[DirectPlaywright] Failed to capture screenshot in screenshot-only mode',
          {
            error: err instanceof Error ? err.message : String(err),
          }
        );
        throw new Error('Failed to capture screenshot in screenshot-only mode');
      }
    }

    // Ref-less tree (fast-drive): capture the RAW substrate with no ai-mode ref
    // stamping and SKIP the per-ref augmentation pass entirely. Text-only — the
    // agent perceives structure + drives via Playwright code; vision is a
    // separate browser_screenshot call. `formatMCPResponse` embeds the YAML and
    // the semantic formatter (in the tool layer) auto-detects ref-less input to
    // keep duplicate-label disambiguation working without refs.
    if (effectiveMode === 'tree-refless') {
      const [rawYaml, reflessTitle] = await Promise.all([
        this.captureRawSubstrate(opts?.signal, { refless: true }),
        this.raceAgainstSignal('snapshot:title', opts?.signal, () =>
          withOperationTimeout(
            this.getPage().then((p) => p.title()),
            TIMEOUTS.GET_TITLE,
            'snapshot:title'
          )
        ).catch(() => ''),
      ]);
      this.lastReflessYaml = rawYaml;
      // Render via the dedicated ref-less formatter (the shared semantic
      // formatter gates on refs and would emit nothing). Pure projection — no
      // further DOM access. Passed as `result` text; formatMCPResponse adds the
      // page URL/title header.
      const reflessText = formatReflessSnapshot(rawYaml, { search: opts?.search });
      return this.formatMCPResponse(reflessText, undefined, undefined, reflessTitle);
    }

    // Screenshot catches locally so its failure doesn't reject the whole
    // Promise.all (tree path still returns text-only). Title goes via
    // getPage() because captureSnapshotInternal may trigger browser recovery
    // that replaces this.page.
    const captureScreenshot: Promise<string | null> =
      effectiveMode === 'both'
        ? this.traced('screenshot', 'capture + downsample screenshot', () =>
            // Raced against the tool signal AND given a hard Playwright timeout:
            // a viewport screenshot still waits for a composited frame, which a
            // jammed renderer can't hand back, so without both bounds it orphans
            // for minutes behind the wedge.
            this.raceAgainstSignal('snapshot:screenshot', opts?.signal, async () => {
              const buffer = await page.screenshot({
                type: 'jpeg',
                quality: 90,
                fullPage: false,
                timeout: TIMEOUTS.SCREENSHOT,
              });
              return downsampleImage(buffer.toString('base64'), opts?.maxImageDimension);
            })
          ).catch((err) => {
            this.logger.warn('[DirectPlaywright] Failed to capture screenshot for snapshot', {
              error: err instanceof Error ? err.message : String(err),
            });
            return null;
          })
        : Promise.resolve(null);

    // Tree capture. When live-snapshot is enabled, captureTreeYaml routes
    // through the manager so an unchanged DOM reuses the cached substrate
    // instantly (revision match) instead of rebuilding it. Title is raced +
    // hard-bounded — `page.title()` reads `document.title` and can hang on a
    // blocked renderer; a failed title degrades to empty, not a failed snapshot.
    const [rawSnapshotYaml, title, downsampledBase64] = await Promise.all([
      this.captureTreeYaml(opts?.signal),
      this.raceAgainstSignal('snapshot:title', opts?.signal, () =>
        withOperationTimeout(
          this.getPage().then((p) => p.title()),
          TIMEOUTS.GET_TITLE,
          'snapshot:title'
        )
      ).catch(() => ''),
      captureScreenshot,
    ]);

    // NOTE: Coordinate enrichment removed - stale coords caused misclicks when page scrolled/animated
    // between snapshot and click. Agents should use ref-based clicking or browser_evaluate for fresh coords.

    // Apply filtering if requested
    const snapshotYaml = this.filterSnapshot(
      rawSnapshotYaml,
      opts?.depth,
      opts?.filterToInteractive
    );
    const snapshotText = this.formatMCPResponse('', snapshotYaml, undefined, title);

    // DOM complexity detection: warn if tree is too large (observability only)
    const DOM_COMPLEXITY_THRESHOLD = 30000; // tokens
    const estimatedTokens = estimateTextTokens(snapshotText);
    const refCount = (snapshotYaml.match(/\[ref=/g) || []).length;

    if (estimatedTokens > DOM_COMPLEXITY_THRESHOLD) {
      this.logger.warn('[DirectPlaywright] Complex DOM detected', {
        estimatedTokens,
        refCount,
        threshold: DOM_COMPLEXITY_THRESHOLD,
      });
    }

    // Tree-only mode: return just the accessibility tree
    if (effectiveMode === 'tree') {
      return snapshotText;
    }

    // Both mode: include screenshot when it succeeded; otherwise fall back to
    // text-only — matching the pre-parallelization behavior.
    if (!downsampledBase64) return snapshotText;
    const media = [{ data: downsampledBase64, mimeType: 'image/jpeg' }];
    return { text: snapshotText, media, images: media };
  }

  /**
   * Filter a snapshot YAML by depth and/or interactive elements.
   */
  private filterSnapshot(yaml: string, depth?: number, filterToInteractive?: boolean): string {
    if (!depth && !filterToInteractive) {
      return yaml;
    }

    const lines = yaml.split('\n');
    const filteredLines: string[] = [];

    // Interactive element roles to keep when filtering
    const interactiveRoles = new Set([
      'button',
      'link',
      'textbox',
      'checkbox',
      'radio',
      'combobox',
      'listbox',
      'option',
      'menuitem',
      'menu',
      'menubar',
      'tab',
      'tablist',
      'slider',
      'spinbutton',
      'searchbox',
      'switch',
      'dialog',
      'alertdialog',
    ]);

    for (const line of lines) {
      // Calculate depth from indentation (each level is 2 spaces in YAML)
      const leadingSpaces = line.match(/^(\s*)/)?.[1]?.length ?? 0;
      const lineDepth = Math.floor(leadingSpaces / 2);

      // Filter by depth
      if (depth !== undefined && lineDepth > depth) {
        continue;
      }

      // Filter to interactive elements
      if (filterToInteractive) {
        // Keep lines that contain an interactive role or a ref (which means it's interactive)
        const hasRef = line.includes('[ref=');
        const hasInteractiveRole = [...interactiveRoles].some((role) =>
          line.toLowerCase().includes(`- ${role}`)
        );

        // Also keep structural lines (page, main, navigation) and lines with refs
        const isStructural =
          line.includes('- page') ||
          line.includes('- main') ||
          line.includes('- navigation') ||
          line.includes('- banner') ||
          line.includes('- contentinfo');

        if (!hasRef && !hasInteractiveRole && !isStructural && line.trim()) {
          continue;
        }
      }

      filteredLines.push(line);
    }

    return filteredLines.join('\n');
  }

  async takeScreenshot(opts?: {
    fullPage?: boolean;
    type?: 'png' | 'jpeg';
    element?: string;
    ref?: string;
    /** Clip to a viewport region (page screenshots only — ignored when `ref` is set). */
    clip?: { x: number; y: number; width: number; height: number };
    label?: string;
    signal?: AbortSignal;
    timeoutMs?: number;
  }): Promise<string | null> {
    const page = await this.getPage();

    // Wait for DOM to settle before capturing screenshot
    await this.stabilize('dom-only');

    try {
      this.logger.debug('[DirectPlaywright] Taking screenshot', {
        type: opts?.type ?? 'jpeg',
        fullPage: opts?.fullPage ?? false,
        hasRef: !!opts?.ref,
        hasClip: !!opts?.clip,
      });

      const screenshotOptions: {
        type: 'png' | 'jpeg';
        fullPage: boolean;
        clip?: { x: number; y: number; width: number; height: number };
      } = {
        type: opts?.type ?? 'jpeg',
        // `clip` and `fullPage` are mutually exclusive in Playwright; a region
        // capture is inherently viewport-scoped, so force fullPage off when clipping.
        fullPage: opts?.clip ? false : (opts?.fullPage ?? false),
      };

      let buffer: Buffer;

      if (opts?.ref) {
        const locator = await this.resolveRef(opts.ref);
        // locator.screenshot captures the element itself; clip does not apply
        // (screenshotOptions.clip is unset on this branch).
        buffer = await locator.screenshot(screenshotOptions);
      } else {
        if (opts?.clip) screenshotOptions.clip = opts.clip;
        buffer = await page.screenshot(screenshotOptions);
      }

      // Return full-quality image - downsampling happens at the tool layer
      // (browserTools.ts) for AI agent, while browser stream gets full quality
      const mime = opts?.type === 'png' ? 'image/png' : 'image/jpeg';
      return `data:${mime};base64,${buffer.toString('base64')}`;
    } catch (err) {
      this.logger.error('[DirectPlaywright] Screenshot error', { error: err });
      return null;
    }
  }

  async evaluate<T = unknown>(
    fn: string,
    opts?: { label?: string; ref?: string; signal?: AbortSignal; timeoutMs?: number }
  ): Promise<T> {
    const dialogMsg = this.getPendingDialogMessage();
    if (dialogMsg) throw new Error(dialogMsg);

    const page = await this.getPage();

    try {
      let result: unknown;

      if (opts?.ref) {
        const locator = await this.resolveRef(opts.ref);
        result = await locator.evaluate(new Function(`return (${fn})()`) as () => unknown);
      } else {
        result = await page.evaluate(new Function(`return (${fn})()`) as () => unknown);
      }

      return this.formatMCPResponse(serializeRunCodeResult(result), undefined, fn) as unknown as T;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : String(err);
      return this.formatMCPResponse(`Error: ${errorMsg}`, undefined, fn) as unknown as T;
    }
  }

  async runCode(
    code: string,
    opts?: BrowserClientOptions & {
      bindings?: Record<string, unknown>;
      onOutcome?: (signals: RunCodeExecutionSignals) => void;
    }
  ): Promise<unknown> {
    // Carry-over guard: a dialog left pending from a prior action (e.g. an
    // un-armed block, or a non-run_code action). CONSUME it (don't just peek) so
    // it clears after one report — otherwise the fast-drive path, which has no
    // browser_handle_dialog to shift the queue, would wedge re-returning this
    // message forever. The next (armed) retry then runs.
    const carriedDialog = this.consumeRunCodeDialog(0);
    if (carriedDialog) {
      try {
        opts?.onOutcome?.({ ok: false, aborted: false, pageTornDown: false });
      } catch {
        /* outcome callback must never break runCode */
      }
      // Raw string (no formatMCPResponse) — matches the prior short-circuit and
      // avoids touching page-dependent framing before getPage() has run.
      return buildRunCodeQueuedDialogMessage(carriedDialog);
    }

    const page = await this.getPage();
    // Snapshot the dialog-queue depth so we can detect a dialog this block
    // triggers (and auto-dismisses) without the agent first arming `dialog(...)`.
    const pendingBefore = this._pendingDialogs.length;

    // Delegate to the shared run_code primitive (single source of truth, also
    // used by deterministic cache replay). It handles binding injection, the
    // fetch/page.request/wait guards, the abort race, and stripping any
    // dialog/download/filechooser listeners the agent's code added
    // (API-1W8 / API-1VZ). This method keeps only the live-agent concerns:
    // the pending-dialog short-circuit, `onOutcome`, and MCP-response framing.
    const outcome = await executeRunCode({
      page,
      code,
      bindings: opts?.bindings,
      signal: opts?.signal,
      logger: this.logger,
      autonomousCursor: this.autonomousCursor,
      ...(this.runCodeEgressPolicy ? { egressPolicy: this.runCodeEgressPolicy } : {}),
    });
    const { warning } = outcome;

    // Detect a native dialog the block triggered (and the session auto-dismissed,
    // cancelling the action) without the agent arming `dialog(...)` first. Only
    // meaningful on the success path — an abort/throw already takes precedence and
    // leaves any dialog for the start-guard backstop on the next call.
    const triggeredDialog =
      !outcome.aborted && !outcome.error ? this.consumeRunCodeDialog(pendingBefore) : null;

    // Report the structural outcome before formatting the response. Failures are
    // returned as a formatted `Error:` / `[TOOL_TIMEOUT]` result here rather than
    // thrown, so callers (fast-drive observability) can't infer success from the
    // return value. A triggered-but-unhandled dialog reverted the action, so the
    // block did NOT succeed. Guarded — a throwing callback must never break the
    // "always return a formatted response" contract.
    try {
      const aborted = outcome.aborted;
      const pageTornDown = hasStructuredPageTeardown(page);
      const ok = outcome.ok && !triggeredDialog && !aborted && !pageTornDown;
      opts?.onOutcome?.(
        ok
          ? { ok: true, aborted: false, pageTornDown: false }
          : { ok: false, aborted, pageTornDown }
      );
    } catch {
      /* outcome callback must never break runCode */
    }

    if (outcome.aborted) {
      // Prefix with the tool-timeout sentinel (matches TOOL_TIMEOUT_SENTINEL
      // in apps/api/src/ai/tools/mcp/browserTools.ts) so that in the rare case
      // this return wins the outer Promise.race, the agent's truncation /
      // recovery path still recognises it as a timeout. Keep the literal in
      // sync with browserTools.ts — cross-package import would invert the
      // dependency direction.
      const msg = `[TOOL_TIMEOUT] runCode aborted${warning ? `\n\n${warning}` : ''}`;
      return this.formatMCPResponse(msg, undefined, truncateForError(code));
    }
    if (outcome.error) {
      // Self-repair: if this was a click that failed on ambiguity or on a
      // hidden/0-size target, non-destructively reveal the real control and
      // append a "retry with <durable locator>" hint. Best-effort — a
      // non-click error (syntax, thrown assertion) is gated out cheaply, the
      // whole path is behind a prod kill-switch, and any failure here never
      // masks the original error.
      const repairHint = isFastDriveClickRepairEnabled()
        ? await diagnoseRunCodeClickFailure({
            errorMessage: outcome.error,
            code,
            getPage: () => this.getPage(),
            raceAgainstSignal: (stage, signal, fn) => this.raceAgainstSignal(stage, signal, fn),
            signal: opts?.signal,
            logger: this.logger,
          }).catch(() => null)
        : null;
      const suffix = [warning, repairHint].filter(Boolean).join('\n\n');
      return this.formatMCPResponse(
        `Error: ${outcome.error}${suffix ? `\n\n${suffix}` : ''}`,
        undefined,
        truncateForError(code)
      );
    }
    if (triggeredDialog) {
      const msg = buildRunCodeQueuedDialogMessage(triggeredDialog);
      return this.formatMCPResponse(warning ? `${msg}\n\n${warning}` : msg, undefined, code);
    }
    const body = serializeRunCodeResult(outcome.result);
    return this.formatMCPResponse(
      warning ? `${body}${body ? '\n\n' : ''}${warning}` : body,
      undefined,
      code
    );
  }

  async consoleMessages(opts?: {
    onlyErrors?: boolean;
    sinceMs?: number;
    includePreStep?: boolean;
    signal?: AbortSignal;
    timeoutMs?: number;
  }): Promise<string> {
    const { lines } = formatConsoleMessages(this._capturedConsoleMessages, opts);
    if (lines.length === 0) {
      return this.formatMCPResponse(emptyConsoleMessagesText(opts));
    }
    return this.formatMCPResponse(lines.join('\n'));
  }

  async waitForNetworkIdle(timeoutMs = TIMEOUTS.STABILIZE_INTERACTION): Promise<void> {
    // Stream-aware: delegates to the HTTP-settle signal rather than Playwright's
    // `networkidle`, which hangs on any open SSE/WebSocket. Name kept for the
    // `IBrowserClient` contract and existing callers.
    await this.waitForHttpSettle(timeoutMs);
  }

  getPendingNetworkInfo(): { pendingCount: number; sampleUrls: string[] } {
    return getPendingNetworkRequestInfo(this._capturedNetworkRequests.values());
  }

  // ── Fast-drive observation signals ───────────────────────────────────────

  getReflessYaml(): string {
    return this.lastReflessYaml;
  }

  async captureReflessYaml(opts?: BrowserClientOptions): Promise<string> {
    await this.stabilize('dom-only', opts?.signal);
    const raw = await this.captureRawSubstrate(opts?.signal, { refless: true });
    if (raw) this.lastReflessYaml = raw;
    return raw;
  }

  async getDomRevision(opts?: BrowserClientOptions): Promise<number> {
    return await readFastDriveDomRevision({
      getPage: () => this.getPage(),
      raceAgainstSignal: (stage, signal, fn) => this.raceAgainstSignal(stage, signal, fn),
      signal: opts?.signal,
    });
  }

  async getDomSignals(opts?: BrowserClientOptions): Promise<BrowserDomSignals> {
    return await readFastDriveDomSignals({
      getPage: () => this.getPage(),
      raceAgainstSignal: (stage, signal, fn) => this.raceAgainstSignal(stage, signal, fn),
      signal: opts?.signal,
    });
  }

  async scan(input: FastDriveScanInput, opts?: BrowserClientOptions): Promise<FastDriveScanResult> {
    return await runFastDriveScan({
      getPage: () => this.getPage(),
      raceAgainstSignal: (stage, signal, fn) => this.raceAgainstSignal(stage, signal, fn),
      signal: opts?.signal,
      input,
      stampingEnabled: isFastDriveScanStampingEnabled(),
      logger: this.logger,
    });
  }

  getOpenPageUrls(): string[] {
    return getFastDriveOpenPageUrls(this.page);
  }

  getOpenPageHandles(): Array<{ handle: number; url: string }> {
    try {
      const context = this.page?.context();
      if (!context) return [];
      return listOpenPageHandles(context.pages(), this._tabRegistry);
    } catch {
      return [];
    }
  }

  getConsoleErrorSummarySince(sinceMs: number): BrowserConsoleErrorSummary {
    return getFastDriveConsoleErrorSummarySince(this._capturedConsoleMessages, sinceMs);
  }

  getNetworkRequestCount(): number {
    return this._capturedNetworkRequests.size;
  }

  /**
   * Stream-aware HTTP-settle wait (replaces Playwright `networkidle` in
   * stabilization). Resolves when no non-stream HTTP request is awaiting
   * response headers, or after `maxWaitMs`. SSE/WebSocket connections never
   * block it — their response headers land immediately so they're non-pending,
   * and `eventsource`/`websocket` resource types are excluded outright. A
   * request stuck pending past `HTTP_SETTLE_MAX_AGE` is aged out. Best-effort:
   * the cap always wins.
   */
  private async waitForHttpSettle(maxWaitMs: number): Promise<void> {
    const page = this.page;
    if (!page || page.isClosed()) return;
    const deadline = Date.now() + maxWaitMs;
    const POLL_MS = 50;
    // Settled already? Return without paying a poll tick.
    while (this.countUnsettledRequests() > 0) {
      if (Date.now() >= deadline) {
        // Gave up early — leave a breadcrumb with what was still pending so a
        // "why did stabilization not wait?" question is answerable from a trace.
        const pending = this.getPendingNetworkInfo();
        this.logger.debug('[DirectPlaywright] HTTP-settle deadline reached, proceeding', {
          maxWaitMs,
          pendingCount: pending.pendingCount,
          sampleUrls: pending.sampleUrls,
        });
        return;
      }
      await new Promise<void>((r) => setTimeout(r, POLL_MS));
    }
  }

  /** Pending non-stream HTTP requests younger than the age guard. 0 ⇒ settled. */
  private countUnsettledRequests(): number {
    return countUnsettledRequests(this._capturedNetworkRequests.values(), Date.now(), {
      noisePatterns: NETWORK_NOISE_URL_PATTERNS,
      maxAgeMs: TIMEOUTS.HTTP_SETTLE_MAX_AGE,
    });
  }

  async networkRequests(_opts?: BrowserClientOptions): Promise<string> {
    if (this._capturedNetworkRequests.size === 0) {
      return this.formatMCPResponse('No network requests captured');
    }

    return this.formatMCPResponse(formatNetworkRequests(this._capturedNetworkRequests.values()));
  }

  // ================ INTERACTION ================

  async click(
    ref: string,
    elementDesc = 'element',
    opts?: {
      doubleClick?: boolean;
      button?: string;
      modifiers?: string[];
      signal?: AbortSignal;
      timeoutMs?: number;
    }
  ): Promise<ClickResult> {
    const dialogMsg = this.getPendingDialogMessage();
    if (dialogMsg) throw new Error(dialogMsg);

    const locator = await this.resolveRef(ref);
    const timeout = opts?.timeoutMs ?? TIMEOUTS.CLICK;

    this.logger.debug('[DirectPlaywright] Clicking via locator', {
      ref,
      elementDesc,
      doubleClick: opts?.doubleClick,
    });

    // Step 1: Scroll element into view
    await locator.scrollIntoViewIfNeeded({ timeout: 5000 }).catch((err) => {
      this.logger.debug('[DirectPlaywright] scrollIntoViewIfNeeded failed, continuing', {
        ref,
        error: err instanceof Error ? err.message : String(err),
      });
    });

    const page = await this.getPage();
    await this.cursorOverlay.moveToElement(page, locator);
    await this.highlightInteraction(locator, 'click');

    const clickOptions = buildLocatorClickOptions({
      button: opts?.button,
      modifiers: opts?.modifiers,
      timeout,
    });

    let fallbackUsed = false;

    // Capture element info BEFORE the click. We need a bounding box so we can
    // call elementFromPoint to describe what was clicked for agent feedback.
    // Capturing after the click is wrong: if the click triggers navigation,
    // locator.boundingBox() waits ~10s for an element that's gone, and even if
    // it returned, elementFromPoint would describe whatever the new page
    // renders at those coordinates. Pre-click is fast and accurate.
    let clickedElement: ClickedElementInfo | undefined;
    let preClickBox: { x: number; y: number; width: number; height: number } | null = null;
    try {
      preClickBox = (await locator.boundingBox()) ?? null;
      if (preClickBox) {
        const centerX = Math.round(preClickBox.x + preClickBox.width / 2);
        const centerY = Math.round(preClickBox.y + preClickBox.height / 2);
        clickedElement = (await this.captureElementAtPoint(centerX, centerY)) ?? undefined;
      }
    } catch (err) {
      this.logger.debug('[DirectPlaywright] Failed to capture clicked element info', {
        ref,
        error: err instanceof Error ? err.message : String(err),
      });
    }

    // Step 2: Try locator click, fallback to coordinate click on timeout
    try {
      await clickLocatorWithOptions(locator, clickOptions, { doubleClick: opts?.doubleClick });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (isPlaywrightTargetClosedError(err)) {
        throw toBrowserActionTargetClosedError(err, {
          operation: 'click',
          ref,
          elementDesc,
          lastKnownUrl: this.lastKnownUrl,
          pendingCrashType: this._pendingCrashType,
          pendingCrashUrl: this._pendingCrashUrl,
        });
      }
      const isTimeout = message.includes('Timeout') || message.includes('timeout');

      if (isTimeout && preClickBox) {
        // Fallback: Try coordinate-based click (bypasses pointer-events issues).
        // Reuse the pre-click bounding box so we don't wait again on a wedged element.
        const centerX = Math.round(preClickBox.x + preClickBox.width / 2);
        const centerY = Math.round(preClickBox.y + preClickBox.height / 2);

        this.logger.warn('[DirectPlaywright] Click timeout, falling back to coordinates', {
          ref,
          elementDesc,
          x: centerX,
          y: centerY,
          error: message,
        });

        const page = await this.getPage();
        await clickMouseWithDelay(page, centerX, centerY, { doubleClick: opts?.doubleClick });
        fallbackUsed = true;
      } else {
        throw err;
      }
    }

    // Wait for page to stabilize after click (may trigger navigation/AJAX)
    await this.stabilize('interaction');

    return { clickedElement, fallbackUsed };
  }

  async clickAtCoordinates(
    x: number,
    y: number,
    elementDesc: string,
    opts?: {
      signal?: AbortSignal;
      timeoutMs?: number;
      doubleClick?: boolean;
      modifiers?: string[];
    }
  ): Promise<{
    clickedElement?: {
      tagName: string;
      id?: string;
      className?: string;
      textContent?: string;
      role?: string;
      ariaLabel?: string;
    };
  }> {
    const dialogMsg = this.getPendingDialogMessage();
    if (dialogMsg) throw new Error(dialogMsg);

    const page = await this.getPage();

    this.logger.debug('[DirectPlaywright] Clicking at coordinates', { x, y, elementDesc });

    await this.cursorOverlay.moveTo(page, x, y);
    await this.highlightPoint(x, y, 'click');

    // Playwright mouse API options. Modifiers are handled separately via keyboard.
    await pressClickModifiers(page, opts?.modifiers);

    await clickMouseWithDelay(page, x, y, { doubleClick: opts?.doubleClick });

    await releaseClickModifiers(page, opts?.modifiers);

    // Capture info about the element at the clicked coordinates
    const clickedElement = await this.captureElementAtPoint(x, y);

    // Wait for page to stabilize after click (may trigger navigation/AJAX)
    await this.stabilize('interaction');

    return { clickedElement: clickedElement ?? undefined };
  }

  async moveToCoordinates(
    x: number,
    y: number,
    elementDesc: string,
    _opts?: BrowserClientOptions
  ): Promise<void> {
    const dialogMsg = this.getPendingDialogMessage();
    if (dialogMsg) throw new Error(dialogMsg);

    const page = await this.getPage();

    this.logger.debug('[DirectPlaywright] Moving to coordinates', { x, y, elementDesc });

    await this.cursorOverlay.moveTo(page, x, y);
    await page.mouse.move(x, y);
  }

  /** True when the element (or an ancestor) opts into native HTML5 DnD. */
  private async usesNativeHtml5Drag(locator: Locator): Promise<boolean> {
    return locator.evaluate((el) => el.closest('[draggable="true"]') !== null).catch(() => false);
  }

  async dragCoordinates(
    startX: number,
    startY: number,
    endX: number,
    endY: number,
    elementDesc: string,
    _opts?: BrowserClientOptions
  ): Promise<void> {
    const dialogMsg = this.getPendingDialogMessage();
    if (dialogMsg) throw new Error(dialogMsg);

    const page = await this.getPage();

    this.logger.debug('[DirectPlaywright] Dragging', { startX, startY, endX, endY, elementDesc });

    await this.cursorOverlay.moveTo(page, startX, startY);
    await this.highlightPoint(startX, startY, 'click');
    await this.highlightPoint(endX, endY, 'click');

    await performPointerDrag(
      page,
      { x: startX, y: startY },
      { x: endX, y: endY },
      { onStep: (x, y) => this.cursorOverlay.moveTo(page, x, y) }
    );

    // Wait for DOM to settle after drag
    await this.stabilize('dom-only');
  }

  async hover(ref: string, elementDesc = 'element', opts?: BrowserClientOptions): Promise<void> {
    const dialogMsg = this.getPendingDialogMessage();
    if (dialogMsg) throw new Error(dialogMsg);

    const locator = await this.resolveRef(ref);

    this.logger.debug('[DirectPlaywright] Hovering', { ref, elementDesc });

    // Scroll into view first
    await locator.scrollIntoViewIfNeeded({ timeout: 5000 }).catch(() => {});

    // Get bounding box for coordinate-based hover (bypasses pointer-events issues)
    const box = await locator.boundingBox();

    if (box) {
      // Round coordinates for consistency (Playwright mouse API accepts floats but pixels are integers)
      const centerX = Math.round(box.x + box.width / 2);
      const centerY = Math.round(box.y + box.height / 2);

      const page = await this.getPage();
      await this.cursorOverlay.moveToElement(page, locator);
      await this.highlightInteraction(locator, 'hover');

      await page.mouse.move(centerX, centerY);

      this.logger.debug('[DirectPlaywright] Coordinate hover executed', {
        ref,
        elementDesc,
        x: centerX,
        y: centerY,
      });
    } else {
      // Fallback: element not visible, try locator-based hover
      this.logger.warn('[DirectPlaywright] No bounding box, falling back to locator hover', {
        ref,
      });

      await this.highlightInteraction(locator, 'hover');
      await locator.hover({ timeout: opts?.timeoutMs ?? TIMEOUTS.CLICK });
    }

    // Wait for DOM to settle after hover (may trigger tooltips/menus)
    await this.stabilize('dom-only');
  }

  async drag(
    startRef: string,
    startElement: string,
    endRef: string,
    endElement: string,
    opts?: BrowserClientOptions
  ): Promise<void> {
    const dialogMsg = this.getPendingDialogMessage();
    if (dialogMsg) throw new Error(dialogMsg);

    const startLocator = await this.resolveRef(startRef);
    const endLocator = await this.resolveRef(endRef);

    this.logger.debug('[DirectPlaywright] Dragging element', {
      startRef,
      startElement,
      endRef,
      endElement,
    });

    const page = await this.getPage();
    await this.cursorOverlay.moveToElement(page, startLocator);
    await this.highlightInteraction(startLocator, 'drag-start');
    await this.highlightInteraction(endLocator, 'drag-end');

    if (await this.usesNativeHtml5Drag(startLocator)) {
      // Native HTML5 DnD needs the dragstart/dragover/drop event sequence,
      // which only locator.dragTo synthesizes.
      await startLocator.dragTo(endLocator, { timeout: opts?.timeoutMs ?? TIMEOUTS.CLICK * 2 });
    } else {
      // Pointer-sensor libraries (dnd-kit & co) never see HTML5 drag events —
      // drive a paced pointer gesture between the element centers instead.
      // Scroll both endpoints into view BEFORE measuring: scrolling the end
      // into view can move the start, so measure only after both settle.
      await startLocator.scrollIntoViewIfNeeded({ timeout: 5000 }).catch(() => {});
      await endLocator.scrollIntoViewIfNeeded({ timeout: 5000 }).catch(() => {});
      const startBox = await startLocator.boundingBox();
      const endBox = await endLocator.boundingBox();
      if (!startBox || !endBox) {
        throw new Error(
          `Cannot drag "${startElement}" to "${endElement}": ` +
            `${!startBox ? 'start' : 'end'} element has no visible bounding box`
        );
      }
      await performPointerDrag(
        page,
        { x: startBox.x + startBox.width / 2, y: startBox.y + startBox.height / 2 },
        { x: endBox.x + endBox.width / 2, y: endBox.y + endBox.height / 2 },
        { onStep: (x, y) => this.cursorOverlay.moveTo(page, x, y) }
      );
    }

    // Wait for DOM to settle after drag
    await this.stabilize('dom-only');
  }

  async type(
    ref: string,
    text: string,
    elementDesc = 'input',
    submit?: boolean,
    opts?: { delay?: number; signal?: AbortSignal; timeoutMs?: number }
  ): Promise<void> {
    const dialogMsg = this.getPendingDialogMessage();
    if (dialogMsg) throw new Error(dialogMsg);

    const locator = await this.resolveRef(ref);
    await assertFillTargetEditable(locator, ref, elementDesc);

    const timeout = opts?.timeoutMs ?? TIMEOUTS.TYPE;
    const textToEnter = await resolveTemporalInputFillValue(
      locator,
      text,
      { ref, action: 'typing' },
      this.logger
    );

    this.logger.debug('[DirectPlaywright] Typing', {
      ref,
      elementDesc,
      inputType: textToEnter.inputType,
      submit,
      textLength: text.length,
    });

    // Step 1: Scroll element into view (like hover does)
    await locator.scrollIntoViewIfNeeded({ timeout: 5000 }).catch((err) => {
      this.logger.debug('[DirectPlaywright] scrollIntoViewIfNeeded failed, continuing', {
        ref,
        error: err instanceof Error ? err.message : String(err),
      });
    });

    // Step 2: Focus the element to ensure it receives keyboard input
    // Using focus() instead of click() to avoid triggering onClick handlers or side effects
    try {
      await locator.focus({ timeout: Math.min(timeout, 5000) });
    } catch (err) {
      this.logger.debug('[DirectPlaywright] Focus failed, continuing with type', {
        ref,
        error: err instanceof Error ? err.message : String(err),
      });
    }

    const page = await this.getPage();
    await this.cursorOverlay.moveToElement(page, locator);
    await this.highlightInteraction(locator, 'type');

    // Step 3: Clear and type with retry on timeout
    const attemptType = async () => {
      if (textToEnter.shouldFillDirectly) {
        await locator.fill(textToEnter.value, { timeout });
        return;
      }

      await locator.clear();
      await locator.pressSequentially(textToEnter.value, {
        delay: opts?.delay ?? 0,
        timeout,
      });
    };

    try {
      await attemptType();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      const isTimeout = message.includes('Timeout') || message.includes('timeout');

      if (isTimeout) {
        this.logger.warn('[DirectPlaywright] Type timeout, retrying with fill() fallback', {
          ref,
          elementDesc,
          textLength: text.length,
          error: message,
        });

        // Fallback: Use fill() which is more reliable for simple text input
        await locator.fill(textToEnter.value, { timeout });
      } else {
        throw err;
      }
    }

    if (submit) {
      // `locator.press()` re-resolves the original aria-ref. Inputs can
      // re-render or collapse after typing, so submit through the focused
      // element instead of waiting on a stale ref.
      await page.keyboard.press('Enter');
      // Wait for page to stabilize after form submission
      await this.stabilize('interaction');
    }
  }

  async pressKey(key: string, _opts?: BrowserClientOptions): Promise<void> {
    const dialogMsg = this.getPendingDialogMessage();
    if (dialogMsg) throw new Error(dialogMsg);

    const page = await this.getPage();

    this.logger.debug('[DirectPlaywright] Pressing key', { key });

    await page.keyboard.press(key);
  }

  async fillForm(
    fields: Array<{ ref: string; element?: string; value: string; type?: string }>,
    opts?: BrowserClientOptions
  ): Promise<void> {
    const dialogMsg = this.getPendingDialogMessage();
    if (dialogMsg) throw new Error(dialogMsg);

    this.logger.debug('[DirectPlaywright] Filling form', { fieldCount: fields.length });

    for (const field of fields) {
      const locator = await this.resolveRef(field.ref);
      const fieldType = field.type ?? 'textbox';
      const timeout = opts?.timeoutMs ?? TIMEOUTS.FILL_FORM;
      const valueToFill = await resolveTemporalInputFillValue(
        locator,
        field.value,
        { ref: field.ref, action: 'filling' },
        this.logger
      );

      // Scroll field into view before interacting
      await locator.scrollIntoViewIfNeeded({ timeout: 5000 }).catch((err) => {
        this.logger.debug('[DirectPlaywright] scrollIntoViewIfNeeded failed, continuing', {
          ref: field.ref,
          error: err instanceof Error ? err.message : String(err),
        });
      });

      const page = await this.getPage();
      await this.cursorOverlay.moveToElement(page, locator);

      switch (fieldType) {
        case 'checkbox': {
          const isChecked = await locator.isChecked();
          const shouldBeChecked = field.value === 'true';
          if (shouldBeChecked !== isChecked) {
            await this.highlightInteraction(locator, 'click');
            await locator.click(buildLocatorClickOptions({ timeout }));
          }
          break;
        }
        case 'radio':
          await this.highlightInteraction(locator, 'click');
          await locator.check({ timeout });
          break;
        case 'combobox':
          await this.highlightInteraction(locator, 'type');
          await locator.selectOption(valueToFill.value, { timeout });
          break;
        case 'slider':
          // Sliders need value set via fill - uses input's value attribute
          await assertFillTargetEditable(locator, field.ref, field.element);
          await this.highlightInteraction(locator, 'type');
          await locator.fill(valueToFill.value, { timeout });
          break;
        default: {
          await assertFillTargetEditable(locator, field.ref, field.element);
          // textbox - focus before filling for better reliability
          try {
            await locator.focus({ timeout: Math.min(timeout, 5000) });
          } catch {
            // Soft failure - continue with fill
          }
          await this.highlightInteraction(locator, 'type');
          await locator.fill(valueToFill.value, { timeout });
        }
      }
    }

    // Wait for DOM to settle after form fill
    await this.stabilize('dom-only');
  }

  async selectOption(
    ref: string,
    value: string,
    elementDesc = 'select',
    opts?: BrowserClientOptions
  ): Promise<void> {
    const dialogMsg = this.getPendingDialogMessage();
    if (dialogMsg) throw new Error(dialogMsg);

    const locator = await this.resolveRef(ref);

    this.logger.debug('[DirectPlaywright] Selecting option', { ref, value, elementDesc });

    // Scroll element into view
    await locator.scrollIntoViewIfNeeded({ timeout: 5000 }).catch((err) => {
      this.logger.debug('[DirectPlaywright] scrollIntoViewIfNeeded failed, continuing', {
        ref,
        error: err instanceof Error ? err.message : String(err),
      });
    });

    const page = await this.getPage();
    await this.cursorOverlay.moveToElement(page, locator);
    await this.highlightInteraction(locator, 'type');

    await locator.selectOption(value, { timeout: opts?.timeoutMs ?? TIMEOUTS.CLICK });

    // Wait for DOM to settle after selection (may trigger onChange handlers)
    await this.stabilize('dom-only');
  }

  async fileUpload(paths: string[], opts?: FileUploadOptions): Promise<void> {
    if (!paths || paths.length === 0) {
      throw new Error('fileUpload requires at least one file path');
    }

    const dialogMsg = this.getPendingDialogMessage();
    if (dialogMsg) throw new Error(dialogMsg);

    const page = await this.getPage();
    const timeout = opts?.timeoutMs ?? TIMEOUTS.TYPE;

    this.logger.debug('[DirectPlaywright] Uploading files', {
      paths,
      ref: opts?.ref,
      hasPreparedFileChooser: !!opts?.preparedFileChooser,
      timeout,
    });

    // Mode 1: Pre-prepared filechooser (cache replay hoisting).
    // The replay loop set up the listener before the preceding click, so the
    // filechooser event has already been captured.
    if (opts?.preparedFileChooser) {
      try {
        const fileChooser = await opts.preparedFileChooser;
        await fileChooser.setFiles(paths);
        await this.stabilize('interaction');
        return;
      } catch (err) {
        this.logger.debug(
          '[DirectPlaywright] preparedFileChooser failed, falling back to setInputFiles',
          {
            error: err instanceof Error ? err.message : String(err),
          }
        );
        // Fall through to setInputFiles
      }
    }

    // Resolve ref once for both Mode 2 and Mode 3
    const refLocator = opts?.ref ? await this.resolveRef(opts.ref) : undefined;

    // Mode 2: Ref provided — try native filechooser approach first.
    // Click the ref element while listening for the filechooser event atomically
    // via Promise.all so the event can't be lost between separate tool calls.
    if (refLocator) {
      await this.cursorOverlay.moveToElement(page, refLocator);
      try {
        const [fileChooser] = await Promise.all([
          page.waitForEvent('filechooser', { timeout: Math.min(timeout, 5000) }),
          refLocator.click(buildLocatorClickOptions({ timeout: Math.min(timeout, 5000) })),
        ]);
        await fileChooser.setFiles(paths);
        await this.stabilize('interaction');
        return;
      } catch (err) {
        this.logger.debug('[DirectPlaywright] filechooser approach failed, trying setInputFiles', {
          ref: opts?.ref,
          error: err instanceof Error ? err.message : String(err),
        });
        // Fall through to setInputFiles fallback
      }
    }

    // Mode 3: Fallback — find the nearest input[type="file"] and set files directly.
    // Works when: no ref, ref didn't trigger a filechooser, or file input is accessible.
    let fileInput;
    if (refLocator) {
      // Check if the ref itself IS a file input
      const isFileInput = await refLocator
        .evaluate((el) => el.tagName === 'INPUT' && (el as HTMLInputElement).type === 'file')
        .catch(() => false);
      if (isFileInput) {
        fileInput = refLocator;
      } else {
        // Look for a file input nested within the ref element
        const nested = refLocator.locator('input[type="file"]');
        fileInput =
          (await nested.count()) > 0 ? nested.first() : page.locator('input[type="file"]').first();
      }
    } else {
      fileInput = page.locator('input[type="file"]').first();
    }

    if ((await fileInput.count()) === 0) {
      throw new Error(
        'No input[type="file"] found on the page. Provide a ref to the upload button or ensure a file input exists.'
      );
    }

    await fileInput.setInputFiles(paths, { timeout });
    await this.stabilize('interaction');
  }

  async scroll(
    direction: 'up' | 'down' | 'left' | 'right',
    amount: number = 3,
    withinRef?: string,
    toRef?: string,
    opts?: BrowserClientOptions
  ): Promise<string | ScrollResult> {
    const dialogMsg = this.getPendingDialogMessage();
    if (dialogMsg) return dialogMsg;

    const page = await this.getPage();
    const scrollParams = { direction, amount, withinRef, toRef };

    // Helper: get scroll position from either a container element or the window
    const getScrollPos = async (elementHandle?: ReturnType<typeof page.locator>) => {
      if (elementHandle) {
        return elementHandle.evaluate((el) => ({ x: el.scrollLeft, y: el.scrollTop }));
      }
      return page.evaluate(() => ({ x: window.scrollX, y: window.scrollY }));
    };

    // Helper: capture position AND geometry in one round-trip so the hot scroll
    // path does one page.evaluate, not two.
    type ScrollState = {
      x: number;
      y: number;
      geometry?: {
        scrollHeight: number;
        clientHeight: number;
        scrollWidth: number;
        clientWidth: number;
        maxScrollY: number;
        maxScrollX: number;
      };
    };
    const getScrollState = async (
      elementHandle?: ReturnType<typeof page.locator>
    ): Promise<ScrollState> => {
      try {
        if (elementHandle) {
          return await elementHandle.evaluate((el) => ({
            x: el.scrollLeft,
            y: el.scrollTop,
            geometry: {
              scrollHeight: el.scrollHeight,
              clientHeight: el.clientHeight,
              scrollWidth: el.scrollWidth,
              clientWidth: el.clientWidth,
              maxScrollY: Math.max(0, el.scrollHeight - el.clientHeight),
              maxScrollX: Math.max(0, el.scrollWidth - el.clientWidth),
            },
          }));
        }
        return await page.evaluate(() => {
          const de = document.scrollingElement || document.documentElement;
          return {
            x: window.scrollX,
            y: window.scrollY,
            geometry: {
              scrollHeight: de.scrollHeight,
              clientHeight: de.clientHeight,
              scrollWidth: de.scrollWidth,
              clientWidth: de.clientWidth,
              maxScrollY: Math.max(0, de.scrollHeight - window.innerHeight),
              maxScrollX: Math.max(0, de.scrollWidth - window.innerWidth),
            },
          };
        });
      } catch {
        // Position-only fallback (no geometry) — page may be navigating.
        const fallback = await page
          .evaluate(() => ({ x: window.scrollX, y: window.scrollY }))
          .catch(() => ({ x: 0, y: 0 }));
        return fallback;
      }
    };

    // Resolve the scroll target element (if scrolling within a ref)
    let scrollTarget: ReturnType<typeof page.locator> | undefined;
    if (withinRef) {
      scrollTarget = await this.resolveRef(withinRef);
    }

    let beforePos: { x: number; y: number };
    try {
      beforePos = await getScrollPos(scrollTarget);
    } catch {
      beforePos = await page.evaluate(() => ({ x: window.scrollX, y: window.scrollY }));
    }

    this.logger.info('[DirectPlaywright] scroll() entry', {
      ...scrollParams,
      beforeScrollX: beforePos.x,
      beforeScrollY: beforePos.y,
    });

    try {
      // Mode 1: Scroll TO an element (center it in viewport)
      if (toRef) {
        const locator = await this.resolveRef(toRef);
        await this.cursorOverlay.moveToElement(page, locator);

        const box = await locator.boundingBox();

        if (box) {
          const viewportSize = this.getViewportSize();
          const elementCenterY = box.y + box.height / 2;
          const viewportCenterY = viewportSize.height / 2;
          const scrollY = elementCenterY - viewportCenterY;

          const elementCenterX = box.x + box.width / 2;
          const viewportCenterX = viewportSize.width / 2;
          // Also scroll horizontally if element is outside the viewport width
          const boxRight = box.x + box.width;
          const needsHorizontalScroll =
            boxRight < 0 ||
            box.x > viewportSize.width ||
            elementCenterX < 0 ||
            elementCenterX > viewportSize.width;
          const scrollX = needsHorizontalScroll ? elementCenterX - viewportCenterX : 0;

          this.logger.debug('[DirectPlaywright] Scrolling to center element', {
            toRef,
            elementCenterY,
            viewportCenterY,
            scrollY,
            elementCenterX,
            scrollX,
            needsHorizontalScroll,
          });

          await page.evaluate(`window.scrollBy(${scrollX}, ${scrollY})`);

          // If the element is inside a scrollable container (e.g., AG Grid),
          // also scroll the container horizontally to bring it into view
          if (needsHorizontalScroll) {
            // Best-effort: a page-close race during scroll can throw
            // out of resolveRefs (the legacy `[aria-ref="..."]` CSS
            // path was a silent no-op). Swallow here — horizontal
            // scroll is a hint, not a load-bearing step.
            await resolveRefs(page, [toRef], (el) => {
              let parent = el.parentElement;
              for (let i = 0; i < 10 && parent; i++) {
                if (parent.scrollWidth > parent.clientWidth) {
                  const elRect = el.getBoundingClientRect();
                  const parentRect = parent.getBoundingClientRect();
                  const targetScrollLeft =
                    parent.scrollLeft +
                    (elRect.left - parentRect.left) -
                    parentRect.width / 2 +
                    elRect.width / 2;
                  parent.scrollLeft = Math.max(0, targetScrollLeft);
                  break;
                }
                parent = parent.parentElement;
              }
              return null;
            }).catch((err) => {
              this.logger.debug('[DirectPlaywright] horizontal-scroll lookup failed', {
                toRef,
                error: err instanceof Error ? err.message : String(err),
              });
            });
          }
        } else {
          this.logger.warn('[DirectPlaywright] No bounding box, using scrollIntoViewIfNeeded', {
            toRef,
          });
          await locator.scrollIntoViewIfNeeded({ timeout: 5000 }).catch(() => {});
        }
      } else {
        // Mode 2: Scroll by amount (within element or page)
        const clampedAmount = Math.max(1, Math.min(10, amount));
        const scrollPixels = clampedAmount * 120;

        this.logger.debug('[DirectPlaywright] Scrolling by amount', {
          direction,
          amount: clampedAmount,
          scrollPixels,
          withinRef,
        });

        if (scrollTarget) {
          await this.cursorOverlay.moveToElement(page, scrollTarget);
          const box = await scrollTarget.boundingBox();

          if (box) {
            const centerX = box.x + box.width / 2;
            const centerY = box.y + box.height / 2;
            await page.mouse.move(centerX, centerY);
          }

          let deltaX = 0;
          let deltaY = 0;

          switch (direction) {
            case 'up':
              deltaY = -scrollPixels;
              break;
            case 'down':
              deltaY = scrollPixels;
              break;
            case 'left':
              deltaX = -scrollPixels;
              break;
            case 'right':
              deltaX = scrollPixels;
              break;
          }

          await page.mouse.wheel(deltaX, deltaY);
        } else {
          // Move cursor to viewport center for page-level scroll
          const vs = this.getViewportSize();
          await this.cursorOverlay.moveTo(
            page,
            Math.round(vs.width / 2),
            Math.round(vs.height / 2)
          );

          let scrollScript: string;

          switch (direction) {
            case 'up':
              scrollScript = `window.scrollBy(0, -${scrollPixels})`;
              break;
            case 'down':
              scrollScript = `window.scrollBy(0, ${scrollPixels})`;
              break;
            case 'left':
              scrollScript = `window.scrollBy(-${scrollPixels}, 0)`;
              break;
            case 'right':
              scrollScript = `window.scrollBy(${scrollPixels}, 0)`;
              break;
          }

          await page.evaluate(scrollScript);
        }
      }
    } catch (err) {
      this.logger.error('[DirectPlaywright] scroll() failed', {
        ...scrollParams,
        error: err instanceof Error ? err.message : String(err),
      });
      throw err;
    }

    // Measure after position AND geometry in a single evaluate() — halves the
    // CDP round-trips on the scroll hot path. Best-effort; measurement failure
    // falls back to position-only.
    const afterState = await getScrollState(scrollTarget);
    const afterPos = { x: afterState.x, y: afterState.y };
    const geometry = afterState.geometry;

    const deltaX = afterPos.x - beforePos.x;
    const deltaY = afterPos.y - beforePos.y;
    const scrollOccurred = deltaX !== 0 || deltaY !== 0;

    const maxY = geometry?.maxScrollY ?? 0;
    const maxX = geometry?.maxScrollX ?? 0;
    const scrollInfo: ScrollInfo = {
      moved: scrollOccurred,
      direction,
      before: beforePos,
      after: afterPos,
      geometry,
      atTop: afterPos.y <= 0,
      atBottom: maxY > 0 ? afterPos.y >= maxY - 1 : true,
      atLeft: afterPos.x <= 0,
      atRight: maxX > 0 ? afterPos.x >= maxX - 1 : true,
      withinRef,
      toRef,
    };

    if (!scrollOccurred) {
      this.logger.warn('[DirectPlaywright] scroll() no movement detected', {
        ...scrollParams,
        beforeScrollX: beforePos.x,
        beforeScrollY: beforePos.y,
        afterScrollX: afterPos.x,
        afterScrollY: afterPos.y,
        geometry,
      });
    } else {
      this.logger.info('[DirectPlaywright] scroll() exit', {
        ...scrollParams,
        afterScrollX: afterPos.x,
        afterScrollY: afterPos.y,
        deltaX,
        deltaY,
        scrollOccurred,
      });
    }

    // No movement: skip the expensive snapshot + screenshot — the DOM hasn't
    // changed, so echoing the same aria tree just burns tokens and lets the
    // agent mistake it for new evidence. Return a compact signal the executor
    // will turn into a recovery hint.
    if (!scrollOccurred) {
      return { text: '', scrollInfo };
    }

    // Wait for DOM to settle after scroll
    await this.stabilize('dom-only');

    // Return a fresh snapshot with screenshot, carrying scrollInfo so the
    // executor can prepend position/delta context to the text field.
    const snap = await this.snapshot({
      signal: opts?.signal,
      timeoutMs: opts?.timeoutMs,
      includeScreenshot: true,
    });
    if (typeof snap === 'string') {
      return { text: snap, scrollInfo };
    }
    return { ...snap, scrollInfo };
  }

  async scrollAtCoordinates(
    x: number,
    y: number,
    direction: 'up' | 'down' | 'left' | 'right',
    amount: number = 3,
    opts?: BrowserClientOptions
  ): Promise<string | ScrollResult> {
    const dialogMsg = this.getPendingDialogMessage();
    if (dialogMsg) return dialogMsg;

    const page = await this.getPage();
    const scrollParams = { x, y, direction, amount };

    const beforePos = await page.evaluate(() => ({ x: window.scrollX, y: window.scrollY }));

    const clampedAmount = Math.max(1, Math.min(10, amount));
    const scrollPixels = clampedAmount * 120;

    let deltaX = 0;
    let deltaY = 0;
    switch (direction) {
      case 'up':
        deltaY = -scrollPixels;
        break;
      case 'down':
        deltaY = scrollPixels;
        break;
      case 'left':
        deltaX = -scrollPixels;
        break;
      case 'right':
        deltaX = scrollPixels;
        break;
    }

    this.logger.info('[DirectPlaywright] scrollAtCoordinates() entry', {
      ...scrollParams,
      clampedAmount,
      deltaX,
      deltaY,
      beforeScrollX: beforePos.x,
      beforeScrollY: beforePos.y,
    });

    try {
      // Move mouse to position and use wheel scroll (mimics real user)
      await this.cursorOverlay.moveTo(page, x, y);
      await page.mouse.move(x, y);
      await page.mouse.wheel(deltaX, deltaY);
    } catch (err) {
      this.logger.error('[DirectPlaywright] scrollAtCoordinates() failed', {
        ...scrollParams,
        error: err instanceof Error ? err.message : String(err),
      });
      throw err;
    }

    // Single-evaluate capture of position + geometry — same optimization as
    // scroll(), halves CDP round-trips. Falls back to position-only on error.
    const afterState = await page
      .evaluate(() => {
        const de = document.scrollingElement || document.documentElement;
        return {
          x: window.scrollX,
          y: window.scrollY,
          geometry: {
            scrollHeight: de.scrollHeight,
            clientHeight: de.clientHeight,
            scrollWidth: de.scrollWidth,
            clientWidth: de.clientWidth,
            maxScrollY: Math.max(0, de.scrollHeight - window.innerHeight),
            maxScrollX: Math.max(0, de.scrollWidth - window.innerWidth),
          },
        };
      })
      .catch(() => ({ x: beforePos.x, y: beforePos.y, geometry: undefined }));
    const afterPos = { x: afterState.x, y: afterState.y };
    const geometry = afterState.geometry;
    const scrollDeltaX = afterPos.x - beforePos.x;
    const scrollDeltaY = afterPos.y - beforePos.y;
    const scrollOccurred = scrollDeltaX !== 0 || scrollDeltaY !== 0;

    const maxY = geometry?.maxScrollY ?? 0;
    const maxX = geometry?.maxScrollX ?? 0;
    const scrollInfo: ScrollInfo = {
      moved: scrollOccurred,
      direction,
      before: beforePos,
      after: afterPos,
      geometry,
      atTop: afterPos.y <= 0,
      atBottom: maxY > 0 ? afterPos.y >= maxY - 1 : true,
      atLeft: afterPos.x <= 0,
      atRight: maxX > 0 ? afterPos.x >= maxX - 1 : true,
    };

    if (!scrollOccurred) {
      this.logger.warn('[DirectPlaywright] scrollAtCoordinates() no movement detected', {
        ...scrollParams,
        beforeScrollX: beforePos.x,
        beforeScrollY: beforePos.y,
        afterScrollX: afterPos.x,
        afterScrollY: afterPos.y,
        geometry,
      });
    } else {
      this.logger.info('[DirectPlaywright] scrollAtCoordinates() exit', {
        ...scrollParams,
        afterScrollX: afterPos.x,
        afterScrollY: afterPos.y,
        deltaX: scrollDeltaX,
        deltaY: scrollDeltaY,
        scrollOccurred,
      });
    }

    if (!scrollOccurred) {
      return { text: '', scrollInfo };
    }

    await this.stabilize('dom-only');

    const snap = await this.snapshot({
      signal: opts?.signal,
      timeoutMs: opts?.timeoutMs,
      includeScreenshot: true,
    });
    if (typeof snap === 'string') {
      return { text: snap, scrollInfo };
    }
    return { ...snap, scrollInfo };
  }

  // ================ DIALOGS ================

  /**
   * Single mutation point for `_nextDialogAction` so every set/clear is logged
   * with its source. Lets us trace why a pre-arm vanished (or never appeared)
   * for any specific run by grepping `[Dialog] _nextDialogAction set`.
   */
  private setPendingArm(
    value: { action: 'accept' | 'dismiss'; promptText?: string } | null,
    source: string
  ): void {
    this.logger.debug('[Dialog] _nextDialogAction set', {
      source,
      action: value?.action ?? null,
      prev: this._nextDialogAction?.action ?? null,
    });
    this._nextDialogAction = value;
  }

  async handleDialog(
    action: 'accept' | 'dismiss',
    promptText?: string,
    _opts?: BrowserClientOptions
  ): Promise<void> {
    this.logger.debug('[DirectPlaywright] Handling dialog', {
      action,
      hasPromptText: !!promptText,
    });

    // Dialogs are auto-dismissed on arrival, so we clear the queue entry
    // and set _nextDialogAction so the global listener uses this action on re-click.
    const pending = this._pendingDialogs.shift();

    // Set the action for the next dialog (the global listener will use it)
    this.setPendingArm({ action, promptText }, 'handleDialog');

    if (pending) {
      // Had a pending dialog — agent is responding to it.
      // 'dismiss' is already done (auto-dismissed), 'accept' will apply on re-click.
      return;
    }

    // No pending dialog — pre-registering for the next dialog that appears.
  }

  setNextDialogAction(action: 'accept' | 'dismiss', promptText?: string): void {
    this.setPendingArm({ action, promptText }, 'setNextDialogAction');
  }

  // ================ WAITING ================

  async waitFor(opts?: {
    timeSec?: number;
    text?: string;
    textGone?: string;
    selector?: string;
    state?: string;
    timeout?: number;
    signal?: AbortSignal;
    timeoutMs?: number;
  }): Promise<void> {
    const timeout = opts?.timeout ?? opts?.timeoutMs ?? TIMEOUTS.WAIT_FOR;

    const attemptWait = async () => {
      const page = await this.getPage();
      if (opts?.timeSec) {
        this.logger.debug('[DirectPlaywright] Waiting for time', { seconds: opts.timeSec });
        await page.waitForTimeout(opts.timeSec * 1000);
        return;
      }
      if (opts?.text) {
        this.logger.debug('[DirectPlaywright] Waiting for text', { text: opts.text });
        await page.getByText(opts.text).first().waitFor({ state: 'visible', timeout });
        return;
      }
      if (opts?.textGone) {
        this.logger.debug('[DirectPlaywright] Waiting for text to disappear', {
          text: opts.textGone,
        });
        await page.getByText(opts.textGone).first().waitFor({ state: 'hidden', timeout });
        return;
      }
      if (opts?.selector) {
        this.logger.debug('[DirectPlaywright] Waiting for selector', {
          selector: opts.selector,
          state: opts.state,
        });
        await page.locator(opts.selector).waitFor({
          state: (opts.state as 'visible' | 'hidden' | 'attached' | 'detached') ?? 'visible',
          timeout,
        });
      }
    };

    try {
      await attemptWait();
    } catch (error) {
      if (!this.isRecoverableClosureError(error)) {
        throw error;
      }

      this.logger.warn(
        '[DirectPlaywright] Wait failed due to closed target; restoring and retrying once',
        {
          error: error instanceof Error ? error.message : String(error),
        }
      );

      this.page = null;
      await this.restoreBrowser('wait_closed_target');
      await attemptWait();
    }
  }

  // ================ ASSERTIONS ================

  /**
   * Register product-specific iframe selectors to search during `assert()`
   * in addition to the default generic list. Apps with embedded frames
   * (e.g. a customer-owned iframe that hosts the app chrome) set this once
   * so the shared package stays app-agnostic.
   */
  setAssertionFrameHints(hints: string[]): void {
    this.assertionFrameHints = [...hints];
  }

  getAssertionFrameHints(): string[] {
    return [...this.assertionFrameHints];
  }

  async assert(
    type: AssertionType,
    condition: string,
    expected?: unknown,
    opts?: BrowserClientOptions & {
      strict?: boolean;
      severity?: AssertionSeverity;
      inlineAssertionId?: string;
      label?: string;
      frameHints?: string[];
      downloadSinceMs?: number;
    }
  ): Promise<{
    passed: boolean;
    evaluationOutcome: AssertionEvaluationOutcome;
    reason?: string;
  }> {
    if (type === 'download_present' || type === 'download_content') {
      return this.assertDownload(type, condition, expected, opts);
    }
    const page = await this.getPage();
    const timeoutMs = opts?.timeoutMs ?? TIMEOUTS.WAIT_FOR;
    const result = await evaluateAssertion(page, { type, condition, expected }, timeoutMs, {
      logger: this.logger,
      frameHints: opts?.frameHints ?? this.assertionFrameHints,
      getCapturedToasts: () => this._lastCapturedToasts,
      signal: opts?.signal,
    });
    this.logger.debug('[DirectPlaywright] Assert evaluated', {
      type,
      condition,
      passed: result.passed,
      reason: result.reason,
    });
    return result;
  }

  private async assertDownload(
    type: Extract<AssertionType, 'download_present' | 'download_content'>,
    condition: string,
    expected: unknown,
    opts?: BrowserClientOptions & { downloadSinceMs?: number }
  ): Promise<{
    passed: boolean;
    evaluationOutcome: AssertionEvaluationOutcome;
    reason?: string;
  }> {
    const timeoutMs = resolveDownloadAssertionTimeout(opts?.timeoutMs);
    const pattern = condition || '*';
    try {
      const first = await this.downloads.waitForCompleted({
        sinceMs: opts?.downloadSinceMs,
        pattern,
        timeoutMs,
        signal: opts?.signal,
      });
      if (type === 'download_present') {
        return { passed: true, evaluationOutcome: 'passed' };
      }

      const eligible = this.downloads.downloads.filter(
        (download) =>
          download.completed &&
          Date.parse(download.timestamp) >= (opts?.downloadSinceMs ?? 0) &&
          filenameMatches(download.suggestedFilename, pattern)
      );
      if (eligible.length !== 1) {
        return {
          passed: false,
          evaluationOutcome: 'authored_error',
          reason: `Download content assertion is ambiguous: ${eligible.length} completed downloads match "${pattern}". Use a filename pattern that selects exactly one file.`,
        };
      }
      const contents = await this.downloads.read(first.id);
      if (contents.textContent === null) {
        return {
          passed: false,
          evaluationOutcome: 'authored_error',
          reason: `Download "${contents.filename}" is binary and cannot be content-asserted.`,
        };
      }
      const expectation = asDownloadExpectation(expected);
      if (!expectation.contains && !expectation.pattern) {
        return {
          passed: false,
          evaluationOutcome: 'authored_error',
          reason: 'download_content requires expected.contains or expected.pattern.',
        };
      }
      if (expectation.contains && !contents.textContent.includes(expectation.contains)) {
        return {
          passed: false,
          evaluationOutcome: 'refuted',
          reason: `Download "${contents.filename}" does not contain the expected text.`,
        };
      }
      if (expectation.pattern) {
        let regex: RegExp;
        try {
          regex = new RegExp(expectation.pattern);
        } catch (err) {
          return {
            passed: false,
            evaluationOutcome: 'authored_error',
            reason: `Invalid download content pattern: ${err instanceof Error ? err.message : String(err)}`,
          };
        }
        if (!regex.test(contents.textContent)) {
          return {
            passed: false,
            evaluationOutcome: 'refuted',
            reason: `Download "${contents.filename}" does not match the expected pattern.`,
          };
        }
      }
      return { passed: true, evaluationOutcome: 'passed' };
    } catch (err) {
      const pageTornDown = this.page === null || hasStructuredPageTeardown(this.page);
      return {
        passed: false,
        evaluationOutcome:
          opts?.signal?.aborted || pageTornDown ? 'infrastructure_error' : 'refuted',
        reason: err instanceof Error ? err.message : String(err),
      };
    }
  }

  // ================ BROWSER MANAGEMENT ================

  async close(_opts?: BrowserClientOptions): Promise<void> {
    const page = await this.getPage();
    const context = await this.getContext();
    const otherUrls = context
      .pages()
      .filter((p) => p !== page)
      .map((p) => p.url());
    const policy = decideClosePolicy(page.url(), otherUrls);
    if (policy.kind === 'refuse') {
      this.logger.info('[DirectPlaywright] Refusing close on last live tab', {
        currentUrl: page.url(),
        otherTabCount: otherUrls.length,
        reason: 'last-tab-guard',
      });
      throw new Error(policy.reason);
    }

    this.pageClosedIntentionally = true;
    this.logger.debug('[DirectPlaywright] Closing page');

    await page.close();
    this.page = null;
  }

  async resize(width: number, height: number, _opts?: BrowserClientOptions): Promise<void> {
    const dialogMsg = this.getPendingDialogMessage();
    if (dialogMsg) throw new Error(dialogMsg);

    const page = await this.getPage();

    this.logger.debug('[DirectPlaywright] Resizing viewport', { width, height });

    await page.setViewportSize({ width, height });

    // Write through to the connect options: they are the single source of the
    // requested viewport, read by getViewportSize() (and from there the agent
    // prompt), by the screencast encoder's canvas, and by every later page this
    // client creates. Leaving them stale would make a restore, a context swap, or
    // a new tab silently revert the caller's resize.
    if (this.lastConnectOptions) {
      this.lastConnectOptions = {
        ...this.lastConnectOptions,
        viewport: { width, height },
      };
    }
  }

  async tabs(
    action: 'list' | 'new' | 'close' | 'select',
    selector?: number | { index?: number; handle?: number; match?: string },
    _opts?: BrowserClientOptions
  ): Promise<unknown> {
    const context = await this.getContext();
    const pages = context.pages();
    const sel = normalizeTabSelector(selector);

    this.logger.debug('[DirectPlaywright] Tab action', {
      action,
      selector: sel,
      pageCount: pages.length,
    });

    switch (action) {
      case 'list':
        return listTabs(pages, this.page, this._tabRegistry);

      case 'new': {
        const newPage = await context.newPage();
        this._setActivePage(newPage);
        return buildSelectedTabResult(context.pages(), newPage, this._tabRegistry);
      }

      case 'close': {
        const resolved = await resolveTabTarget(pages, sel, this._tabRegistry);
        // A supplied-but-unresolved selector (stale handle, popup race, no
        // match) must NOT fall through to closing the active page. Only default
        // to this.page when NO selector was supplied.
        if (hasExplicitTabSelector(sel) && !resolved) {
          return buildNoTabMatchResult(sel);
        }
        const target = resolved ?? this.page;
        const closingActivePage = target !== null && target === this.page;
        if (target) await target.close();
        // Closing a background tab must not change focus. Only pick a fallback
        // tab when the active page itself was closed or disappeared.
        const remainingPages = context.pages();
        const currentActive = this.page && !this.page.isClosed() ? this.page : null;
        const remaining = chooseActivePageAfterClose({
          closingActivePage,
          currentActivePage: currentActive,
          remainingPages,
        });
        if (remaining) this._setActivePage(remaining);
        else this.page = null;
        // Return a small result (not void) so callers/CLI renderers have
        // something to display and can confirm the new active tab after a close.
        return buildClosedTabResult(remainingPages, remaining, this._tabRegistry);
      }

      case 'select': {
        const target = await resolveTabTarget(pages, sel, this._tabRegistry);
        if (!target) {
          return buildNoTabMatchResult(sel);
        }
        this._setActivePage(target);
        return buildSelectedTabResult(pages, target, this._tabRegistry);
      }
    }
  }

  /**
   * Enable/disable auto-capture of newly opened tabs (popups / target=_blank):
   * their listeners get wired at open time so they feed console/URL signals and
   * are valid select targets, but the active page is NOT switched. Driven by the
   * `browser.fast_drive.tabs.enabled` knob on the API side.
   */
  setAutoCaptureNewPages(enabled: boolean): void {
    this._autoCaptureNewPages = enabled;
  }

  // ================ PAGE ACCESS (for cache replay) ================

  /**
   * Get the underlying Playwright Page for direct manipulation.
   * Used by cache replay to execute actions directly without going through the agent.
   * Returns null if no page is available.
   */
  async getPageForReplay(): Promise<Page | null> {
    if (this.pageClosedIntentionally) {
      this.logger.warn('[DirectPlaywright] Page was closed intentionally, cannot get for replay');
      return null;
    }
    if (this.page && !this.page.isClosed()) {
      return this.page;
    }
    return null;
  }

  // ================ STORAGE ================

  async getStorageState(opts?: {
    signal?: AbortSignal;
    timeoutMs?: number;
    method?: StorageStateMethod;
  }): Promise<unknown> {
    const context = await this.getContext();
    const nativeTimeoutMs = Math.min(opts?.timeoutMs ?? 15_000, 15_000);
    const indexedDBTimeoutMs = Math.min(opts?.timeoutMs ?? 30_000, 30_000);

    this.logger.debug('[DirectPlaywright] Getting storage state', {
      method: opts?.method ?? 'playwright',
    });

    // Get native Playwright storage state (cookies + localStorage)
    const playwrightState = await withOperationTimeout(
      context.storageState(),
      nativeTimeoutMs,
      '[DirectPlaywright] context.storageState'
    );
    this.logger.debug('[DirectPlaywright] Native storage state captured', {
      cookieCount: playwrightState.cookies.length,
      originCount: playwrightState.origins.length,
    });

    // Also extract IndexedDB data (critical for Firebase Auth and similar)
    try {
      const page = await this.getPage();
      const indexedDB = await withOperationTimeout(
        extractIndexedDB(page),
        indexedDBTimeoutMs,
        '[DirectPlaywright] extractIndexedDB'
      );
      if (indexedDB) {
        this.logger.info('[DirectPlaywright] Extracted IndexedDB data', {
          databaseCount: indexedDB.databases.length,
          databaseNames: indexedDB.databases.map((db) => db.name),
        });
        return { ...playwrightState, indexedDB };
      } else {
        this.logger.debug('[DirectPlaywright] No IndexedDB databases found on current page');
      }
    } catch (err) {
      this.logger.warn('[DirectPlaywright] Failed to extract IndexedDB, saving without it', {
        error: err instanceof Error ? err.message : String(err),
      });
    }

    return playwrightState;
  }

  /**
   * Record the authenticated storage state for crash/wedge recovery.
   *
   * The deferred+login auth path logs in on the live context without a swap, so
   * `storageStatePath` / `lastConnectOptions.storageStatePath` still point at the
   * pre-login state. Recovery (restoreBrowser) rebuilds the context from
   * `lastConnectOptions.storageStatePath`; without this update it would restore a
   * logged-out context and the run would hit an auth wall. Mirror what
   * swapContext() already does so recovery restores the authenticated session.
   */
  setAuthenticatedStorageStatePath(storageStatePath: string): void {
    this.storageStatePath = storageStatePath;
    if (this.lastConnectOptions) {
      this.lastConnectOptions = {
        ...this.lastConnectOptions,
        storageStatePath,
        storageStateRequired: true,
      };
    }
    this.logger.info('[DirectPlaywright] Recorded authenticated storage state for recovery', {
      statePathFingerprint: fingerprintStorageState(storageStatePath),
      storageStateRequired: true,
    });
  }

  async getCurrentUrl(_opts?: BrowserClientOptions): Promise<string> {
    return (await this.getPage()).url();
  }

  onNavigation(handler: (url: string) => void): () => void {
    this.navigationHandlers.add(handler);
    return () => {
      this.navigationHandlers.delete(handler);
    };
  }

  /**
   * Get and clear recovery info if a crash recovery happened recently.
   * Returns recovery info once, then null on subsequent calls until next crash.
   * Used by browser tools to prepend recovery notice to tool results.
   */
  consumeRecoveryInfo(): RecoveryInfo | null {
    const info = this._lastRecoveryInfo;
    this._lastRecoveryInfo = null;
    return info;
  }

  /**
   * Get the current viewport dimensions.
   * Returns the viewport size used when connecting, defaulting to 1280x800.
   */
  getViewportSize(): { width: number; height: number } {
    return this.lastConnectOptions?.viewport ?? DEFAULT_VIEWPORT;
  }

  async getTitle(_opts?: BrowserClientOptions): Promise<string> {
    return await (await this.getPage()).title();
  }

  async getLinks(_opts?: BrowserClientOptions): Promise<string[]> {
    const page = await this.getPage();
    // Get all links with their resolved (absolute) href property
    // Using .href (property) instead of getAttribute('href') to get fully resolved URLs
    // Filter to HTTP/HTTPS links only to match MCP behavior
    return await page.$$eval('a[href]', (links) =>
      links
        .map((a) => (a as HTMLAnchorElement).href) // .href returns the resolved absolute URL
        .filter((h): h is string => !!h && (h.startsWith('http://') || h.startsWith('https://')))
    );
  }

  async writeSnapshotToDisk(yaml: string): Promise<string> {
    // Generate unique filename with timestamp and short UUID
    const timestamp = Date.now();
    const shortId = crypto.randomUUID().slice(0, 8);
    const filename = `snapshot-${timestamp}-${shortId}.yaml`;
    const filePath = path.join(this.tmpBaseDir, filename);

    await fs.writeFile(filePath, yaml, 'utf-8');
    this.snapshotFilePaths.add(filePath);

    this.logger.debug('[DirectPlaywright] Wrote snapshot to disk', {
      filePath,
      size: yaml.length,
    });

    return filePath;
  }

  async getElementBoundingBox(
    ref: string,
    _opts?: BrowserClientOptions
  ): Promise<{ x: number; y: number; width: number; height: number } | null> {
    try {
      const locator = await this.resolveRef(ref);
      const box = await locator.boundingBox();
      if (!box) {
        this.logger.debug('[DirectPlaywright] Element has no bounding box (not visible)', { ref });
        return null;
      }
      return { x: box.x, y: box.y, width: box.width, height: box.height };
    } catch (err) {
      this.logger.debug('[DirectPlaywright] Failed to get bounding box', {
        ref,
        error: err instanceof Error ? err.message : String(err),
      });
      return null;
    }
  }

  async getElementBoundingBoxes(
    refs: string[],
    _opts?: BrowserClientOptions
  ): Promise<Map<string, { x: number; y: number; width: number; height: number }>> {
    const result = new Map<string, { x: number; y: number; width: number; height: number }>();
    if (refs.length === 0) return result;

    try {
      const page = await this.getPage();
      const boxes = await resolveRefs(page, refs, (el) => {
        const r = el.getBoundingClientRect();
        if (r.width === 0 && r.height === 0) return null;
        return { x: r.x, y: r.y, width: r.width, height: r.height };
      });
      for (const [ref, box] of Object.entries(boxes)) {
        if (box) result.set(ref, box);
      }
    } catch (err) {
      this.logger.debug('[DirectPlaywright] Failed to batch-query bounding boxes', {
        refCount: refs.length,
        error: err instanceof Error ? err.message : String(err),
      });
    }

    return result;
  }

  /**
   * Probe what element is at a given pixel coordinate.
   *
   * After a snapshot is captured, uses elementFromPoint to inspect
   * the DOM at (x, y), then correlates with the current accessibility
   * snapshot to find the matching ref.
   */
  async probeAtCoordinates(
    x: number,
    y: number,
    _opts?: BrowserClientOptions
  ): Promise<ProbeResult | null> {
    try {
      const page = await this.getPage();

      // Get element info at coordinates via DOM
      const elementInfo = await page.evaluate(
        ({ px, py }) => {
          const el = document.elementFromPoint(px, py);
          if (!el) return null;

          // Mark for ref correlation
          el.setAttribute('data-probe-target', 'true');

          return {
            tagName: el.tagName.toLowerCase(),
            id: el.id || undefined,
            className: typeof el.className === 'string' ? el.className || undefined : undefined,
            ariaLabel: el.getAttribute('aria-label') || undefined,
            textContent: el.textContent?.trim().slice(0, 100) || undefined,
            role: el.getAttribute('role') || undefined,
          };
        },
        { px: x, py: y }
      );

      if (!elementInfo) return null;

      // Try to find the ref by matching against the last snapshot
      let ref: string | undefined;
      let label: string | undefined;
      let boundingBox: { x: number; y: number; width: number; height: number } | undefined;

      if (this.lastSnapshotYaml) {
        const elements = parseSnapshot(this.lastSnapshotYaml);

        // Walk tree to find element matching the probed element
        const findMatch = (els: SnapshotElement[]): SnapshotElement | undefined => {
          for (const el of els) {
            // Match by aria-label or text content
            if (elementInfo.ariaLabel && el.text === elementInfo.ariaLabel) return el;
            if (elementInfo.id && el.text === elementInfo.id) return el;
            const childResult = findMatch(el.children);
            if (childResult) return childResult;
          }
          return undefined;
        };

        const matched = findMatch(elements);
        if (matched?.ref) {
          // Verify via DOM identity
          try {
            const probeLocator = page.locator('[data-probe-target="true"]');
            const refLocator = page.locator(`aria-ref=${matched.ref}`);
            const probeHandle = await probeLocator.elementHandle();
            const refHandle = await refLocator.elementHandle();

            if (probeHandle && refHandle) {
              const isSame = await page.evaluate(
                ([a, b]) => a === b || a.contains(b) || b.contains(a),
                [probeHandle, refHandle] as const
              );
              if (isSame) {
                ref = matched.ref;
                label = matched.text || undefined;
              }
              await probeHandle.dispose();
              await refHandle.dispose();
            }
          } catch {
            // Non-fatal: ref correlation failed
          }
        }

        // If no match via tree walk, try bounding box overlap
        if (!ref) {
          const walkRefs = (els: SnapshotElement[]): string[] => {
            const refs: string[] = [];
            for (const el of els) {
              if (el.ref) refs.push(el.ref);
              refs.push(...walkRefs(el.children));
            }
            return refs;
          };

          for (const candidateRef of walkRefs(elements)) {
            try {
              const box = await this.getElementBoundingBox(candidateRef);
              if (
                box &&
                x >= box.x &&
                x <= box.x + box.width &&
                y >= box.y &&
                y <= box.y + box.height
              ) {
                ref = candidateRef;
                // Find label from tree
                const findByRef = (els: SnapshotElement[]): SnapshotElement | undefined => {
                  for (const el of els) {
                    if (el.ref === candidateRef) return el;
                    const child = findByRef(el.children);
                    if (child) return child;
                  }
                  return undefined;
                };
                const foundEl = findByRef(elements);
                label = foundEl?.text || undefined;
                boundingBox = box;
                break;
              }
            } catch {
              // Skip this ref
            }
          }
        }
      }

      // Get bounding box for the probed element if not already found
      if (!boundingBox && ref) {
        boundingBox = (await this.getElementBoundingBox(ref)) ?? undefined;
      }

      // Cleanup probe marker
      await page
        .evaluate(() => {
          const el = document.querySelector('[data-probe-target]');
          el?.removeAttribute('data-probe-target');
        })
        .catch(() => {});

      return {
        ref,
        label,
        boundingBox,
        elementInfo,
      };
    } catch (err) {
      this.logger.debug('[DirectPlaywright] Probe at coordinates failed', {
        x,
        y,
        error: err instanceof Error ? err.message : String(err),
      });
      return null;
    }
  }

  // ================ TRACING ================

  async startTracing(_opts?: BrowserClientOptions): Promise<void> {
    await this.traceAdapter.startTracing(_opts);
  }

  async traceGroup(name: string): Promise<void> {
    await this.traceAdapter.traceGroup(name);
  }

  async traceGroupEnd(): Promise<void> {
    await this.traceAdapter.traceGroupEnd();
  }

  traceGroupDepth(): number {
    return this.traceAdapter.traceGroupDepth();
  }

  async traceGroupEndUntil(depth: number): Promise<void> {
    await this.traceAdapter.traceGroupEndUntil(depth);
  }

  /**
   * Stop tracing and get trace file path.
   *
   * Note: Unlike MCP which produces separate `.trace`, `.network`, and `/resources` files,
   * Playwright's native tracing API produces a single `trace.zip` file containing all data.
   * The `network` and `resources` fields are returned as empty strings for interface compatibility.
   * The trace.zip can be viewed at https://trace.playwright.dev/
   */
  async stopTracing(_opts?: BrowserClientOptions): Promise<TraceStopResult> {
    return this.traceAdapter.stopTracing(_opts);
  }

  // ================ PRIVATE: Accessibility Snapshot ================

  /**
   * Capture accessibility snapshot using Playwright's internal AI snapshot API.
   * This produces the EXACT same format as MCP including:
   * - Refs on all elements (not just interactive)
   * - Link URLs, heading levels, cursor styles
   * - Same role vocabulary (generic, paragraph, etc.)
   */
  /**
   * Full capture = raw substrate (cheap) + augmentation (expensive). Legacy
   * path (flag off) and any internal caller that wants the complete substrate.
   * Returns the previous YAML on abort (unchanged behavior).
   */
  private async captureSnapshotInternal(signal?: AbortSignal): Promise<string> {
    await this.traceGroup('Canary Internal: Capture Snapshot');
    try {
      const previousYaml = this.lastSnapshotYaml;
      const rawYaml = await this.captureRawSubstrate(signal);
      if (signal?.aborted || !rawYaml) return previousYaml;
      return await this.augmentSubstrate(rawYaml, signal);
    } finally {
      await this.traceGroupEnd();
    }
  }

  /**
   * CHEAP tier: read toasts, run the pre-snapshot DOM enrichers, and take the
   * `ariaSnapshot`. Returns the raw (un-augmented) substrate YAML — the
   * ground-truth representation used for freshness comparison — or '' if the
   * capture was aborted mid-way.
   */
  private async captureRawSubstrate(
    signal?: AbortSignal,
    opts?: { refless?: boolean }
  ): Promise<string> {
    const page = await this.getPage();

    // Read and clear any captured toast notifications before snapshotting.
    // Raced against `signal` so an orphaned capture (per-tool timeout fired)
    // doesn't keep running and later mutate `_lastCapturedToasts`.
    let capturedToasts: CapturedToast[] = [];
    try {
      capturedToasts = await this.raceAgainstSignal('snapshot:toasts', signal, () =>
        page.evaluate(() => {
          const w = window as unknown as Record<string, unknown>;
          const toasts = (w.__capturedToasts as CapturedToast[]) || [];
          w.__capturedToasts = [];
          return toasts;
        })
      );
    } catch {
      // Non-fatal: page may have navigated, context destroyed, or signal aborted.
    }

    if (signal?.aborted) return '';

    if (capturedToasts.length > 0) {
      // Assign TTL to new toasts
      for (const t of capturedToasts) {
        t.snapshotsRemaining = TOAST_SNAPSHOT_TTL;
      }
      // Merge: new toasts take priority over existing ones with same text
      const existingByText = new Map(this._lastCapturedToasts.map((t) => [t.text, t]));
      for (const t of capturedToasts) {
        existingByText.set(t.text, t);
      }
      // Re-check abort right before the write so a late orphan can't corrupt
      // `_lastCapturedToasts` after the per-tool timeout has elapsed.
      if (signal?.aborted) return '';
      this._lastCapturedToasts = Array.from(existingByText.values());
      this.logger.debug('[DirectPlaywright] Captured ephemeral toasts', {
        count: capturedToasts.length,
        texts: capturedToasts.map((t) => t.text),
      });
    }

    if (signal?.aborted) return '';

    // Fast-drive (refless) intentionally reasons over the RAW substrate. The
    // built-in SVG/hidden-clickable/modal promotion below injects synthetic
    // role/aria-label anchors into the a11y tree. When a cache recipe is authored
    // from a fast-drive capture, those synthetic anchors get baked into locators
    // that don't correspond to anything in the real DOM, so replay is fragile.
    // Skip ALL enrichment when refless and let the agent (and the cache it writes)
    // see exactly what the page renders. Full-agent (`mode:'ai'`) captures still
    // enrich, so nothing changes for the ref-based agent.
    const skipEnrichment = opts?.refless === true;

    // Enrich interactive SVG elements, hidden clickables, and modal dialogs.
    // Must run BEFORE ariaSnapshot() since that reads the live a11y tree.
    if (!skipEnrichment) {
      await this.traced('enrichment', 'DOM enrichment (SVG, hidden, modals)', () =>
        this.raceAgainstSignal('snapshot:enrichment', signal, () =>
          Promise.all([
            this.enrichInteractiveSVGElements(page),
            this.enrichHiddenClickableElements(page),
            this.enrichModalDialogElements(page),
          ]).then(() => undefined)
        )
      );
    }

    if (signal?.aborted) return '';

    // Use Playwright's public ariaSnapshot API with AI mode
    // This generates the YAML-like format with refs automatically
    // Retry with backoff: the API can throw transient errors when
    // the accessibility tree isn't fully initialized after navigation.
    const snapshot = await this.traced(
      'snapshot_for_ai',
      'capture accessibility tree',
      async () => {
        let result: string | undefined;
        const maxRetries = 3;
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          if (signal?.aborted) return undefined;
          try {
            // Driver-cap the ariaSnapshot itself. ariaSnapshot has no Playwright
            // timeout and ignores AbortSignal, so on a jammed renderer the op
            // parks indefinitely; `withOperationTimeout` makes the *driver* give
            // up (the orphan CDP op is then reaped by force-recovery). See
            // TIMEOUTS.ARIA_SNAPSHOT_CAP.
            result = await this.raceAgainstSignal('snapshot:aria', signal, () =>
              withOperationTimeout(
                // Ref-less (fast-drive): `ariaSnapshot()` with no mode emits the
                // YAML tree WITHOUT `[ref=...]`/`[cursor=...]` stamping. ai-mode
                // stamps a ref on every node — the cost this mode exists to avoid.
                opts?.refless ? page.ariaSnapshot() : page.ariaSnapshot({ mode: 'ai' }),
                TIMEOUTS.ARIA_SNAPSHOT_CAP,
                'snapshot:aria'
              )
            );
            break;
          } catch (err) {
            if (signal?.aborted) return undefined;
            // A cap timeout means the renderer is jammed — retrying ariaSnapshot
            // would just park again. Bail straight to the empty fallback and let
            // the page be force-recovered (the orphan op dies with the page).
            // Discriminate via the typed error + label (not the message string),
            // so a future change to the timeout message can't silently disable it.
            if (err instanceof OperationTimeoutError && err.label === 'snapshot:aria') {
              this.logger.warn(
                '[DirectPlaywright] ariaSnapshot exceeded driver cap, using empty fallback',
                { capMs: TIMEOUTS.ARIA_SNAPSHOT_CAP, attempt }
              );
              break;
            }
            if (attempt < maxRetries) {
              this.logger.debug('[DirectPlaywright] Snapshot attempt failed, retrying', {
                attempt,
                maxRetries,
                error: err instanceof Error ? err.message : String(err),
              });
              await this.raceAgainstSignal(
                'snapshot:retry-backoff',
                signal,
                () => new Promise<void>((resolve) => setTimeout(resolve, 200 * attempt))
              );
            } else {
              this.logger.warn(
                '[DirectPlaywright] All snapshot attempts failed, using empty fallback',
                {
                  attempts: maxRetries,
                  error: err instanceof Error ? err.message : String(err),
                }
              );
            }
          }
        }
        return result;
      }
    );

    if (signal?.aborted) return '';
    return snapshot || '- page: (empty)';
  }

  /**
   * EXPENSIVE tier: augment a raw substrate into the full substrate (per-ref
   * DOM-derived hints, v2 attrs, frame selectors). Publishes `lastSnapshotYaml`.
   * Returns the previous YAML on abort.
   */
  private async augmentSubstrate(rawYaml: string, signal?: AbortSignal): Promise<string> {
    const page = await this.getPage();
    const previousYaml = this.lastSnapshotYaml;
    if (signal?.aborted) return previousYaml;

    const augmentedYaml = await this.traced(
      'augmentation',
      'YAML augmentation (labels, grids, forms, selects)',
      () =>
        this.raceAgainstSignal('snapshot:augmentation', signal, async () => {
          const elements = parseSnapshot(rawYaml);
          // Identify large grids so we can skip augmenting cells inside grids
          // that will be collapsed in the semantic snapshot.
          const largeGridRefs = new Set(identifyLargeGridRefs(rawYaml, elements));

          // Single consolidated per-ref pass for ALL augmentation: icon-only
          // buttons/links, unlabeled clickable generics, clickable images,
          // toggles, svg promotion, editability, gridCellValue,
          // formFieldLabel, filterState, modalV2. Ref resolution is split into
          // bounded batches so large pages cannot enqueue thousands of
          // simultaneous CDP operations. `~name-src=augmented:dom-mutation`
          // provenance is tagged inline in the apply phase; no side-channel map
          // needed.
          const perRef = await runPerRefAugmentations(page, rawYaml, this.logger, {
            v2: true,
            preParsedElements: elements,
            skipGridRefs: largeGridRefs,
            signal,
          });
          if (signal?.aborted) return previousYaml;
          let yaml = perRef.yaml;

          yaml = await this.augmentHiddenSelectOptions(page, yaml);
          if (signal?.aborted) return previousYaml;
          yaml = await augmentBlockingModalSignalInSnapshot(page, yaml, this.logger, elements);
          if (signal?.aborted) return previousYaml;
          // Substrate-v2 post-pass: decorate the augmented YAML with synthetic
          // `~`-prefixed attrs so downstream consumers (parser, `buildLocator`)
          // route through the v2 path. Pure string transform, additive only.
          if (perRef.v2ModalOverrides.length > 0) {
            yaml = appendV2RoleOverrides(
              yaml,
              perRef.v2ModalOverrides.map((o) => ({
                ref: o.ref,
                augRole: o.augRole,
                augName: o.augName,
                cssHint: buildCssHint(o.cssTag, o.cssClasses ?? []),
              }))
            );
          }
          // Walk every sub-frame and tag refs inside iframes with their CSS
          // selector so `buildLocator` can produce
          // `frameLocator('iframe[name="..."]').getByRole(...)` strategies
          // without relying on recording-time metadata grafts. Cross-origin
          // frames silently skip — those refs stay un-tagged and replay falls
          // back to main-frame behavior. (Page-bound — stays on the host thread.)
          const frameByRef = await collectFrameSelectors(page, yaml, this.logger);
          if (signal?.aborted) return previousYaml;

          // Pure tail (frame-selector tagging + v2 derived attrs) — O(n) string
          // passes with no page access. Offloaded to a worker process when
          // browser.cpu-offload.enabled so they don't block the host event loop
          // under concurrent load; identical output inline when disabled (the
          // default) or on worker failure.
          yaml = await augmentTailMaybeOffloaded({
            yaml,
            frameSelectors: frameByRef.size > 0 ? [...frameByRef] : undefined,
          });
          if (signal?.aborted) return previousYaml;
          return yaml;
        })
    );

    // Final abort gate before publishing snapshot state. If the per-tool
    // timeout fired anywhere above, drop the (potentially stale) result on
    // the floor instead of overwriting `lastSnapshotYaml` /
    // `lastSnapshotTimestamp` after the agent has already received the
    // timeout sentinel.
    if (signal?.aborted) return previousYaml;
    this.lastSnapshotYaml = augmentedYaml;
    this.lastSnapshotTimestamp = Date.now();

    // Diagnostic logging deliberately carries metrics only — page content may
    // contain customer data or credentials and must never enter logs.
    const snapshotSize = buildSnapshotSizeLogFields(this.lastSnapshotYaml);
    this.logger.debug('[DirectPlaywright] Snapshot captured', snapshotSize);

    if (snapshotSize.estimatedTokens > 50000) {
      this.logger.warn('[DirectPlaywright] Large snapshot detected', snapshotSize);
    }

    return this.lastSnapshotYaml;
  }

  /**
   * Surface hidden <select> option values from Chosen.js / Select2 widgets
   * so the agent knows what choices exist before clicking.
   */
  private async augmentHiddenSelectOptions(page: Page, yaml: string): Promise<string> {
    return augmentHiddenSelectOptionsInSnapshot(page, yaml, this.logger);
  }

  /**
   * Enrich interactive SVG elements so they appear as labeled groups
   * in the accessibility tree. Targets SVG shape elements (path, polygon,
   * rect, circle) that have an `id` attribute and are not pointer-events:none.
   *
   * For each qualifying shape, walks to the parent `<g>` and injects
   * `aria-label` derived from: <title> in group → <text> in group → shape id.
   *
   * This runs BEFORE ariaSnapshot() so the browser promotes the enriched
   * `<g>` elements to `group "label"` in the accessibility tree, making them
   * ref-clickable by the agent.
   *
   * Marked with `data-svg-enriched="true"` for diagnostics and cleanup.
   */
  private async enrichInteractiveSVGElements(page: Page): Promise<void> {
    await enrichInteractiveSVGElementsInPage(page, this.logger);
  }

  /**
   * Enrich hidden clickable elements from known UI library patterns.
   * Injects `role="button"` and `aria-label` so they appear in the a11y tree.
   */
  private async enrichHiddenClickableElements(page: Page): Promise<void> {
    await enrichHiddenClickableElementsInPage(page, this.logger);
  }

  /**
   * Enrich positioned overlays that look like modals but lack `role="dialog"`.
   * Injects `role="dialog"` and `aria-label` so the agent recognizes them.
   */
  private async enrichModalDialogElements(page: Page): Promise<void> {
    await enrichModalDialogElementsInPage(page, this.logger);
  }

  /**
   * Resolve a ref to a Playwright locator using the internal aria-ref selector.
   * This is the same mechanism MCP uses to resolve refs.
   *
   * Also extracts and caches element metadata for the recording layer.
   */
  private async resolveRef(ref: string): Promise<ReturnType<Page['locator']>> {
    const page = await this.getPage();

    // Use Playwright's internal aria-ref= selector - same as MCP uses
    // This directly resolves the ref from the last snapshot
    const locator = page.locator(`aria-ref=${ref}`);

    // Verify the element exists
    const count = await locator.count();
    if (count === 0) {
      throw new Error(
        `Ref "${ref}" not found in the current page snapshot. Try capturing a new snapshot.`
      );
    }
    if (count > 1) {
      throw new Error(
        `Ref "${ref}" is ambiguous — resolves to ${count} elements. ` +
          `Capture a fresh snapshot and use a more specific ref ` +
          `(snapshots disambiguate sibling duplicates as "(1st)/(2nd)/…").`
      );
    }

    // Extract and cache element metadata for recording layer
    // This provides accurate DOM properties without YAML snapshot parsing
    try {
      const metadata = await this.extractElementMetadata(locator, ref);
      this.lastResolvedElementMetadata.set(ref, metadata);
    } catch (err) {
      // Non-fatal - recording will fall back to snapshot parsing if needed
      this.logger.debug('[DirectPlaywright] Failed to extract element metadata', {
        ref,
        error: err instanceof Error ? err.message : String(err),
      });
    }

    return locator;
  }

  /**
   * Capture rich info about the DOM element at a given point.
   * Used for click feedback — describes what was actually clicked with
   * structural context (parent role, table row, column headers, etc.).
   */
  private async captureElementAtPoint(x: number, y: number): Promise<ClickedElementInfo | null> {
    const page = await this.getPage();
    return captureElementAtPointInPage(page, x, y);
  }

  /**
   * Extract cache-relevant metadata directly from the DOM element.
   * This gets actual properties, not parsed snapshot YAML.
   *
   * The extracted metadata is more accurate than YAML parsing because:
   * - Accessible names from YAML can concatenate text from child elements
   * - Direct DOM access gets the actual aria-label, textContent, etc.
   */
  private async extractElementMetadata(
    locator: ReturnType<Page['locator']>,
    ref: string
  ): Promise<ElementInfo> {
    return extractElementMetadataFromLocator(locator, ref, this.logger);
  }

  /**
   * Get cached element metadata from the last resolveRef call.
   * Used by the recording layer to derive selectors without re-parsing YAML snapshots.
   *
   * @param ref - The element ref (e.g., 'e5')
   * @returns Cached ElementInfo or undefined if not available
   */
  getLastResolvedMetadata(ref: string): ElementInfo | undefined {
    return this.lastResolvedElementMetadata.get(ref);
  }

  /**
   * Get the raw YAML from the most recent snapshot, if available.
   */
  getLastSnapshotYaml(): string | null {
    return this.lastSnapshotYaml || null;
  }

  getLastSnapshotIfFresh(maxAgeMs: number): string | null {
    if (!this.lastSnapshotYaml) return null;
    if (Date.now() - this.lastSnapshotTimestamp > maxAgeMs) return null;
    return this.lastSnapshotYaml;
  }

  /**
   * Return the warm (augmented) snapshot YAML if its raw substrate still matches
   * the live page, else null — verified with a cheap raw capture, never
   * augmenting. Used by the executor to reuse the warm snapshot as a diff
   * baseline. Returns null when live-snapshot is disabled (caller falls back to
   * a fresh capture).
   */
  async getReusableSnapshotYaml(): Promise<string | null> {
    if (!this.liveSnapshotEnabled) return null;
    const reusable = await this.liveSnapshot.getReusable();
    return reusable ? mintedYaml(reusable) : null;
  }

  /**
   * Fire-and-forget background *preparation* of the next snapshot. Waits for the
   * DOM to settle, then re-captures and re-augments on change so the next
   * request hits the reuse path. No-op when live-snapshot is disabled.
   */
  warmSnapshot(opts?: { quietMs?: number; maxWaitMs?: number; signal?: AbortSignal }): void {
    if (!this.liveSnapshotEnabled) return;
    // Fast-drive reads the ref-less substrate; the warm builds the EXPENSIVE
    // augmented substrate, which a ref-less capture never reuses. Skip it so
    // fast-drive doesn't pay for an augment it will throw away.
    if (this.navigationSnapshotGate.shouldSkipWarmSnapshot()) return;
    this.liveSnapshot.warm({ ...opts, rawOnMiss: this.liveSnapshotRawOnMiss });
  }

  /**
   * Record an executor-layer LiveSnapshot event (reuse hit/miss, capture timing,
   * screenshot decision) into the per-step accumulator. No-op when the feature is
   * off so the executor can call unconditionally.
   */
  recordLiveSnapshotEvent(ev: LiveSnapshotExecutorEvent): void {
    if (!this.liveSnapshotEnabled) return;
    this.liveSnapshotStatsAcc.recordExecutorEvent(ev);
  }

  /**
   * Snapshot and reset the per-step LiveSnapshot efficacy stats. Returns null
   * when nothing was recorded (feature off, or a step with no captures), so the
   * agent persists no `liveSnapshot` metadata for those steps.
   */
  drainLiveSnapshotStats(): LiveSnapshotStats | null {
    return this.liveSnapshotStatsAcc.drain();
  }

  // ================ CDP SCREENCAST ================

  async startScreencast(
    onFrame: ScreencastFrameHandler,
    options?: ScreencastOptions,
    videoCallbacks?: VideoEncodingCallbacks
  ): Promise<void> {
    if (!this.page) {
      this.logger.warn('[DirectPlaywright] Cannot start screencast: no page');
      return;
    }

    if (!this.screencastManager) {
      this.screencastManager = new CdpScreencastManager(
        options,
        this.screencastEncoderFactory ?? undefined
      );
    }
    this.screencastHandler = onFrame;
    this.screencastOptions = options;
    this.screencastVideoCallbacks = videoCallbacks;
    // Legacy start() path — single-handler API, internally subscribes to the
    // shared bus so a concurrent recording (A) and live stream (B) consume
    // ONE Chrome screencast subscription per page. We pass `options` through
    // so B's choices override any constructor-time defaults (which may have
    // been set by A's earlier lazy-create with `undefined`).
    //
    // `videoCallbacks` — new, additive 3rd parameter — is the delivery target
    // for `options.enableVideoEncoding`: it is forwarded straight to
    // `CdpScreencastManager.start()`, which only wires up the injected
    // `screencastEncoderFactory` when BOTH `enableVideoEncoding` is set AND a
    // real `videoCallbacks` object is present. Before this parameter existed,
    // no caller of `startScreencast()` had any way to supply one, so
    // `enableVideoEncoding` could never actually activate — this is what makes
    // it reachable.
    await this.screencastManager.start(this.page, onFrame, videoCallbacks, options);
  }

  async stopScreencast(): Promise<void> {
    if (this.screencastManager) {
      await this.screencastManager.stop();
    }
  }

  isScreencastActive(): boolean {
    return this.screencastManager?.active ?? false;
  }

  /**
   * Clear cached element metadata.
   * Called when a new snapshot is taken to prevent stale metadata.
   */
  private clearCachedMetadata(): void {
    this.lastResolvedElementMetadata.clear();
  }

  /**
   * Enrich snapshot YAML by adding center coordinates inline with refs.
   * Transforms [ref=e1] -> [ref=e1 center=450,320]
   *
   * Queries bounding boxes for all elements with refs in parallel batches.
   * Elements that are off-screen or no longer exist are silently skipped.
   */
  private async enrichSnapshotWithCoordinates(snapshotYaml: string): Promise<string> {
    // Extract all unique refs from the snapshot
    const refMatches = snapshotYaml.matchAll(/\[ref=(\w+)\]/g);
    const refs = [...new Set([...refMatches].map((m) => m[1]))];

    if (refs.length === 0) return snapshotYaml;

    const startTime = Date.now();

    // Query bounding boxes in parallel batches to avoid overwhelming the browser
    const BATCH_SIZE = 20;
    const coordMap = new Map<string, { x: number; y: number }>();

    for (let i = 0; i < refs.length; i += BATCH_SIZE) {
      const batch = refs.slice(i, i + BATCH_SIZE);
      const results = await Promise.allSettled(
        batch.map(async (ref) => {
          try {
            const locator = await this.resolveRef(ref);
            const box = await locator.boundingBox();
            if (box) {
              return {
                ref,
                x: Math.round(box.x + box.width / 2),
                y: Math.round(box.y + box.height / 2),
              };
            }
          } catch {
            // Element may no longer exist or be off-screen, skip silently
          }
          return null;
        })
      );

      for (const result of results) {
        if (result.status === 'fulfilled' && result.value) {
          coordMap.set(result.value.ref, { x: result.value.x, y: result.value.y });
        }
      }
    }

    // Replace [ref=X] with [ref=X center=x,y] inline
    let enrichedYaml = snapshotYaml;
    for (const [ref, coords] of coordMap) {
      enrichedYaml = enrichedYaml.replace(
        new RegExp(`\\[ref=${ref}\\]`, 'g'),
        `[ref=${ref} center=${coords.x},${coords.y}]`
      );
    }

    const elapsedMs = Date.now() - startTime;
    this.logger.debug('[DirectPlaywright] Enriched snapshot with coordinates', {
      totalRefs: refs.length,
      enrichedRefs: coordMap.size,
      elapsedMs,
    });

    return enrichedYaml;
  }

  // ================ PRIVATE: Response Formatting ================

  /**
   * Format response in MCP-compatible markdown format.
   * Matches MCP's exact output structure for parity.
   */
  private formatMCPResponse(
    result: string,
    snapshot?: string,
    code?: string,
    title?: string
  ): string {
    const pageUrl = this.page?.url();
    const formatted = formatMcpResponse({
      result,
      snapshot,
      code,
      title,
      pageUrl,
      capturedToasts: this._lastCapturedToasts,
      capturedDownloads: this.downloads.downloads,
      lastReportedDownloadIndex: this.downloads.getLastReportedIndex(),
    });
    this._lastCapturedToasts = formatted.nextCapturedToasts;
    this.downloads.setLastReportedIndex(formatted.nextLastReportedDownloadIndex);
    return formatted.text;
  }

  // ================ PRIVATE: Helpers ================

  /**
   * Wait for page to stabilize after an action.
   * All waits are soft (catch errors) to avoid blocking on streaming/websocket sites.
   *
   * @param type - Stabilization level:
   *   - 'navigation': Full network idle wait (after navigate/goBack)
   *   - 'interaction': Brief DOM delay + network idle (after click/submit)
   *   - 'dom-only': Just DOM/animation settling (before snapshot, after hover)
   */
  private async stabilize(
    type: 'navigation' | 'interaction' | 'dom-only',
    signal?: AbortSignal
  ): Promise<void> {
    const page = this.page;
    if (!page || page.isClosed()) return;

    // `networkidle` is intentionally NOT used here. Playwright's networkidle
    // waits for the underlying connection to CLOSE; an app holding an SSE /
    // WebSocket open (e.g. a notification stream) never closes it, so
    // networkidle burns its full timeout on every step. `waitForHttpSettle`
    // keys off response HEADERS instead (stream-safe), and `waitDomQuiet` is
    // driver-capped. Both are raced against the tool signal so an abort skips
    // the wait promptly; all are best-effort (the cap always wins).
    try {
      switch (type) {
        case 'navigation':
          await this.raceAgainstSignal('stabilize:dom', signal, () =>
            this.waitDomQuiet(this.stabilizeDomMs, TIMEOUTS.STABILIZE_NAV)
          ).catch(() => {});
          await this.raceAgainstSignal('stabilize:http', signal, () =>
            this.waitForHttpSettle(TIMEOUTS.STABILIZE_NAV)
          ).catch(() => {});
          break;
        case 'interaction':
          await this.raceAgainstSignal('stabilize:dom-delay', signal, () =>
            page.waitForTimeout(this.stabilizeDomMs)
          ).catch(() => {});
          await this.raceAgainstSignal('stabilize:http', signal, () =>
            this.waitForHttpSettle(TIMEOUTS.STABILIZE_INTERACTION)
          ).catch(() => {});
          break;
        case 'dom-only':
          // Event-driven DOM quiescence when live-snapshot is on (a strictly
          // better signal than a fixed delay); otherwise the legacy fixed wait.
          if (this.liveSnapshotEnabled) {
            await this.raceAgainstSignal('stabilize:dom', signal, () =>
              this.waitDomQuiet(this.stabilizeDomMs, TIMEOUTS.STABILIZE_INTERACTION)
            ).catch(() => {});
          } else {
            await page.waitForTimeout(this.stabilizeDomMs).catch(() => {});
          }
          break;
      }
    } catch (err) {
      // Ignore stabilization errors - page may have closed or navigated
      this.logger.debug('[DirectPlaywright] Stabilization skipped', {
        type,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  /**
   * Returns a descriptive message if a native dialog (alert/confirm/prompt) is
   * currently blocking the page, or null if none is pending.
   *
   * When a dialog is open, all Playwright page interactions hang indefinitely.
   * Callers should check this before touching the page and surface the message
   * to the agent so it can call browser_handle_dialog.
   */
  private getPendingDialogMessage(): string | null {
    return getPendingDialogQueueMessage(this._pendingDialogs);
  }

  consumePendingDialogMessage(): string | null {
    return consumePendingDialogQueueMessage(this._pendingDialogs);
  }

  /**
   * Pop the first native dialog queued *since* a given queue depth, consuming all
   * newly-queued dialogs (so a `run_code` block that triggered one doesn't wedge
   * the next call). Returns null if nothing new was queued. Used by `runCode` to
   * detect a dialog the agent's code triggered without first arming `dialog(...)`.
   */
  private consumeRunCodeDialog(since: number): PendingDialog | null {
    return consumeQueuedRunCodeDialog(this._pendingDialogs, since);
  }

  getCursorOverlay(): CursorOverlay {
    return this.cursorOverlay;
  }

  /** The autonomous (event-driven) cursor used by the fast-drive run_code path. */
  getAutonomousCursor(): AutonomousCursor {
    return this.autonomousCursor;
  }

  private escapeString(str: string): string {
    return str.replace(/"/g, '\\"').replace(/\n/g, '\\n');
  }

  private clearNavigationArtifacts(): void {
    this._capturedConsoleMessages = [];
    this._capturedNetworkRequests.clear();
    this._lastSnapshotConsoleIndex = 0;
    this._snapshotedFailedRequestUrls.clear();
    // Drop the ref-less diff baseline too — a settle diff after navigation must
    // not compare against the previous page's tree (fast-drive mode).
    this.lastReflessYaml = '';
  }

  // ================ PRIVATE: Interaction Highlighting ================

  /**
   * Inject CSS styles for interaction highlights.
   * Only injected once per page; re-injected after navigation or context swap.
   *
   * Sources the stylesheet from `cursor-visuals.ts` rather than restating it. The
   * inline copy this replaced had drifted: its click ripple was red (`#ef4444`)
   * while the shared palette is brand blue, so whichever stylesheet happened to be
   * appended last decided the ripple color by cascade order.
   */
  private async injectHighlightStyles(): Promise<void> {
    if (this.highlightStylesInjected || !this.page) return;

    await this.page.addStyleTag({ content: buildHighlightCss() });
    this.highlightStylesInjected = true;
  }

  /**
   * Show a visual highlight on an element before interacting with it.
   * Used for click, type, hover, and drag interactions.
   * Gracefully handles errors to avoid breaking the actual interaction.
   */
  private async highlightInteraction(
    locator: ReturnType<Page['locator']>,
    type: 'click' | 'type' | 'hover' | 'drag-start' | 'drag-end'
  ): Promise<void> {
    if (!this.highlightEnabled || !this.videoEnabled) return;

    try {
      await this.injectHighlightStyles();

      // Strict locator boundingBox() throws on multi-match; if anything slipped
      // past resolveRef (e.g. a future caller using a non-resolved locator), fall
      // back to first() so the pulse still renders. Log loudly the first time it
      // happens — a missing pulse used to silently fail in `debug` logs, which
      // made it invisible in CloudWatch unless you already knew to grep for it.
      let box: Awaited<ReturnType<typeof locator.boundingBox>> = null;
      try {
        box = await locator.boundingBox();
      } catch (boxErr) {
        const msg = boxErr instanceof Error ? boxErr.message : String(boxErr);
        if (msg.includes('strict mode violation')) {
          this.logger.warn(
            '[DirectPlaywright] highlightInteraction: locator matched multiple elements, ' +
              'falling back to first(). resolveRef should have caught this earlier.',
            { type }
          );
          box = await locator.first().boundingBox();
        } else {
          throw boxErr;
        }
      }
      if (!box) return;

      const page = await this.getPage();
      const highlightType = type.startsWith('drag') ? 'hover' : type;

      // Calculate position: center for click, cover element for type/hover
      const isClick = type === 'click';
      const left = isClick ? box.x + box.width / 2 : box.x - 4;
      const top = isClick ? box.y + box.height / 2 : box.y - 4;
      const width = box.width + 8;
      const height = box.height + 8;

      await page.evaluate(
        ({ left, top, width, height, highlightType, duration, isClick }) => {
          const el = document.createElement('div');
          el.className = `chatsdet-highlight chatsdet-highlight--${highlightType}`;
          el.style.left = `${left}px`;
          el.style.top = `${top}px`;
          if (!isClick) {
            el.style.width = `${width}px`;
            el.style.height = `${height}px`;
          }
          document.body.appendChild(el);
          setTimeout(() => el.remove(), duration + 100);
        },
        { left, top, width, height, highlightType, duration: this.highlightDuration, isClick }
      );

      // Wait for animation to be visible in video
      await page.waitForTimeout(this.highlightDuration);
    } catch (err) {
      // Don't fail the interaction if highlighting fails
      this.logger.debug('[DirectPlaywright] Highlight injection failed', {
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  private async highlightPoint(x: number, y: number, type: 'click' | 'hover'): Promise<void> {
    if (!this.highlightEnabled || !this.videoEnabled) return;

    try {
      await this.injectHighlightStyles();

      const page = await this.getPage();
      await page.evaluate(
        ({ x, y, highlightType, duration }) => {
          const el = document.createElement('div');
          el.className = `chatsdet-highlight chatsdet-highlight--${highlightType}`;
          el.style.left = `${x}px`;
          el.style.top = `${y}px`;
          document.body.appendChild(el);
          setTimeout(() => el.remove(), duration + 100);
        },
        { x, y, highlightType: type, duration: this.highlightDuration }
      );

      await page.waitForTimeout(this.highlightDuration);
    } catch (err) {
      this.logger.debug('[DirectPlaywright] Highlight point failed', {
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  private async getContext(): Promise<BrowserContext> {
    // For persistent context (extensions), context exists without a separate browser handle
    if (this.extensionsEnabled && this.context) {
      return this.context;
    }

    // For pool path, browser is null but context is managed by the lease
    if (this.browserLease && this.context) {
      return this.context;
    }

    if (this.context && this.browser?.isConnected()) {
      return this.context;
    }

    await withOperationTimeout(
      this.restoreBrowser('context_missing'),
      FORCE_RECOVER_TIMEOUT_MS,
      'getContext:restoreBrowser'
    );

    if (!this.context) {
      throw new Error('Browser context unavailable and could not be restored');
    }

    return this.context;
  }

  /**
   * Returns an active page or restores the browser/page if it was closed unexpectedly.
   * If the page was closed intentionally via browser_close/saveVideo, fail fast so the
   * agent learns to reopen a tab instead of continuing blindly.
   */
  private async getPage(): Promise<Page> {
    if (this.pageClosedIntentionally) {
      const message =
        'Current page was closed intentionally. Open a new tab before issuing more browser actions.';
      this.logger.warn(
        '[DirectPlaywright] Page was closed intentionally; refusing to reuse handle'
      );
      throw new Error(message);
    }

    if (this.page && !this.page.isClosed()) {
      return this.page;
    }

    await withOperationTimeout(
      this.restoreBrowser('page_closed'),
      FORCE_RECOVER_TIMEOUT_MS,
      'getPage:restoreBrowser'
    );

    if (!this.page) {
      throw new Error('No active page available after restore');
    }

    return this.page;
  }

  private setupEventListeners(): void {
    if (!this.page) return;
    this.setupPageEventListeners(this.page);
  }

  private setupPageEventListeners(page: Page): void {
    // Idempotent — a page followed via context.on('page') and later selected
    // must not double-wire its listeners.
    if (this._wiredPages.has(page)) return;
    this._wiredPages.add(page);

    // Prune the reverse handle map when any tracked page closes.
    page.on('close', () => {
      this._tabRegistry.prune(page);
    });

    // Console messages
    page.on('console', (msg) => {
      this._capturedConsoleMessages.push({
        type: msg.type(),
        text: msg.text(),
        location: `${msg.location().url}:${msg.location().lineNumber}`,
        // Receive-time, not browser emit-time — Playwright's ConsoleMessage
        // doesn't expose an emit timestamp. Close enough for step-boundary
        // filtering; revisit if we ever route console via CDP directly.
        timestampMs: Date.now(),
      });
    });

    page.on('close', () => {
      if (
        this.pageClosedIntentionally ||
        this.contextClosedIntentionally ||
        this.disconnectInProgress ||
        this.forcedTeardownInProgress
      ) {
        this.logger.debug('[DirectPlaywright] Page closed intentionally');
        return;
      }
      // A background/popup tab closing must NOT null the active binding or
      // trigger crash-recovery — only the currently-active page does.
      if (page !== this.page) {
        this.logger.debug('[DirectPlaywright] Non-active page closed; keeping active binding');
        return;
      }
      // Preserve URL for recovery
      const crashedUrl = this.lastKnownUrl;
      if (crashedUrl && crashedUrl !== 'about:blank') {
        this._pendingCrashUrl = crashedUrl;
        this._pendingCrashType = 'page_crash';
      }
      this.page = null;
      this.recordBrowserLifecycleDiagnostic('page_closed', { url: crashedUrl });
      this.logger.warn('[DirectPlaywright] Page closed unexpectedly; will restore on next action', {
        url: crashedUrl,
      });
    });

    page.on('crash', () => {
      // A background/popup tab crashing must NOT null the active binding or
      // trigger crash-recovery — only the currently-active page does.
      if (page !== this.page) {
        this.logger.debug('[DirectPlaywright] Non-active page crashed; keeping active binding');
        return;
      }
      // Preserve URL for recovery (use lastKnownUrl as page.url() may not be reliable after crash)
      const crashedUrl = this.lastKnownUrl ?? page.url();
      this._pendingCrashUrl = crashedUrl;
      this._pendingCrashType = 'page_crash';
      this.page = null;
      this.recordBrowserLifecycleDiagnostic('page_crashed', { url: crashedUrl });
      this.logger.error('[DirectPlaywright] Page crashed; will attempt to restore on next action', {
        event: 'page_crash',
        url: crashedUrl,
      });
    });

    // Track URL changes for crash recovery + diagnostics. Fires on full loads,
    // redirects, AND SPA pushState/popstate — that breadth is what makes this
    // the right place to source the navigation timeline (vs hooking only the
    // explicit navigate tool call).
    page.on('framenavigated', (frame) => {
      // Only the ACTIVE page drives client-wide navigation state. With popup
      // auto-capture, this listener is also wired on background tabs; a
      // background popup redirecting must NOT overwrite lastKnownUrl or fire the
      // navigation stream, or the timeline reports the background URL and later
      // crash-recovery could restore the active page to it. Per-page consumers
      // still get their own page's events elsewhere.
      if (page !== this.page) return;
      // Only track main frame navigations
      if (frame === page.mainFrame()) {
        const newUrl = frame.url();
        if (newUrl && newUrl !== 'about:blank') {
          this.lastKnownUrl = newUrl;
          const sanitizedUrl = sanitizeDiagnosticUrl(newUrl);
          if (sanitizedUrl) {
            this.pushAuthDiagnostic({
              ...this.authDiagnosticContext(),
              kind: 'navigation',
              url: sanitizedUrl,
            });
          }
          for (const handler of this.navigationHandlers) {
            try {
              handler(newUrl);
            } catch {
              // A buggy subscriber must never break navigation tracking.
            }
          }
        }
      }
    });

    // Network requests
    page.on('request', (request) => {
      const authEvidence = inspectAuthRequest({
        url: request.url(),
        method: request.method(),
        postData: request.postData(),
      });
      if (authEvidence) {
        const requestId = `auth-${++this._authRequestSequence}`;
        const startedAtMs = Date.now();
        this._authRequests.set(request, { requestId, startedAtMs, evidence: authEvidence });
        this.pushAuthDiagnostic({
          ...this.authDiagnosticContext(),
          kind: 'auth_request',
          phase: 'started',
          requestId,
          evidence: authEvidence,
        });
      }
      this._capturedNetworkRequests.set(request.url(), {
        method: request.method(),
        url: request.url(),
        status: 'pending',
        statusText: '',
        resourceType: request.resourceType(),
        startedAt: Date.now(),
      });
      if (request.isNavigationRequest() && request.frame() === page.mainFrame()) {
        void this.downloadRecovery.recover({
          page,
          sourceUrl: page.url(),
          url: request.url(),
          startedAtMs: Date.now(),
        });
      }
    });

    page.on('response', (response) => {
      const existing = this._capturedNetworkRequests.get(response.url());
      if (existing) {
        existing.status = response.status();
        existing.statusText = response.statusText();
      }
      this.trackAuthResponse(response);
    });

    page.on('requestfailed', (request) => {
      const tracked = this._authRequests.get(request);
      if (!tracked) return;
      this.pushAuthDiagnostic({
        ...this.authDiagnosticContext(),
        kind: 'auth_request',
        phase: 'failed',
        requestId: tracked.requestId,
        evidence: tracked.evidence,
        durationMs: Date.now() - tracked.startedAtMs,
        error: sanitizeDiagnosticMessage(request.failure()?.errorText ?? 'request failed').slice(
          0,
          256
        ),
      });
    });

    // Dialogs - capture info and handle to unblock Playwright actions.
    // By default, auto-dismiss so clicks don't stall. If _nextDialogAction is set
    // (via browser_handle_dialog), use that action instead (e.g. accept a confirm).
    page.on('dialog', (dialog) => {
      // Decision logic is extracted to `decideDialogAction` so it can be
      // unit-tested without a real Page/Dialog. The idempotency guard
      // (`_handledDialogs` WeakSet) prevents a duplicate fire from agent-
      // authored listeners (via browser_run_code) from throwing "Cannot
      // accept dialog which is already handled!" — see API-1W8 / API-1VZ.
      const dialogType = dialog.type();
      const dialogMessage = dialog.message();
      const preArmed = !!this._nextDialogAction;
      const wasHandledBefore = this._handledDialogs.has(dialog);
      const { action, clearNextAction } = decideDialogAction(
        dialog,
        this._handledDialogs,
        this._nextDialogAction
      );
      // Per-dialog telemetry: did pre-arm fire? did our listener win? what did it choose?
      // Cross-reference with [Dialog] dispatch ok / FAILED below to verify the dispatch
      // actually closed the dialog (Playwright's accept/dismiss promise resolves when
      // the CDP message is sent, NOT when the browser confirms the dialog closed).
      this.logger.info('[Dialog] event', {
        type: dialogType,
        message: dialogMessage,
        decision: action.kind,
        preArmed,
        hasCompetingHandler: wasHandledBefore,
      });
      if (clearNextAction) this.setPendingArm(null, 'listener-clear');
      switch (action.kind) {
        case 'skip':
          return;
        case 'accept':
          dialog.accept(action.promptText).then(
            () =>
              this.logger.info('[Dialog] dispatch ok', {
                kind: 'accept',
                type: dialogType,
                message: dialogMessage,
              }),
            (err) =>
              this.logger.error('[Dialog] dispatch FAILED', {
                kind: 'accept',
                type: dialogType,
                message: dialogMessage,
                err: err instanceof Error ? err.message : String(err),
              })
          );
          return;
        case 'dismiss-preset':
          dialog.dismiss().then(
            () =>
              this.logger.info('[Dialog] dispatch ok', {
                kind: 'dismiss-preset',
                type: dialogType,
                message: dialogMessage,
              }),
            (err) =>
              this.logger.error('[Dialog] dispatch FAILED', {
                kind: 'dismiss-preset',
                type: dialogType,
                message: dialogMessage,
                err: err instanceof Error ? err.message : String(err),
              })
          );
          return;
        case 'dismiss-and-queue':
          this._pendingDialogs.push({
            type: dialogType,
            message: dialogMessage,
            defaultValue: dialog.defaultValue(),
          });
          dialog.dismiss().then(
            () =>
              this.logger.info('[Dialog] dispatch ok', {
                kind: 'dismiss-and-queue',
                type: dialogType,
                message: dialogMessage,
              }),
            (err) =>
              this.logger.error('[Dialog] dispatch FAILED', {
                kind: 'dismiss-and-queue',
                type: dialogType,
                message: dialogMessage,
                err: err instanceof Error ? err.message : String(err),
              })
          );
          return;
      }
    });
  }

  /**
   * Force the REQUESTED viewport onto a page, whatever its context was built with.
   *
   * The context-level `viewport` option cannot be relied on. Contexts that reach
   * this client from outside carry `viewport: null` — "render at the browser
   * window size" — and there are at least two such paths: `connectOverCDP`
   * reuses the browser's pre-existing default context (see connect()), and the
   * VPC relay creates contexts on the remote host. A dropped viewport is close to
   * invisible: the page simply lays out at the remote window's height (measured
   * 1280x660 against a requested 1280x800), 140px less above the fold than asked
   * for, while `getViewportSize()` — which feeds the agent prompt — keeps
   * reporting the request. The only visible trace was the grey band the video
   * encoder padded into every frame to reconcile the two.
   *
   * `Page.setViewportSize` is safe on a null-viewport Chromium context (it drives
   * `Emulation.setDeviceMetricsOverride`) and is a no-op when the metrics already
   * match, so this costs nothing on the paths that were already correct.
   *
   * Best-effort by design: emulation failing is worth a warning, not a failed
   * connect — a page at the wrong size still runs.
   */
  private async applyRequestedViewport(page: Page, reason: string): Promise<void> {
    const viewport = this.getViewportSize();
    try {
      await page.setViewportSize(viewport);
    } catch (error) {
      this.logger.warn('[DirectPlaywright] Failed to apply requested viewport to page', {
        reason,
        viewport,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  private async createPageWithListeners(context: BrowserContext, reason: string): Promise<Page> {
    const page = await context.newPage();
    await this.applyRequestedViewport(page, reason);
    this.page = page;
    this.pageClosedIntentionally = false;
    this.clearNavigationArtifacts();
    this._pendingDialogs = [];
    this.setPendingArm(null, 'createPageWithListeners');
    this.lastSnapshotYaml = '';
    // Fresh page: drop any warm snapshot so we never reuse a substrate from the
    // old page.
    this.liveSnapshot.reset();
    this.highlightStylesInjected = false; // Reset so styles are re-injected on new page
    this.cursorOverlay.resetInjectionState();
    this.setupPageEventListeners(page);
    page.setDefaultNavigationTimeout(TIMEOUTS.NAVIGATE);
    page.setDefaultTimeout(TIMEOUTS.CLICK);

    // Inject toast capture observer so ephemeral notifications are recorded
    await page.addInitScript(TOAST_CAPTURE_SCRIPT);

    this.logger.info('[DirectPlaywright] Created new page', { reason });
    return page;
  }

  private attachContextListeners(context: BrowserContext): void {
    // Downloads can be emitted by a popup or a cross-origin navigation rather
    // than the page that initiated the action. Context-level capture observes
    // all of those paths exactly once; page-level listeners miss the popup case.
    context.on('download', (download) => {
      this.downloads.capture(download);
    });
    // Install the fake-mic bus before any page loads so apps that call
    // getUserMedia receive a MediaStream backed by our AudioContext
    // destination. See playwright-client/fake-mic-script.ts.
    void context.addInitScript(FAKE_MIC_SCRIPT).catch((err) => {
      this.logger.warn('[DirectPlaywright] Failed to install fake-mic init script', {
        error: err instanceof Error ? err.message : String(err),
      });
    });
    // Symmetric output-side: tee everything reaching ctx.destination /
    // <audio>.play() into a MediaRecorder so workflows can capture and
    // assert on what the page plays. See playwright-client/audio-capture-script.ts.
    void context.addInitScript(AUDIO_CAPTURE_SCRIPT).catch((err) => {
      this.logger.warn('[DirectPlaywright] Failed to install audio-capture init script', {
        error: err instanceof Error ? err.message : String(err),
      });
    });
    // DOM revision counter + quiescence helper (window.__domRevision /
    // __waitDomQuiet), used by the live-snapshot manager to decide reuse and to
    // settle-wait. Installed unconditionally (cheap, idempotent); only consulted
    // when liveSnapshotEnabled.
    void context.addInitScript(DOM_REVISION_SCRIPT).catch((err) => {
      this.logger.warn('[DirectPlaywright] Failed to install dom-revision init script', {
        error: err instanceof Error ? err.message : String(err),
      });
    });
    // Grant mic + camera permissions globally so permission prompts never
    // block flows. `--use-fake-ui-for-media-stream` is a belt-and-suspenders
    // launch flag.
    void context.grantPermissions(['microphone', 'camera']).catch((err) => {
      this.logger.warn('[DirectPlaywright] Failed to grant media permissions', {
        error: err instanceof Error ? err.message : String(err),
      });
    });

    // Track new pages (popups, target="_blank" links) for multi-tab video recording.
    // Assign a stable ID to every page (incl. programmatic popups / target="_blank")
    // and start screencast frame delivery so the live encoder receives frames from
    // any focused tab.
    context.on('page', async (newPage) => {
      const pageId = this._assignPageId(newPage);
      this.logger.debug('[DirectPlaywright] New page detected', {
        stablePageId: pageId,
        url: newPage.url(),
      });
      // Auto-CAPTURE (not auto-switch): wire the popup's listeners so it feeds
      // console/close signals and is a valid select target, but leave the active
      // page put — the agent moves only on an explicit browser_tabs select.
      if (this._autoCaptureNewPages) {
        this.setupPageEventListeners(newPage);
      }
      // Popups are pages this client never created, so they miss
      // createPageWithListeners. They still become selectable targets and still
      // feed the encoder (below) at the requested canvas size, so they get the
      // same viewport. Listeners are wired first: this awaits a CDP round-trip,
      // and a popup that closes or logs during it must still be observed.
      await this.applyRequestedViewport(newPage, 'popup');
      if (this.videoEnabled && this.videoDir) {
        await this._startScreencastOnPage(newPage, pageId);
      }
    });

    context.on('close', () => {
      if (this.contextClosedIntentionally) {
        this.logger.debug('[DirectPlaywright] Browser context closed intentionally');
      } else {
        // Preserve URL for recovery
        const crashedUrl = this.lastKnownUrl;
        if (crashedUrl && crashedUrl !== 'about:blank') {
          this._pendingCrashUrl = crashedUrl;
          this._pendingCrashType = 'context_closed';
        }
        this.logger.error('[DirectPlaywright] Browser context closed unexpectedly', {
          event: 'context_closed',
          url: crashedUrl,
        });
        this.recordBrowserLifecycleDiagnostic('context_closed', { url: crashedUrl });
      }
      this.context = null;
      this.page = null;
      this.contextClosedIntentionally = false;
    });
  }

  private attachBrowserListeners(browser: Browser): void {
    browser.on('disconnected', () => {
      if (this.disconnectInProgress || this.contextClosedIntentionally) {
        this.logger.debug('[DirectPlaywright] Browser disconnected intentionally');
        return;
      }
      // Preserve URL for recovery
      const crashedUrl = this.lastKnownUrl;
      if (crashedUrl && crashedUrl !== 'about:blank') {
        this._pendingCrashUrl = crashedUrl;
        this._pendingCrashType = 'browser_disconnect';
      }
      this.logger.error('[DirectPlaywright] Browser disconnected unexpectedly', {
        url: crashedUrl,
      });
      this.recordBrowserLifecycleDiagnostic('browser_disconnected', { url: crashedUrl });
      this.page = null;
      this.context = null;
    });
  }

  private async restoreBrowser(reason: string): Promise<void> {
    if (this.restoreInProgress && this.restorePromise) {
      await withOperationTimeout(
        this.restorePromise,
        FORCE_RECOVER_TIMEOUT_MS,
        'restoreBrowser:coalescedRestore'
      );
      return;
    }

    const connectOptions = this.lastConnectOptions;
    if (!connectOptions) {
      throw new Error('Browser not connected. Call connect() first.');
    }

    // Capture pending crash info before restore
    const urlToRestore = this._pendingCrashUrl;
    const crashType = this._pendingCrashType;
    this.recordBrowserLifecycleDiagnostic('recovery_started', {
      url: urlToRestore,
      reason,
    });

    this.restoreInProgress = true;
    this.restorePromise = (async () => {
      // Helper to restart CDP screencast streaming after recovery if it was active
      const restartScreencastIfNeeded = async () => {
        if (this.screencastHandler && this.page) {
          await this.startScreencast(
            this.screencastHandler,
            this.screencastOptions,
            this.screencastVideoCallbacks
          ).catch((err) => {
            this.logger.warn('[DirectPlaywright] Failed to restart screencast after recovery', {
              error: err instanceof Error ? err.message : String(err),
            });
          });
        }
      };

      // Helper to restart screencast file recording after recovery
      const restartScreencastRecordingIfNeeded = async () => {
        if (this.videoEnabled && this.page) {
          const pageId = this._assignPageId(this.page);
          this._currentFocusedPageId = pageId;
          await this._startScreencastOnPage(this.page, pageId).catch((err) => {
            this.logger.warn(
              '[DirectPlaywright] Failed to restart screencast recording after recovery',
              {
                error: err instanceof Error ? err.message : String(err),
              }
            );
          });
        }
      };

      // For persistent context (extensions), check if context is still valid
      if (this.extensionsEnabled && this.context) {
        try {
          this.logger.warn('[DirectPlaywright] Restoring page in existing persistent context', {
            reason,
          });
          await this.createPageWithListeners(this.context, reason);
          await this.restoreUrlAfterRecovery(urlToRestore, crashType);
          await restartScreencastIfNeeded();
          await restartScreencastRecordingIfNeeded();
          return;
        } catch (err) {
          this.logger.warn(
            '[DirectPlaywright] Persistent context unusable, falling through to full recreation',
            { reason, error: err instanceof Error ? err.message : String(err) }
          );
          this.context = null;
        }
      }

      const reuseContext = this.browser?.isConnected() && this.context;
      if (reuseContext) {
        try {
          this.logger.warn('[DirectPlaywright] Restoring page in existing context', { reason });
          await this.createPageWithListeners(this.context!, reason);
          await this.restoreUrlAfterRecovery(urlToRestore, crashType);
          await restartScreencastIfNeeded();
          await restartScreencastRecordingIfNeeded();
          return;
        } catch (err) {
          this.logger.warn(
            '[DirectPlaywright] Context unusable despite connected browser, falling through',
            { reason, error: err instanceof Error ? err.message : String(err) }
          );
          this.context = null;
        }
      }

      // Pool path: recover via lease (get new context from a healthy pooled browser)
      if (this.browserLease) {
        this.logger.warn('[DirectPlaywright] Recovering via pool lease', {
          reason,
          leaseId: this.browserLease.leaseId,
        });

        const newLease = await this.browserLease.recover();
        this.browserLease = newLease;

        const {
          storageStatePath: ssp,
          storageStateRequired,
          recordVideo: rv,
          extraHTTPHeaders: headers,
        } = connectOptions;
        const videoConfig = this.resolveVideoConfig(rv);
        this.videoEnabled = !!videoConfig;
        this.pendingVideoDir = videoConfig?.dir ?? null;
        this._savedVideoPath = null;

        const ctxOpts: {
          viewport?: { width: number; height: number };
          storageState?: string;
          recordVideo?: { dir: string; size?: { width: number; height: number } };
          extraHTTPHeaders?: Record<string, string>;
          acceptDownloads?: boolean;
        } = {
          viewport: connectOptions.viewport,
          acceptDownloads: true,
        };
        if (ssp) {
          try {
            await fs.access(ssp);
            ctxOpts.storageState = ssp;
          } catch (err) {
            if (storageStateRequired) {
              throw new Error(
                `Required recovery storage state is unreadable: ${err instanceof Error ? err.message : String(err)}`
              );
            }
            // The recovered context will be UNAUTHENTICATED without this — the
            // run will hit an auth wall. Never swallow silently: this is the
            // single hardest-to-diagnose recovery failure mode.
            this.logger.warn(
              '[DirectPlaywright] Recovery storage state unreadable — recovered context will be unauthenticated',
              { storageStatePath: ssp, error: err instanceof Error ? err.message : String(err) }
            );
          }
        } else {
          if (storageStateRequired) {
            throw new Error('Required recovery storage state path is missing');
          }
          this.logger.warn(
            '[DirectPlaywright] Recovery has no storage state path — recovered context will be unauthenticated'
          );
        }
        if (videoConfig) {
          await fs.mkdir(videoConfig.dir, { recursive: true });
        }
        if (headers && Object.keys(headers).length > 0) {
          ctxOpts.extraHTTPHeaders = headers;
        }

        this._contextCreatedAtMs = Date.now();
        this.context = await newLease.createContext(ctxOpts);
        this.browser = null;

        this.attachContextListeners(this.context);
        await this.createPageWithListeners(this.context, reason);
        await this.restoreRecreatedContextState(ctxOpts.storageState);
        await this.restoreUrlAfterRecovery(urlToRestore, crashType);
        await restartScreencastIfNeeded();
        await restartScreencastRecordingIfNeeded();
        return;
      }

      this.logger.warn('[DirectPlaywright] Recreating browser after unexpected closure', {
        reason,
        hadBrowser: !!this.browser,
      });

      // Clean up any partially closed browser
      if (this.browser) {
        try {
          await this.browser.close();
        } catch {
          // ignore
        }
      }

      // Clean up old user data directory if needed before creating new one
      if (this.shouldCleanupUserDataDir && this.userDataDir) {
        try {
          await fs.rm(this.userDataDir, { recursive: true, force: true });
        } catch {
          // ignore
        }
        this.userDataDir = null;
      }

      const {
        storageStatePath,
        storageStateRequired,
        browserMode,
        recordVideo,
        stealth,
        extensions,
      } = connectOptions;
      const videoConfig = this.resolveVideoConfig(recordVideo);
      this.videoEnabled = !!videoConfig;
      this.pendingVideoDir = videoConfig?.dir ?? null;
      this._savedVideoPath = null;

      // Determine if stealth mode is enabled
      const useStealthMode = stealth === true || (typeof stealth === 'object' && stealth.enabled);
      this.stealthEnabled = useStealthMode;

      // Determine if extensions are requested
      const hasExtensions = !!(extensions?.paths && extensions.paths.length > 0);
      this.extensionsEnabled = hasExtensions;

      // Use the launchBrowser helper for consistent behavior
      const { browser, context } = await this.launchBrowser({
        browserMode,
        storageStatePath,
        storageStateRequired,
        recordVideo,
        stealth,
        extensions,
      });

      this.browser = browser;
      this.context = context;

      this.attachContextListeners(this.context);
      if (this.browser) {
        this.attachBrowserListeners(this.browser);
      }

      await this.createPageWithListeners(this.context, reason);
      await this.restoreRecreatedContextState(storageStatePath);
      await this.restoreUrlAfterRecovery(urlToRestore, crashType);
      await restartScreencastIfNeeded();
      await restartScreencastRecordingIfNeeded();
    })();

    try {
      await this.restorePromise;
      this.recordBrowserLifecycleDiagnostic('recovery_completed', {
        url: this.page?.url() ?? urlToRestore,
        reason,
      });
    } catch (error) {
      this.recordBrowserLifecycleDiagnostic('recovery_failed', {
        url: urlToRestore,
        reason: `${reason}: ${error instanceof Error ? error.message : String(error)}`,
      });
      throw error;
    } finally {
      this.restoreInProgress = false;
      this.restorePromise = null;
    }
  }

  /**
   * Attempt to restore the URL after a crash recovery.
   * Sets recovery info for the next tool call to consume.
   */
  private async restoreUrlAfterRecovery(
    urlToRestore: string | null,
    crashType: RecoveryInfo['crashType'] | null
  ): Promise<void> {
    // Clear pending crash state
    this._pendingCrashUrl = null;
    this._pendingCrashType = null;

    if (!urlToRestore || urlToRestore === 'about:blank' || !crashType) {
      return;
    }

    let urlRestored = false;

    try {
      if (this.page) {
        this.logger.info('[DirectPlaywright] Restoring URL after crash recovery', {
          url: urlToRestore,
          crashType,
        });

        await this.page.goto(urlToRestore, {
          waitUntil: 'domcontentloaded',
          timeout: TIMEOUTS.NAVIGATE,
        });

        // Update lastKnownUrl after successful navigation
        this.lastKnownUrl = urlToRestore;
        urlRestored = true;

        this.logger.info('[DirectPlaywright] URL restored successfully after crash', {
          url: urlToRestore,
          crashType,
        });
      }
    } catch (error) {
      this.logger.warn('[DirectPlaywright] Failed to restore URL after crash', {
        url: urlToRestore,
        crashType,
        error: error instanceof Error ? error.message : String(error),
      });
    }

    // Set recovery info for the next tool call to consume
    // Include video info for crash recovery if video was recording
    this._lastRecoveryInfo = {
      crashType,
      recoveredUrl: urlToRestore,
      urlRestored,
      timestamp: new Date().toISOString(),
      wasRecording: this.videoEnabled,
      videoDir: this.videoDir ?? undefined,
    };
    // Monotonic, never-cleared — lets a caller detect a recovery within a step.
    this._recoveryGeneration += 1;

    // The isolated api_request cookie state (if any) was seeded from the
    // pre-recovery context and is now stale relative to the rebuilt context.
    // Drop it so subsequent api_sequence steps don't authenticate with a stale
    // session until beginApiRequestSession seeds from the fresh context.
    await this.endApiRequestSession();
  }

  /**
   * Monotonic count of completed crash/wedge recoveries for this client. Compare
   * the value across a step to tell whether the browser context was rebuilt
   * mid-step — used to classify a post-recovery auth wall as retriable infra.
   */
  getRecoveryGeneration(): number {
    return this._recoveryGeneration;
  }

  // ================ VIDEO RECORDING ================

  /**
   * Resolve video recording configuration from connect options.
   * Recording is always done through the screencast encoder (CDP-driven).
   * Returns null when disabled; otherwise allocates a temp dir and uses the
   * default viewport size.
   */
  private resolveVideoConfig(
    recordVideo: boolean | undefined
  ): { dir: string; size: { width: number; height: number } } | null {
    if (!recordVideo) return null;
    return {
      dir: path.join(this.tmpBaseDir, `playwright-video-${Date.now()}`),
      size: DEFAULT_VIEWPORT,
    };
  }

  isVideoRecordingEnabled(): boolean {
    return this.videoEnabled;
  }

  getContextCreatedAtMs(): number | null {
    return this._contextCreatedAtMs;
  }

  /**
   * Get the wall-clock timestamp when the screencast encoder started recording.
   * More accurate than getContextCreatedAtMs() for screencast mode.
   */
  getRecordingStartedAtMs(): number | null {
    return this._screencastEncoder?.startedAtMs ?? this._contextCreatedAtMs;
  }

  // ─── Screencast file recording helpers ───

  /**
   * Create the screencast encoder if it doesn't exist yet.
   * BrowserSession normally calls this at its logical recording boundary. Remote
   * transports can create their encoder earlier while reconfiguring a pre-launched
   * context, so callers must not treat this hook as a cross-substrate guarantee
   * that earlier footage is absent.
   */
  ensureScreencastEncoder(): void {
    if (!this.videoEnabled || this._screencastEncoder) return;
    if (!this.videoDir) return;

    const size = this.lastConnectOptions?.viewport ?? DEFAULT_VIEWPORT;
    const videoFileName = `screencast-${Date.now()}.mp4`;
    const outputPath = path.join(this.videoDir, videoFileName);
    this._screencastEncoder = new ScreencastFileEncoder({
      outputPath,
      size,
      logger: this.logger,
    });
    this._screencastEncoder.start();
    // The encoder owns the output path now — `videoPath` derives from it.
    // Clear any prior snapshot from a previous saveVideo() so the getter
    // returns the live encoder's path, not a stale finalized one.
    this._savedVideoPath = null;

    this.logger.info('[DirectPlaywright] Screencast encoder started', {
      outputPath,
      size,
    });
  }

  /**
   * Complain, once per page, when the frames Chrome captures are not the size the
   * encoder canvas was cut to.
   *
   * The encoder's canvas is fixed at start() and cannot change mid-stream, so a
   * mismatch is reconciled by ffmpeg's pad/crop filter — which is exactly how a
   * 140-row band of fill colour ended up in every recorded video for weeks with
   * nothing in the logs to explain it. Making the divergence say its own name and
   * both numbers turns the next occurrence into a grep instead of an
   * investigation. `deviceWidth`/`deviceHeight` are the page's real viewport as
   * Chrome sees it (see ScreencastFrameMetadata).
   */
  private _warnOnceOnCanvasMismatch(
    canvas: { width: number; height: number },
    metadata: ScreencastFrameMetadata,
    pageId: number
  ): void {
    if (this._canvasMismatchWarnedPageIds.has(pageId)) return;
    const { deviceWidth, deviceHeight } = metadata;
    if (deviceWidth === undefined || deviceHeight === undefined) return;
    this._canvasMismatchWarnedPageIds.add(pageId);
    if (!isReportableCanvasMismatch(canvas, { width: deviceWidth, height: deviceHeight })) {
      return;
    }
    this.logger.warn(
      '[DirectPlaywright] Screencast frame size does not match the encoder canvas — video will be padded/cropped',
      {
        pageId,
        canvas,
        frame: { width: deviceWidth, height: deviceHeight },
        requestedViewport: this.getViewportSize(),
      }
    );
  }

  /**
   * Start frame delivery from a page into the shared screencast encoder.
   * Frames are only piped once the encoder exists (see ensureScreencastEncoder).
   */
  private async _startScreencastOnPage(page: Page, pageId: number): Promise<void> {
    if (!this.videoEnabled || !this.videoDir) return;

    // Guard: prevent double-start when context.on('page') and explicit call race
    if (this._screencastDisposables.has(page)) {
      this.logger.debug('[DirectPlaywright] Screencast already active on page, skipping', {
        pageId,
      });
      return;
    }

    const size = this.lastConnectOptions?.viewport ?? DEFAULT_VIEWPORT;

    if (!this.screencastManager) {
      this.screencastManager = new CdpScreencastManager(
        undefined,
        this.screencastEncoderFactory ?? undefined
      );
    }

    const subscription = await this.screencastManager.subscribe(page, {
      onJpeg: (jpeg, metadata) => {
        // Only accept frames from the currently focused page.
        if (this._currentFocusedPageId === pageId) {
          this._warnOnceOnCanvasMismatch(size, metadata, pageId);
          this._screencastEncoder?.pushFrame(jpeg);
        }
      },
      // 10fps ≈ original page.screencast cadence after gap-fill. The encoder's
      // monotonic-clock gap-fill keeps wall-clock duration accurate either way.
      minIntervalMs: 100,
      maxWidth: size.width,
      maxHeight: size.height,
    });

    this._screencastDisposables.set(page, {
      dispose: () => subscription.unsubscribe(),
    });

    // Clean up when page closes — actually unsubscribe (not just forget the
    // handle), otherwise the bus keeps the closed-page subscription in its
    // map and inflates paramsSuperset for live subscribers on other pages.
    page.on('close', () => {
      const info = this._screencastDisposables.get(page);
      this._screencastDisposables.delete(page);
      if (info) {
        info.dispose().catch((err) => {
          this.logger.debug('[DirectPlaywright] Subscription dispose on page close failed', {
            error: err instanceof Error ? err.message : String(err),
          });
        });
      }
    });

    this.logger.debug('[DirectPlaywright] Screencast started on page', { pageId });
  }

  /**
   * Stop screencast on all active pages and finalize the encoder.
   * Returns a single VideoRecordingResult — no stitching needed.
   */
  private async _saveVideoScreencast(): Promise<VideoRecordingResult | null> {
    const SCREENCAST_STOP_TIMEOUT_MS = 10_000;

    // Stop screencast on all active pages in parallel (with timeout per page)
    const stopPromises = [...this._screencastDisposables].map(async ([page, info]) => {
      try {
        if (!page.isClosed()) {
          await Promise.race([
            info.dispose(),
            new Promise<void>((resolve) => setTimeout(resolve, SCREENCAST_STOP_TIMEOUT_MS)),
          ]);
        }
      } catch (err) {
        this.logger.warn('[DirectPlaywright] Failed to stop screencast on page', {
          error: err instanceof Error ? err.message : String(err),
        });
      }
    });
    await Promise.allSettled(stopPromises);
    this._screencastDisposables.clear();

    // Stop the encoder (finalizes MP4 with faststart). Snapshot its output
    // path *before* nulling so `videoPath` stays available for log lines and
    // post-disconnect callers.
    const encoder = this._screencastEncoder;
    if (!encoder) {
      this._screencastEncoder = null;
      this.logger.warn(
        '[DirectPlaywright] Screencast encoder was never started — no video produced',
        {
          videoEnabled: this.videoEnabled,
          videoDir: this.videoDir,
        }
      );
      return null;
    }
    this._savedVideoPath = encoder.outputFilePath;
    this._screencastEncoder = null;

    const stopResult = await encoder.stop();
    const {
      durationMs,
      receivedFrameCount,
      writtenFrameCount,
      encoderCreatedAtMs,
      firstFrameWallClockMs,
      framesDroppedBeforeReady,
      maxInterFrameGapMs,
    } = stopResult;

    // `screencastStartCount` preserves prior semantics: cold start + page-swap
    // restarts only (i.e. one bump per `page.screencast.start()` equivalent).
    // `paramRestartCount` is new — bumped when a stricter subscriber forces a
    // CDP stop+start with new superset params. Splitting them lets investigation
    // b4b2e3e5 separate page churn (expected) from param-thrash (a bug).
    const screencastStartCount = this.screencastManager?.startCount ?? 0;
    const paramRestartCount = this.screencastManager?.paramRestartCount ?? 0;

    this.logger.info('[DirectPlaywright] Screencast recording saved', {
      videoPath: this.videoPath,
      durationMs,
      receivedFrameCount,
      writtenFrameCount,
      encoderCreatedAtMs,
      firstFrameWallClockMs,
      framesDroppedBeforeReady,
      maxInterFrameGapMs,
      screencastStartCount,
      paramRestartCount,
    });

    if (!this.videoPath) return null;

    return {
      videoPath: this.videoPath,
      recorded: true,
      screencastTelemetry: {
        encoderDurationMs: durationMs,
        receivedFrameCount,
        writtenFrameCount,
        encoderCreatedAtMs,
        firstFrameWallClockMs,
        framesDroppedBeforeReady,
        maxInterFrameGapMs,
        screencastStartCount,
        paramRestartCount,
      },
    };
  }

  /**
   * Stop screencast frame delivery on all pages WITHOUT stopping the encoder.
   * Used during context swaps — the encoder keeps running.
   */
  private async _stopScreencastFrameDelivery(): Promise<void> {
    const SWAP_STOP_TIMEOUT_MS = 5_000;
    const stopPromises = [...this._screencastDisposables].map(async ([page, info]) => {
      try {
        if (!page.isClosed()) {
          await Promise.race([
            info.dispose(),
            new Promise<void>((resolve) => setTimeout(resolve, SWAP_STOP_TIMEOUT_MS)),
          ]);
        }
      } catch (err) {
        this.logger.debug('[DirectPlaywright] Screencast frame delivery stop failed during swap', {
          error: err instanceof Error ? err.message : String(err),
        });
      }
    });
    await Promise.allSettled(stopPromises);
    this._screencastDisposables.clear();
  }

  async saveVideo(): Promise<VideoRecordingResult | null> {
    if (!this.videoEnabled) {
      this.logger.debug('[DirectPlaywright] Video recording not enabled, nothing to save');
      return null;
    }
    return this._saveVideoScreencast();
  }

  async getVideoPath(): Promise<VideoRecordingResult | null> {
    if (!this.videoEnabled) {
      return null;
    }

    // Return cached path from saveVideo()
    if (this.videoPath) {
      return { videoPath: this.videoPath, recorded: true };
    }

    // Video not saved yet - caller should call saveVideo() first
    this.logger.debug(
      '[DirectPlaywright] No saved video path. Call saveVideo() before getVideoPath().'
    );
    return null;
  }

  // ================ TOASTS ================

  getCapturedToasts(): CapturedToast[] {
    return this._lastCapturedToasts;
  }

  getNewConsoleErrors(): CapturedConsoleMessage[] {
    const errors = this._capturedConsoleMessages
      .slice(this._lastSnapshotConsoleIndex)
      .filter((m) => m.type === 'error');
    this._lastSnapshotConsoleIndex = this._capturedConsoleMessages.length;
    return errors;
  }

  getNewFailedRequests(): CapturedNetworkRequest[] {
    const failed: CapturedNetworkRequest[] = [];
    for (const req of this._capturedNetworkRequests.values()) {
      if (typeof req.status === 'number' && req.status >= 400) {
        const key = `${req.status}:${req.method}:${req.url}`;
        if (!this._snapshotedFailedRequestUrls.has(key)) {
          failed.push(req);
          this._snapshotedFailedRequestUrls.add(key);
        }
      }
    }
    return failed;
  }

  // ================ DOWNLOADS ================

  getCapturedDownloads(): CapturedDownload[] {
    return this.downloads.downloads;
  }

  async listDownloads(): Promise<string> {
    return this.downloads.list();
  }

  async readDownload(downloadId?: number): Promise<{
    filename: string;
    sizeBytes: number;
    mimeType: string;
    textContent: string | null;
  }> {
    return this.downloads.read(downloadId);
  }

  /**
   * Fetch a PDF's raw bytes using the browser's authenticated context (cookies).
   * Shared by `fetchPdfText` (extract text) and `fetchPdfImage` (return the PDF
   * to the model as vision input).
   */
  private async fetchPdfBuffer(
    url: string,
    maxBytes: number
  ): Promise<{ buffer: Buffer; sizeBytes: number }> {
    if (!this.context) {
      throw new Error('Browser not connected. Call connect() first.');
    }

    const requestUrl = resolveBrowserContextRequestUrl(url, this.page?.url() ?? null);

    this.logger.info('[DirectPlaywright] Fetching PDF via browser context', {
      url: requestUrl.slice(0, 200),
      originalUrl: requestUrl === url ? undefined : url.slice(0, 200),
    });

    // Use Playwright's APIRequestContext which inherits browser cookies
    const response = await this.context.request.get(requestUrl);
    const status = response.status();

    if (!response.ok()) {
      this.logger.warn('[DirectPlaywright] PDF fetch returned non-OK status', {
        url: requestUrl.slice(0, 200),
        status,
      });
      throw new Error(
        `Failed to fetch PDF from ${requestUrl}: HTTP ${status} ${response.statusText()}`
      );
    }

    const contentLength = parseContentLengthBytes(response.headers()['content-length']);
    assertPdfFetchSizeWithinLimit(contentLength, maxBytes, requestUrl, 'Content-Length');

    const buffer = Buffer.from(await response.body());
    assertPdfFetchSizeWithinLimit(buffer.length, maxBytes, requestUrl, 'downloaded body');
    return { buffer, sizeBytes: buffer.length };
  }

  async fetchPdfText(
    url: string
  ): Promise<{ text: string; totalPages: number; sizeBytes: number }> {
    const { buffer, sizeBytes } = await this.fetchPdfBuffer(url, MAX_PDF_TEXT_FETCH_BYTES);

    this.logger.info('[DirectPlaywright] PDF fetched, extracting text', { sizeBytes });

    const { extractPdfTextFromBuffer } = await import('./pdf-extract.js');
    const { text, totalPages } = await extractPdfTextFromBuffer(buffer);
    return { text, totalPages, sizeBytes };
  }

  /**
   * Fetch a PDF and return it as a base64 `application/pdf` payload so the model
   * can read it via native document vision — the durable answer to inline PDFs
   * that Chromium renders to a plugin surface the DOM/snapshot can't see.
   */
  async fetchPdfImage(
    url: string
  ): Promise<{ base64: string; mediaType: string; sizeBytes: number }> {
    const { buffer, sizeBytes } = await this.fetchPdfBuffer(url, MAX_PDF_IMAGE_BYTES);

    if (sizeBytes > MAX_PDF_IMAGE_BYTES) {
      throw new Error(
        `PDF is too large for visual mode (${(sizeBytes / 1024 / 1024).toFixed(1)}MB exceeds ` +
          `${MAX_PDF_IMAGE_BYTES / 1024 / 1024}MB). Call browser_pdf_read without image mode to extract its text instead.`
      );
    }

    this.logger.info('[DirectPlaywright] PDF fetched for visual mode', { sizeBytes });
    return { base64: buffer.toString('base64'), mediaType: 'application/pdf', sizeBytes };
  }

  // ================ HTTP REQUESTS ================

  /**
   * Begin an isolated api_sequence request session.
   *
   * Seeds a native Fetch-backed request cookie jar from the live browser
   * context's current storage state (cookies + origins), so api_sequence steps
   * run with the authenticated session without Playwright APIRequestContext's
   * Bun-specific Set-Cookie parser.
   *
   * Best-effort: on any failure the session stays inactive and {@link fetch}
   * transparently falls back to the browser-context path (preserving prior
   * behavior). Nothing here is persisted — the storage state lives only until
   * {@link endApiRequestSession}/{@link disconnect}.
   */
  async beginApiRequestSession(): Promise<void> {
    // Best-effort by contract: never throws. On any failure we leave the session
    // inactive and fetch() falls back to the live browser-context cookie jar.
    if (!this.context) {
      this.logger.warn(
        '[DirectPlaywright] beginApiRequestSession: not connected; isolated session unavailable',
        { willFallbackToBrowserContext: true }
      );
      return;
    }
    try {
      this.apiRequestStorageState = await withOperationTimeout(
        this.context.storageState(),
        5_000,
        '[DirectPlaywright] beginApiRequestSession storageState'
      );
      this.apiRequestSessionActive = true;
      this.logger.info('[DirectPlaywright] Native api request session started', {
        cookieCount: this.apiRequestStorageState.cookies.length,
      });
    } catch (err) {
      this.apiRequestSessionActive = false;
      this.apiRequestStorageState = undefined;
      this.logger.warn(
        '[DirectPlaywright] Failed to start isolated api request session; falling back to browser context',
        {
          error: err instanceof Error ? err.message : String(err),
          willFallbackToBrowserContext: true,
        }
      );
    }
  }

  /** Drop the isolated api_sequence request session, if any. Idempotent. */
  async endApiRequestSession(): Promise<void> {
    this.apiRequestSessionActive = false;
    this.apiRequestStorageState = undefined;
  }

  private createNativeRequestCookieJar(
    context: BrowserContext,
    useIsolated: boolean
  ): NativeRequestCookieJar {
    if (useIsolated && this.apiRequestStorageState) {
      return {
        cookies: async () => this.apiRequestStorageState?.cookies ?? [],
        applySetCookies: async (cookies) => {
          if (!this.apiRequestStorageState) return;
          this.apiRequestStorageState = {
            ...this.apiRequestStorageState,
            cookies: mergeNativeRequestCookies(this.apiRequestStorageState.cookies, cookies),
          };
          this.logger.info('[DirectPlaywright] Reconciled native api request cookies', {
            rotatedCount: cookies.length,
            cookieCount: this.apiRequestStorageState.cookies.length,
          });
        },
      };
    }

    return {
      cookies: (requestUrl) => context.cookies(requestUrl),
      applySetCookies: async (cookies) => {
        const nowSec = Math.floor(Date.now() / 1000);
        const liveCookies = cookies.filter(
          (cookie) => !(cookie.expires > 0 && cookie.expires <= nowSec)
        );
        const expiredCookies = cookies.filter(
          (cookie) => cookie.expires > 0 && cookie.expires <= nowSec
        );

        for (const cookie of expiredCookies) {
          try {
            await context.clearCookies({
              name: cookie.name,
              domain: cookie.domain,
              path: cookie.path,
            });
          } catch (err) {
            this.logger.warn('[DirectPlaywright] Failed to clear expired response cookie', {
              error: err instanceof Error ? err.message : String(err),
              cookieName: cookie.name,
              domain: cookie.domain,
              path: cookie.path,
            });
          }
        }

        if (liveCookies.length > 0) {
          await context.addCookies(liveCookies);
        }
      },
    };
  }

  async fetch(options: {
    url: string;
    method?: string;
    headers?: Record<string, string>;
    body?: string;
    timeoutMs?: number;
    signal?: AbortSignal;
  }): Promise<{
    status: number;
    statusText: string;
    headers: Record<string, string>;
    body: string | null;
  }> {
    if (!this.context) {
      throw new Error('Browser not connected. Call connect() first.');
    }

    const requestUrl = resolveBrowserContextRequestUrl(options.url, this.page?.url() ?? null);

    const useIsolated = this.apiRequestSessionActive && !!this.apiRequestStorageState;

    this.logger.info('[DirectPlaywright] Making HTTP request', {
      url: requestUrl.slice(0, 200),
      originalUrl: requestUrl === options.url ? undefined : options.url.slice(0, 200),
      method: options.method ?? 'GET',
      timeoutMs: options.timeoutMs,
      isolated: useIsolated,
    });

    return fetchWithNativeRequestCookies({
      requestUrl,
      method: options.method ?? 'GET',
      headers: options.headers,
      body: options.body,
      timeoutMs: options.timeoutMs,
      signal: options.signal,
      cookieJar: this.createNativeRequestCookieJar(this.context, useIsolated),
    });
  }

  // ================ EMULATION ROUTES ================

  setRunCodeEgressPolicy(policy: RunCodeEgressPolicy | null): void {
    this.runCodeEgressPolicy = policy;
  }

  async setupContextRoutes(
    routes: ContextRoute[],
    key: string = 'default',
    opts?: { matchFirst?: boolean; reapplyOnContextSwap?: boolean }
  ): Promise<void> {
    // Resolve the context BEFORE taking the lock: getContext can enter
    // recovery, which re-arms routes and takes this same (non-reentrant) lock.
    const context = await this.getContext();
    await this.withContextRouteLock(async () => {
      // Replace only THIS owner's group; an empty array disarms the group.
      if (routes.length === 0) {
        this.storedContextRoutes.delete(key);
      } else {
        this.storedContextRoutes.set(key, {
          routes,
          matchFirst: opts?.matchFirst ?? false,
          reapplyOnContextSwap: opts?.reapplyOnContextSwap ?? true,
        });
      }
      await this.rebuildContextRoutes(context);
    });
  }

  private withContextRouteLock<T>(fn: () => Promise<T>): Promise<T> {
    const run = this.contextRouteLock.then(fn);
    this.contextRouteLock = run.then(
      () => undefined,
      () => undefined
    );
    return run;
  }

  /**
   * Re-register the stored route groups. New handlers are registered BEFORE
   * the stale ones are unrouted (Playwright matches newest-first, so the new
   * set takes effect immediately) — there is no unguarded window mid-rebuild,
   * and arming one owner's group never de-arms another's. Groups flagged
   * `matchFirst` (the diagnostic host boundary) register last so they are
   * consulted before every other group; their pass-throughs use `fallback()`
   * so lower-priority groups still run.
   */
  private async rebuildContextRoutes(context: BrowserContext): Promise<void> {
    const stale = this.armedContextRouteContext === context ? this.armedContextRouteHandlers : [];
    const groups = [...this.storedContextRoutes.values()].sort(
      (a, b) => Number(a.matchFirst) - Number(b.matchFirst)
    );
    const armed: typeof this.armedContextRouteHandlers = [];
    for (const group of groups) {
      for (const route of group.routes) {
        const handler: Parameters<BrowserContext['route']>[1] = async (pwRoute) => {
          await route.handler(createSingleUseContextRouteHandle(pwRoute, this.logger));
        };
        await context.route(route.urlPattern, handler);
        armed.push({ pattern: route.urlPattern, handler });
      }
    }
    this.armedContextRouteHandlers = armed;
    this.armedContextRouteContext = context;
    for (const old of stale) {
      try {
        await context.unroute(old.pattern, old.handler);
      } catch (err) {
        this.logger.debug('[DirectPlaywright] Failed to unroute stale context route', {
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }
  }

  /**
   * Restart tracing on a freshly recreated (post-crash/recovery) context. The
   * pre-recovery trace died with the old context and cannot be flushed, so the
   * best we can do is capture post-recovery activity and ensure the final
   * stopTracing() targets a context that actually has tracing started.
   * Best-effort — no-op when tracing wasn't active.
   */
  private async resumeTracingOnRecreatedContext(): Promise<void> {
    await this.traceAdapter.resumeAfterRecreatedContext();
  }

  /**
   * Re-arm the stored context routes on the current (freshly recreated) context.
   * Called from recovery paths that build a new context — without this, scoped
   * header injection (e.g. Cloudflare Access) silently stops after a restore and
   * the edge redirects subsequent requests to a login page. Best-effort.
   */
  private async reapplyStoredContextRoutes(): Promise<void> {
    if (this.storedContextRoutes.size === 0 || !this.context) return;
    const context = this.context;
    await this.withContextRouteLock(async () => {
      try {
        await this.rebuildContextRoutes(context);
        this.logger.info('[DirectPlaywright] Re-armed context route groups on recreated context', {
          groupKeys: [...this.storedContextRoutes.keys()],
        });
      } catch (err) {
        // ERROR, not warn: a failed re-arm can leave a security group (the
        // diagnostic host boundary) disarmed on a live context. Recovery is
        // inherently best-effort — operators alert on this line; the
        // fail-closed owners (diagnostic sessions) additionally verify their
        // own arm on every path they control (start, credential swap).
        this.logger.error('[DirectPlaywright] Failed to re-arm context routes after recreation', {
          error: err instanceof Error ? err.message : String(err),
          groupKeys: [...this.storedContextRoutes.keys()],
        });
      }
    });
  }
}

type EditableProbe = {
  readonly?: boolean;
  disabled?: boolean;
  fillable?: boolean;
  tagName?: string;
  role?: string | null;
  inputType?: string | null;
};

type EditableProbeLocator = Pick<Locator, 'evaluate'>;

export interface BrowserActionTargetClosedContext {
  operation: 'click';
  ref: string;
  elementDesc?: string;
  lastKnownUrl: string | null;
  pendingCrashType: RecoveryInfo['crashType'] | null;
  pendingCrashUrl: string | null;
}

export interface BrowserNavigateTargetClosedContext {
  operation: 'goto';
  url: string;
  lastKnownUrl: string | null;
  pendingCrashType: RecoveryInfo['crashType'] | null;
  pendingCrashUrl: string | null;
  signalAborted: boolean;
  signalReason: string | null;
}

export function isPlaywrightTargetClosedError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return /Target page, context or browser has been closed/i.test(message);
}

export function toBrowserActionTargetClosedError(
  error: unknown,
  context: BrowserActionTargetClosedContext
): Error {
  const label = context.elementDesc ? `${context.elementDesc} (${context.ref})` : context.ref;
  const message = error instanceof Error ? error.message : String(error);
  const wrapped = new Error(`Browser click target closed for ${label}: ${message}`, {
    cause: error instanceof Error ? error : undefined,
  });
  wrapped.name = 'BrowserActionTargetClosedError';
  Object.assign(wrapped, { browserActionTargetClosed: context });
  return wrapped;
}

function abortSignalReason(signal: AbortSignal | undefined): string | null {
  if (!signal?.aborted) {
    return null;
  }

  const reason = signal.reason;
  if (reason instanceof Error) {
    return `${reason.name}: ${reason.message}`;
  }
  if (typeof reason === 'string') {
    return reason;
  }
  if (reason === undefined) {
    return null;
  }
  return String(reason);
}

function browserDiagnosticUrl(value: string | null): string | null {
  const trimmed = value?.trim();
  if (!trimmed) {
    return null;
  }

  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
      return `${parsed.origin}${parsed.pathname}`;
    }
    if (parsed.protocol === 'about:') {
      return `about:${parsed.pathname}`;
    }
    return `${parsed.protocol}//${parsed.host || 'local'}`;
  } catch {
    return trimmed.split(/[?#]/, 1)[0] ?? trimmed;
  }
}

function sanitizeNavigateTargetClosedContext(
  context: BrowserNavigateTargetClosedContext
): BrowserNavigateTargetClosedContext {
  return {
    ...context,
    url: browserDiagnosticUrl(context.url) ?? 'unknown',
    lastKnownUrl: browserDiagnosticUrl(context.lastKnownUrl),
    pendingCrashUrl: browserDiagnosticUrl(context.pendingCrashUrl),
  };
}

export function toBrowserNavigateTargetClosedError(
  error: unknown,
  context: BrowserNavigateTargetClosedContext
): Error {
  const message = error instanceof Error ? error.message : String(error);
  const sanitizedContext = sanitizeNavigateTargetClosedContext(context);
  const wrapped = new Error(
    `Browser goto target closed while navigating to ${sanitizedContext.url}: ${message}`,
    {
      cause: error instanceof Error ? error : undefined,
    }
  );
  wrapped.name = 'BrowserNavigateTargetClosedError';
  Object.assign(wrapped, { browserNavigateTargetClosed: sanitizedContext });
  return wrapped;
}

export interface FillTargetProbeErrorContext {
  ref: string;
  elementDesc?: string;
  stage: 'editability_probe';
}

export function toFillTargetProbeError(
  error: unknown,
  context: FillTargetProbeErrorContext
): Error {
  const label = context.elementDesc ? `${context.elementDesc} (${context.ref})` : context.ref;
  const message = error instanceof Error ? error.message : String(error);
  const wrapped = new Error(`Fill target editability probe failed for ${label}: ${message}`, {
    cause: error instanceof Error ? error : undefined,
  });
  wrapped.name = 'FillTargetProbeError';
  Object.assign(wrapped, { fillTargetProbe: context });
  return wrapped;
}

export async function assertFillTargetEditable(
  locator: EditableProbeLocator,
  ref: string,
  elementDesc?: string
): Promise<void> {
  let probe: EditableProbe;
  try {
    probe = await locator.evaluate((el): EditableProbe => {
      const element = el as HTMLElement;
      const htmlInput = element instanceof HTMLInputElement ? element : null;
      const htmlTextArea = element instanceof HTMLTextAreaElement ? element : null;
      const htmlSelect = element instanceof HTMLSelectElement ? element : null;
      const htmlButton = element instanceof HTMLButtonElement ? element : null;
      const ariaReadonly = element.getAttribute('aria-readonly') === 'true';
      const ariaDisabled = element.getAttribute('aria-disabled') === 'true';
      const contentEditableFalse = element.getAttribute('contenteditable') === 'false';
      const inputType = htmlInput?.type.toLowerCase() ?? null;
      const fillableInputTypes = new Set([
        '',
        'color',
        'date',
        'datetime-local',
        'email',
        'month',
        'number',
        'password',
        'range',
        'search',
        'tel',
        'text',
        'time',
        'url',
        'week',
      ]);

      return {
        readonly: Boolean(
          htmlInput?.readOnly || htmlTextArea?.readOnly || ariaReadonly || contentEditableFalse
        ),
        disabled: Boolean(
          htmlInput?.disabled ||
          htmlTextArea?.disabled ||
          htmlSelect?.disabled ||
          htmlButton?.disabled ||
          ariaDisabled
        ),
        fillable: Boolean(
          (htmlInput && fillableInputTypes.has(inputType ?? '')) ||
          htmlTextArea ||
          element.isContentEditable
        ),
        tagName: element.tagName.toLowerCase(),
        role: element.getAttribute('role'),
        inputType,
      };
    });
  } catch (error) {
    throw toFillTargetProbeError(error, {
      ref,
      elementDesc,
      stage: 'editability_probe',
    });
  }

  if (!probe.readonly && !probe.disabled && probe.fillable) return;

  const state = probe.disabled ? 'disabled' : probe.readonly ? 'readonly' : 'not-fillable';
  const tag = probe.tagName ?? 'element';
  throw new FillTargetNotEditableError({
    ref,
    elementDesc,
    state,
    tagName: tag,
    role: probe.role,
    ...(probe.inputType ? { inputType: probe.inputType } : {}),
  });
}
